import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { BACKEND_URL } from '../config';
import TradeDetailModal from './TradeDetailModal';
import WinLossAnalysisContent from './WinLossAnalysisContent';
import MLLearningProgress from './MLLearningProgress';
import GuardianStatus from './GuardianStatus';
import EarlyIgnitionTierStats from './EarlyIgnitionTierStats';
import NotificationLab from './NotificationLab';
import { FamilyAccounts } from './family/FamilyAccounts';

const AdminDashboard = () => {
  // Add CSS animation for blacklist flash and breakout alert flash
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes flash {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.7; transform: scale(1.1); }
      }
      @keyframes breakoutFlash {
        0%, 100% { 
          box-shadow: 0 0 20px rgba(239, 68, 68, 0.8), 0 0 40px rgba(239, 68, 68, 0.6), 0 0 60px rgba(239, 68, 68, 0.4);
          border-color: #ef4444;
        }
        50% { 
          box-shadow: 0 0 30px rgba(245, 158, 11, 0.9), 0 0 60px rgba(245, 158, 11, 0.7), 0 0 90px rgba(245, 158, 11, 0.5);
          border-color: #f59e0b;
        }
      }
      @keyframes breakoutPulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.02); }
      }
      @keyframes breakoutBanner {
        0%, 100% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
      }
      .breakout-alert-active {
        animation: breakoutFlash 1s ease-in-out infinite, breakoutPulse 2s ease-in-out infinite;
        border: 4px solid #ef4444 !important;
      }
      .breakout-banner {
        background: linear-gradient(90deg, #ef4444, #f59e0b, #ef4444, #f59e0b);
        background-size: 300% 100%;
        animation: breakoutBanner 2s linear infinite;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const [activeTab, setActiveTab] = useState('protocolperf');
  const [tier1Prices, setTier1Prices] = useState({ d7: 25, d14: 40, d30: 70 });
  const [tier2Prices, setTier2Prices] = useState({ d7: 500, d14: 800, d30: 1300 });
  const [blackFridayPrice, setBlackFridayPrice] = useState(500);
  const [blackFridayEnabled, setBlackFridayEnabled] = useState(false);
  const [stripeKey, setStripeKey] = useState('');
  const [inviteRequests, setInviteRequests] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Performance Tab State
  const [trackedTradesAnalytics, setTrackedTradesAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [momentumLastRefresh, setMomentumLastRefresh] = useState(null);
  const [momentumCountdown, setMomentumCountdown] = useState(60);
  
  // Breakout Analytics State
  const [breakoutAnalytics, setBreakoutAnalytics] = useState(null);
  const [loadingBreakoutAnalytics, setLoadingBreakoutAnalytics] = useState(false);
  const [breakoutVersion, setBreakoutVersion] = useState('all'); // 'all', 'v1', 'v2'
  const [breakoutComparison, setBreakoutComparison] = useState(null); // V1 vs V2 comparison
  
  // Divergence Analytics State (NEW - similar to breakout analytics)
  const [divergenceAnalytics, setDivergenceAnalytics] = useState(null);
  const [loadingDivergenceAnalytics, setLoadingDivergenceAnalytics] = useState(false);
  
  // 🎯 Toggle states for performance sections (hide by default to save space)
  const [showDivergencePerformance, setShowDivergencePerformance] = useState(false);
  const [showBreakoutPerformance, setShowBreakoutPerformance] = useState(false);
  
  // Active Breakout Alerts State (NEW - shows pending breakout opportunities)
  const [breakoutAlerts, setBreakoutAlerts] = useState([]);
  const [loadingBreakoutAlerts, setLoadingBreakoutAlerts] = useState(false);
  // 🔧 NEW (Jan 26): Breakout trade activation with risk input
  const [breakoutRiskInput, setBreakoutRiskInput] = useState({}); // Track risk input per alert
  const [showBreakoutRiskInput, setShowBreakoutRiskInput] = useState({}); // Track which alerts show risk input
  
  // 👀 Pending Retests State (Jan 31) - Breakouts waiting for retest entry
  const [pendingRetests, setPendingRetests] = useState([]);
  const [loadingPendingRetests, setLoadingPendingRetests] = useState(false);
  
  // 🚨 Currency Strength Divergence Alerts (NEW - violent move predictions)
  const [divergenceAlerts, setDivergenceAlerts] = useState([]);
  const [loadingDivergenceAlerts, setLoadingDivergenceAlerts] = useState(false);
  const [divergenceRiskInput, setDivergenceRiskInput] = useState({}); // Track risk input per alert
  const [showDivergenceRiskInput, setShowDivergenceRiskInput] = useState({}); // Track which alerts show risk input
  
  // 🔧 FIX (Jan 26): Use ref to track alerts for stable comparison (prevents stale closure issues)
  const divergenceAlertsRef = useRef([]);
  useEffect(() => {
    divergenceAlertsRef.current = divergenceAlerts;
  }, [divergenceAlerts]);
  
  // Family & Friends Whitelist State
  const [familyWhitelist, setFamilyWhitelist] = useState([]);
  const [newFriend, setNewFriend] = useState({ name: '', relationship: '', email: '' });
  const [whitelistMessage, setWhitelistMessage] = useState('');

  // Blacklist State
  const [blacklist, setBlacklist] = useState([]);
  const [loadingBlacklist, setLoadingBlacklist] = useState(false);
  const [newBlacklistEntries, setNewBlacklistEntries] = useState(0);

  // Trading Statistics State (Admin view of personal stats)
  const [tradeStats, setTradeStats] = useState(null);
  const [loadingTradeStats, setLoadingTradeStats] = useState(false);

  // Custom Date Range State
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [customPerformance, setCustomPerformance] = useState(null);
  const [loadingCustomPerformance, setLoadingCustomPerformance] = useState(false);
  
  // Session Performance State (6-hour periods)
  const [sessionPerformance, setSessionPerformance] = useState(null);
  const [loadingSessionPerformance, setLoadingSessionPerformance] = useState(false);

  // 📒 Protocol Performance State
  const [protocolSettings, setProtocolSettings] = useState({ account_size: 10000, risk_pct: 1.0, milestones: [{ trigger_r: 1, move_sl_to_r: 0 }, { trigger_r: 1.5, move_sl_to_r: 0.5 }, { trigger_r: 2, move_sl_to_r: 1 }] });
  const [savingProtocolSettings, setSavingProtocolSettings] = useState(false);
  const [protocolStartDate, setProtocolStartDate] = useState('');
  const [protocolEndDate, setProtocolEndDate] = useState('');
  const [protocolPerformance, setProtocolPerformance] = useState(null);
  const [loadingProtocolPerformance, setLoadingProtocolPerformance] = useState(false);

  // Exit-Strategy Simulator State (read-only re-score: tighter SL + fixed R:R TP)
  const [slTightnessPct, setSlTightnessPct] = useState(1);
  const [tpR, setTpR] = useState(3);
  const [exitSim, setExitSim] = useState(null);
  const [loadingExitSim, setLoadingExitSim] = useState(false);

  // ML Loss Analysis State (Intelligent Loss Learning)
  const [mlLossAnalysis, setMlLossAnalysis] = useState(null);
  const [loadingMlLossAnalysis, setLoadingMlLossAnalysis] = useState(false);

  // Deep Pattern Analysis State
  const [deepPatterns, setDeepPatterns] = useState(null);
  const [loadingDeepPatterns, setLoadingDeepPatterns] = useState(false);

  // Weekly Probability Analysis State
  const [weeklyProbAnalysis, setWeeklyProbAnalysis] = useState(null);
  const [loadingWeeklyProb, setLoadingWeeklyProb] = useState(false);
  const [probDayFilter, setProbDayFilter] = useState('all');

  // Filtered Trades Analysis State (News + Probability filtered)
  const [filteredTradesAnalysis, setFilteredTradesAnalysis] = useState(null);
  const [loadingFilteredTrades, setLoadingFilteredTrades] = useState(false);
  const [filteredTradesFilter, setFilteredTradesFilter] = useState('all'); // 'all', 'news', 'probability'

  // Bulk Calculate All Pairs State
  const [bulkRiskAmount, setBulkRiskAmount] = useState('100');
  const [bulkAccountBalance, setBulkAccountBalance] = useState('10000');
  const [bulkTimeframe, setBulkTimeframe] = useState('1D');
  const [bulkResults, setBulkResults] = useState(null);
  const [loadingBulkCalc, setLoadingBulkCalc] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 25 });

  // Trade Detail Modal State
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [showTradeDetailModal, setShowTradeDetailModal] = useState(false);

  // Probability Analysis State
  const [probabilityAnalysis, setProbabilityAnalysis] = useState(null);
  const [loadingProbabilityAnalysis, setLoadingProbabilityAnalysis] = useState(false);
  const [probabilityAnalysisError, setProbabilityAnalysisError] = useState(null);

  // Win/Loss Analysis State
  const [lossAnalysis, setLossAnalysis] = useState(null);
  const [loadingLossAnalysis, setLoadingLossAnalysis] = useState(false);
  const [winAnalysis, setWinAnalysis] = useState(null);
  const [loadingWinAnalysis, setLoadingWinAnalysis] = useState(false);
  const [comparisonStats, setComparisonStats] = useState(null);
  const [loadingComparisonStats, setLoadingComparisonStats] = useState(false);
  const [resetInfo, setResetInfo] = useState(null);
  const [analysisSubTab, setAnalysisSubTab] = useState('wins'); // 'wins', 'losses', 'comparison'

  // Trade Status Filter Tab State
  const [tradeFilterTab, setTradeFilterTab] = useState('active'); // 'active', 'closed', 'pending'

  // Close All Trades Handler
  const [closingAllTrades, setClosingAllTrades] = useState(false);
  
  const handleCloseAllTrades = async () => {
    if (!window.confirm('⚠️ Are you sure you want to close ALL active/pending trades? This action cannot be undone.')) {
      return;
    }
    
    setClosingAllTrades(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${backendUrl}/api/admin/close-all-trades`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        alert(`✅ Success! Closed ${response.data.closed_count} trades.`);
        // Refresh trade stats
        fetchTradeStats();
      } else {
        alert('❌ Failed to close trades. Please try again.');
      }
    } catch (error) {
      console.error('Error closing trades:', error);
      alert('❌ Error closing trades: ' + (error.response?.data?.detail || error.message));
    } finally {
      setClosingAllTrades(false);
    }
  };
  
  // Manual Rollover Closure Handler
  const [closingRollover, setClosingRollover] = useState(false);
  
  const handleManualRolloverClosure = async () => {
    if (!window.confirm('🟢 ROLLOVER CLOSURE: Close all active trades with WON/LOST status for ML training?\n\nThis will:\n- Fetch current market prices\n- Mark trades as WON if in profit\n- Mark trades as LOST if in drawdown\n- Provide ML training data')) {
      return;
    }
    
    setClosingRollover(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${backendUrl}/api/admin/manual-rollover-closure`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        const { trades_closed, wins, losses, total_pnl, win_rate } = response.data;
        toast.success(
          `✅ Rollover Closure Complete!\n\n` +
          `Trades: ${trades_closed}\n` +
          `Wins: ${wins} | Losses: ${losses}\n` +
          `Total P&L: $${total_pnl.toFixed(2)}\n` +
          `Win Rate: ${win_rate.toFixed(1)}%`,
          { duration: 8000 }
        );
        
        // Show detailed breakdown
        alert(
          `✅ ROLLOVER CLOSURE COMPLETE\n\n` +
          `📊 Summary:\n` +
          `   Trades closed: ${trades_closed}\n` +
          `   Wins: ${wins}\n` +
          `   Losses: ${losses}\n` +
          `   Total P&L: $${total_pnl > 0 ? '+' : ''}${total_pnl.toFixed(2)}\n` +
          `   Win Rate: ${win_rate.toFixed(1)}%\n\n` +
          `🧠 ML training data has been recorded for all trades.`
        );
        
        // Refresh trade stats
        fetchTradeStats();
      } else {
        alert('❌ Failed to close trades. ' + (response.data.message || 'Please try again.'));
      }
    } catch (error) {
      console.error('Error in rollover closure:', error);
      alert('❌ Error in rollover closure: ' + (error.response?.data?.detail || error.message));
    } finally {
      setClosingRollover(false);
    }
  };
  
  const [tradeDetailLoading, setTradeDetailLoading] = useState(false);

  // Rollover check state
  const [isRolloverPeriod, setIsRolloverPeriod] = useState(false);
  const [tradingStatus, setTradingStatus] = useState({
    calculator_enabled: true,
    news_risk: 'LOW',
    news_recommendation: '',
    block_due_to_news: false,
    in_rollover_period: false,
    reason: ''
  });
  
  // ❌ REMOVED: Delete duplicates handler (was dangerous - deleted all trades instead of duplicates)
  // If needed in future, must rewrite with proper duplicate detection and dry-run mode
  // const handleDeleteDuplicates = async () => { ... }

  const backendUrl = BACKEND_URL;

  // Check trading status (news risk + rollover period)
  const checkTradingStatus = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/trading-status`);
      const data = await response.json();
      
      if (data.success) {
        // 🔧 FIX (Jan 27): Only update state if values actually changed
        const statusChanged = (
          tradingStatus.calculator_enabled !== data.calculator_enabled ||
          tradingStatus.in_rollover_period !== data.in_rollover_period ||
          tradingStatus.block_due_to_news !== data.block_due_to_news ||
          tradingStatus.news_risk !== data.news_risk
        );
        
        if (statusChanged) {
          setTradingStatus(data);
          setIsRolloverPeriod(data.in_rollover_period);
        }
      }
    } catch (error) {
      console.error('Error checking trading status:', error);
      // Only set error state if we're not already in error state
      if (tradingStatus.reason !== 'Error checking status') {
        setTradingStatus({
          calculator_enabled: true,
          news_risk: 'UNKNOWN',
          news_recommendation: 'Unable to check status',
          block_due_to_news: false,
          in_rollover_period: false,
          reason: 'Error checking status'
        });
      }
    }
  };

  // Legacy function for backward compatibility
  const checkRolloverPeriod = () => {
    const now = new Date();
    const utcHours = now.getUTCHours();
    const utcMinutes = now.getUTCMinutes();
    const currentMinutes = utcHours * 60 + utcMinutes;
    
    // Rollover period: 21:00 UTC to 23:05 UTC
    const rolloverStart = 21 * 60; // 21:00 = 1260 minutes
    const rolloverEnd = 23 * 60 + 5; // 23:05 = 1385 minutes
    
    const isInRollover = currentMinutes >= rolloverStart && currentMinutes <= rolloverEnd;
    setIsRolloverPeriod(isInRollover);
    
    return isInRollover;
  };

  useEffect(() => {
    fetchPricing();
    fetchInviteRequests();
    fetchAnalytics();
    fetchTrackedTradesAnalytics();
    fetchBreakoutAnalytics();
    fetchDivergenceAnalytics(); // Fetch divergence trades analytics
    fetchBreakoutAlerts(); // Fetch active breakout alerts
    fetchPendingRetests(); // 👀 Fetch pending retests (breakouts waiting for retest)
    fetchDivergenceAlerts(true); // Fetch currency divergence alerts (initial load)
    fetchFamilyWhitelist();
    fetchBlacklist();
    
    // Check trading status (news + rollover) on mount and every 2 minutes
    // 🔧 FIX (Jan 27): Reduced frequency to minimize screen refreshes
    checkTradingStatus();
    const statusInterval = setInterval(checkTradingStatus, 120000); // Check every 2 minutes (was 1 min)
    
    // Auto-refresh breakout alerts every 5 minutes (was 2 min)
    // 🔧 FIX (Jan 27): Increased interval + added change detection to prevent unnecessary re-renders
    const alertsInterval = setInterval(fetchBreakoutAlerts, 300000);
    
    // 👀 Auto-refresh pending retests every 2 minutes
    const pendingRetestsInterval = setInterval(fetchPendingRetests, 120000);
    
    // Auto-refresh divergence alerts every 5 minutes (background refresh, no loading state)
    // 🔧 FIX (Jan 26): Increased from 2 min to 5 min to reduce screen refresh annoyance
    const divergenceInterval = setInterval(() => fetchDivergenceAlerts(false), 300000);
    
    // Restore bulk calculation state if user navigated away during calculation
    const savedStartTime = localStorage.getItem('bulkCalculationStartTime');
    const savedParams = localStorage.getItem('bulkCalculationParams');
    
    if (savedStartTime && savedParams) {
      const startTime = parseInt(savedStartTime);
      const currentTime = Date.now();
      const elapsedSeconds = Math.floor((currentTime - startTime) / 1000);
      const expectedDuration = 25 * 10; // 25 pairs × 10 seconds each
      
      if (elapsedSeconds < expectedDuration) {
        // Calculation should still be running
        const params = JSON.parse(savedParams);
        setBulkRiskAmount(params.risk_amount);
        setBulkTimeframe(params.timeframe);
        setLoadingBulkCalc(true);
        
        // Calculate current progress based on elapsed time
        const currentProgress = Math.min(Math.floor(elapsedSeconds / 10), 24);
        setBulkProgress({ current: currentProgress, total: 25 });
        
        // Continue the progress simulation from where it left off
        const remainingIntervals = 25 - currentProgress - 1;
        if (remainingIntervals > 0) {
          const progressInterval = setInterval(() => {
            setBulkProgress(prev => {
              if (prev.current < prev.total - 1) {
                return { ...prev, current: prev.current + 1 };
              }
              return prev;
            });
          }, 10000);
          
          window.bulkCalcProgressInterval = progressInterval;
          
          // Set timeout to complete the calculation
          setTimeout(() => {
            clearInterval(progressInterval);
            setBulkProgress({ current: 25, total: 25 });
            setLoadingBulkCalc(false);
            localStorage.removeItem('bulkCalculationStartTime');
            localStorage.removeItem('bulkCalculationParams');
            toast.info('Bulk calculation completed while you were away. Please re-run to get fresh results.');
          }, remainingIntervals * 10000);
        } else {
          // Calculation should be complete
          setBulkProgress({ current: 25, total: 25 });
          setLoadingBulkCalc(false);
          localStorage.removeItem('bulkCalculationStartTime');
          localStorage.removeItem('bulkCalculationParams');
          toast.info('Bulk calculation completed while you were away. Please re-run to get fresh results.');
        }
      } else {
        // Calculation should have completed, clean up
        localStorage.removeItem('bulkCalculationStartTime');
        localStorage.removeItem('bulkCalculationParams');
      }
    }
    
    return () => {
      clearInterval(statusInterval);
      clearInterval(alertsInterval);
      clearInterval(pendingRetestsInterval);
      clearInterval(divergenceInterval);
      // Clean up bulk calculation interval on unmount
      if (window.bulkCalcProgressInterval) {
        clearInterval(window.bulkCalcProgressInterval);
      }
    };
  }, []);

  // 📈 MOMENTUM AUTO-REFRESH: Update momentum data every 60 seconds
  useEffect(() => {
    // Set initial refresh time
    setMomentumLastRefresh(new Date());
    setMomentumCountdown(60);
    
    // Countdown timer (updates every second)
    const countdownInterval = setInterval(() => {
      setMomentumCountdown(prev => {
        if (prev <= 1) {
          return 60; // Reset countdown
        }
        return prev - 1;
      });
    }, 1000);
    
    // Fetch momentum data every 60 seconds
    const momentumInterval = setInterval(() => {
      fetchTrackedTradesAnalytics();
      setMomentumLastRefresh(new Date());
      setMomentumCountdown(60);
    }, 60000);
    
    return () => {
      clearInterval(countdownInterval);
      clearInterval(momentumInterval);
    };
  }, []);

  useEffect(() => {
    if (activeTab === 'protocolperf') {
      fetchProtocolSettings();
    } else if (activeTab === 'performance') {
      fetchTrackedTradesAnalytics();
      
      // Auto-refresh live P&L every 60 seconds
      const interval = setInterval(() => {
        fetchTrackedTradesAnalytics();
      }, 60000); // 60 seconds
      
      return () => clearInterval(interval);
    } else if (activeTab === 'lossanalysis') {
      fetchWinAnalysis();
      fetchLossAnalysis();
      fetchComparisonStats();
      fetchResetInfo();
    } else if (activeTab === 'probanalysis') {
      fetchWeeklyProbAnalysis();
    } else if (activeTab === 'filteredtrades') {
      fetchFilteredTradesAnalysis();
    }
  }, [activeTab]);

  // Fetch Weekly Probability Analysis
  const fetchWeeklyProbAnalysis = async (dayFilter = probDayFilter) => {
    setLoadingWeeklyProb(true);
    console.log('🎯 Fetching weekly probability analysis...', { dayFilter, backendUrl });
    try {
      const token = localStorage.getItem('auth_token');
      const url = `${backendUrl}/api/tracked-trades/weekly-probability-analysis?day_filter=${dayFilter}&system_version=v2.0`;
      console.log('🔗 API URL:', url);
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('📡 Response status:', response.status);
      
      const data = await response.json();
      console.log('📊 API Response:', data);
      
      if (data.success) {
        console.log('✅ Setting weeklyProbAnalysis with', data.week_summary?.total_trades, 'trades');
        setWeeklyProbAnalysis(data);
      } else {
        console.error('❌ API returned success=false:', data.error);
      }
    } catch (error) {
      console.error('❌ Error fetching weekly probability analysis:', error);
    }
    setLoadingWeeklyProb(false);
  };

  // Fetch Filtered Trades Analysis (News + Probability filtered trades)
  const fetchFilteredTradesAnalysis = async (filterType = filteredTradesFilter) => {
    setLoadingFilteredTrades(true);
    console.log('📰 Fetching filtered trades analysis...', { filterType, backendUrl });
    try {
      const token = localStorage.getItem('auth_token');
      const url = `${backendUrl}/api/tracked-trades/filtered-trades-analysis?filter_type=${filterType}&system_version=v2.0`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      console.log('📊 Filtered Trades Response:', data);
      
      if (data.success) {
        setFilteredTradesAnalysis(data);
      } else {
        console.error('❌ API returned success=false:', data.error);
        toast.error('Failed to fetch filtered trades analysis');
      }
    } catch (error) {
      console.error('❌ Error fetching filtered trades analysis:', error);
      toast.error('Error fetching filtered trades');
    }
    setLoadingFilteredTrades(false);
  };

  const fetchPricing = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/pricing`);
      const data = await response.json();
      if (data.success) {
        setTier1Prices(data.tier1);
        setTier2Prices(data.tier2);
        setBlackFridayPrice(data.black_friday?.price || 500);
        setBlackFridayEnabled(data.black_friday?.enabled || false);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const fetchInviteRequests = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/admin/invite-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setInviteRequests(data.requests);
      }
    } catch (error) {
      console.error('Error fetching invite requests:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/analytics`);
      const data = await response.json();
      if (data.success) {
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchTrackedTradesAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/tracked-trades/analytics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setTrackedTradesAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching tracked trades analytics:', error);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const fetchLossAnalysis = async () => {
    setLoadingLossAnalysis(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/admin/diagnose-losses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setLossAnalysis(data);
      }
    } catch (error) {
      console.error('Error fetching loss analysis:', error);
      toast.error('Failed to load loss analysis');
    } finally {
      setLoadingLossAnalysis(false);
    }
  };

  const fetchWinAnalysis = async () => {
    setLoadingWinAnalysis(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/admin/analyze-wins`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setWinAnalysis(data);
      }
    } catch (error) {
      console.error('Error fetching win analysis:', error);
      toast.error('Failed to load win analysis');
    } finally {
      setLoadingWinAnalysis(false);
    }
  };

  const fetchComparisonStats = async () => {
    setLoadingComparisonStats(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/admin/comparison-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setComparisonStats(data);
      }
    } catch (error) {
      console.error('Error fetching comparison stats:', error);
      toast.error('Failed to load comparison stats');
    } finally {
      setLoadingComparisonStats(false);
    }
  };

  const fetchResetInfo = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/admin/analysis-reset-info`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setResetInfo(data);
      }
    } catch (error) {
      console.error('Error fetching reset info:', error);
    }
  };

  const handleResetCarousel = async () => {
    if (!window.confirm('⚠️ Reset carousel to $0? This will archive all completed trades (they can still be analyzed). Continue?')) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/admin/reset-carousel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`✅ ${data.message}`);
        // Refresh analytics to show updated numbers
        fetchTrackedTradesAnalytics();
      } else {
        toast.error('Failed to reset carousel');
      }
    } catch (error) {
      console.error('Error resetting carousel:', error);
      toast.error('Error resetting carousel');
    }
  };

  const handleDeleteAllTrades = async () => {
    if (!window.confirm('🚨 WARNING: This will PERMANENTLY DELETE ALL trades (active, pending, and closed)!\n\nThis action CANNOT be undone!\n\nAre you absolutely sure?')) {
      return;
    }

    // Double confirmation for safety
    if (!window.confirm('⚠️ FINAL CONFIRMATION: Delete ALL trade data? Type YES in your mind and click OK.')) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/tracked-trades/delete-all`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`✅ Successfully deleted ${data.trades_deleted} trades. Database is now clean!`);
        // Refresh analytics to show empty state
        fetchTrackedTradesAnalytics();
      } else {
        toast.error('Failed to delete trades');
      }
    } catch (error) {
      console.error('Error deleting all trades:', error);
      toast.error('Error deleting all trades');
    }
  };



  const fetchBreakoutAnalytics = async (version = 'all') => {
    setLoadingBreakoutAnalytics(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/tracked-trades/breakout-analytics?version=${version}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setBreakoutAnalytics(data);
      }
      
      // Also fetch comparison data
      const compResponse = await fetch(`${backendUrl}/api/tracked-trades/breakout-comparison`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const compData = await compResponse.json();
      if (compData.success) {
        setBreakoutComparison(compData);
      }
    } catch (error) {
      console.error('Error fetching breakout analytics:', error);
    } finally {
      setLoadingBreakoutAnalytics(false);
    }
  };

  // Fetch divergence analytics (similar to breakout analytics)
  const fetchDivergenceAnalytics = async () => {
    setLoadingDivergenceAnalytics(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/tracked-trades/divergence-analytics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setDivergenceAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching divergence analytics:', error);
    } finally {
      setLoadingDivergenceAnalytics(false);
    }
  };

  // Fetch active breakout alerts (pending opportunities with trade parameters)
  const fetchBreakoutAlerts = async () => {
    // 🔧 FIX (Jan 27): Don't set loading state on background refreshes - causes screen flash
    // setLoadingBreakoutAlerts(true);  // REMOVED - was causing refresh
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/breakout-alerts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        const newAlerts = data.alerts || [];
        
        // 🔧 FIX (Jan 27): Only update state if alerts actually changed
        const alertsChanged = (() => {
          if (newAlerts.length !== breakoutAlerts.length) return true;
          for (let i = 0; i < newAlerts.length; i++) {
            if (newAlerts[i].alert_id !== breakoutAlerts[i]?.alert_id) return true;
            if (newAlerts[i].confidence !== breakoutAlerts[i]?.confidence) return true;
          }
          return false;
        })();
        
        // If there are NEW alerts that weren't there before, show notification
        const previousAlertIds = new Set(breakoutAlerts.map(a => a.alert_id));
        const brandNewAlerts = newAlerts.filter(a => !previousAlertIds.has(a.alert_id));
        
        if (brandNewAlerts.length > 0) {
          const newAlert = brandNewAlerts[0];
          toast.warning(
            `🚨 BREAKOUT DETECTED: ${newAlert.pair} ${newAlert.direction} (${newAlert.confidence}% confidence)`,
            { duration: 10000 }
          );
          
          // Play alert sound
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQ4JXrPb4pt0Fw05teleY8EPEVq82teleR0NMdrTpWkfFDl0wtOsZB0SON3WpWgeFC51wtKqYx0VMuHap2geFTB1w9OrYhwUL+Lap2keFy5zwtKsYxwULePbp2kfFi5zwtKsYxsVL+Tbp2ofFi1ywtKqYhsVMOXdqGofFi1ywtCqYRsVM+beqWofFSxyws+oYBkUM+beqWshFitxwc2mXhcTNencqmshFipvwMukXBYSN+vdq2shFSluvsilWhQQOe3frmwiEyhsu8WiWBMPPPDhsG0jEidrtcKfVRENQfTkunAlESVqr76cUg4LP/nqvnInESRoqruZTwwJQ/3xw3UqESFmpbaVTAoHSQH20ngrEB5jn7GSRwcFUQj94YI2Dxlhla2MQwQDWQ8A');
            audio.volume = 0.5;
            audio.play().catch(() => {});
          } catch (e) {}
        }
        
        // Only update state if there are actual changes (prevents re-render)
        if (alertsChanged) {
          setBreakoutAlerts(newAlerts);
        }
      }
    } catch (error) {
      console.error('Error fetching breakout alerts:', error);
    }
    // 🔧 FIX: Removed setLoadingBreakoutAlerts(false) - not needed without initial loading state
  };
  
  // 👀 Fetch pending retests (breakouts waiting for retest entry) - Jan 31, 2026
  const fetchPendingRetests = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/pending-retests`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setPendingRetests(data.pending_retests || []);
      }
    } catch (error) {
      console.error('Error fetching pending retests:', error);
    }
  };
  
  // 🚨 Fetch currency strength divergence alerts (violent move predictions)
  // 🔧 FIX (Jan 23): Smooth updates without full screen refresh + deduplication
  const fetchDivergenceAlerts = async (isInitialLoad = false) => {
    // Only show loading on initial load, not on background refreshes
    if (isInitialLoad) {
      setLoadingDivergenceAlerts(true);
    }
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/divergence-alerts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        let newAlerts = data.alerts || [];
        
        // 🔧 FIX: Deduplicate alerts - keep only one per pair (highest divergence score)
        const alertsByPair = {};
        newAlerts.forEach(alert => {
          const key = alert.pair;
          if (!alertsByPair[key] || alert.divergence_score > alertsByPair[key].divergence_score) {
            alertsByPair[key] = alert;
          }
        });
        newAlerts = Object.values(alertsByPair);
        
        // Sort by divergence score descending
        newAlerts.sort((a, b) => b.divergence_score - a.divergence_score);
        
        // 🔧 FIX (Jan 26): Use ref for stable comparison to avoid stale closure
        const currentAlerts = divergenceAlertsRef.current;
        const previousAlertIds = new Set(currentAlerts.map(a => a.alert_id));
        
        // Show toast for new HIGH or EXTREME alerts (only for truly new alerts)
        const brandNewAlerts = newAlerts.filter(a => !previousAlertIds.has(a.alert_id));
        if (brandNewAlerts.length > 0) {
          const newAlert = brandNewAlerts[0];
          if (newAlert.alert_level === 'EXTREME' || newAlert.alert_level === 'HIGH') {
            toast.error(
              `🚨 VIOLENT MOVE ALERT: ${newAlert.pair} ${newAlert.suggested_direction} - ${newAlert.alert_level} (${newAlert.divergence_score}pt divergence)`,
              { duration: 15000 }
            );
            
            // Play more urgent sound for divergence alerts
            try {
              const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQ4JXrPb4pt0Fw05teleY8EPEVq82teleR0NMdrTpWkfFDl0wtOsZB0SON3WpWgeFC51wtKqYx0VMuHap2geFTB1w9OrYhwUL+Lap2keFy5zwtKsYxwULePbp2kfFi5zwtKsYxsVL+Tbp2ofFi1ywtKqYhsVMOXdqGofFi1ywtCqYRsVM+beqWofFSxyws+oYBkUM+beqWshFitxwc2mXhcTNencqmshFipvwMukXBYSN+vdq2shFSluvsilWhQQOe3frmwiEyhsu8WiWBMPPPDhsG0jEidrtcKfVRENQfTkunAlESVqr76cUg4LP/nqvnInESRoqruZTwwJQ/3xw3UqESFmpbaVTAoHSQH20ngrEB5jn7GSRwcFUQj94YI2Dxlhla2MQwQDWQ8A');
              audio.volume = 0.7;
              audio.play().catch(() => {});
            } catch (e) {
              // Audio not supported
            }
          }
        }
        
        // 🔧 FIX (Jan 26): More robust change detection + prevent unnecessary re-renders
        // Use JSON comparison for deep equality check
        const alertsChanged = (() => {
          if (newAlerts.length !== divergenceAlerts.length) return true;
          
          // Compare each alert by key properties only (use ref for stable comparison)
          for (let i = 0; i < newAlerts.length; i++) {
            const newA = newAlerts[i];
            const oldA = currentAlerts[i];  // 🔧 FIX: Use ref instead of state
            if (!oldA) return true;
            if (newA.alert_id !== oldA.alert_id) return true;
            if (newA.divergence_score !== oldA.divergence_score) return true;
            if (newA.status !== oldA.status) return true;
            // Only check entry_quality if price_action exists
            if (newA.price_action?.entry_quality !== oldA.price_action?.entry_quality) return true;
          }
          return false;
        })();
        
        if (alertsChanged) {
          console.log('📊 Divergence alerts updated:', newAlerts.length, 'alerts');
          setDivergenceAlerts(newAlerts);
        } else {
          // No changes - skip state update to prevent re-render
          // This prevents the whole screen from refreshing
        }
      }
    } catch (error) {
      console.error('Error fetching divergence alerts:', error);
    } finally {
      if (isInitialLoad) {
        setLoadingDivergenceAlerts(false);
      }
    }
  };
  
  // Dismiss a divergence alert
  const dismissDivergenceAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${backendUrl}/api/divergence-alerts/${alertId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      fetchDivergenceAlerts();
      toast.success('Divergence alert dismissed');
    } catch (error) {
      console.error('Error dismissing divergence alert:', error);
      toast.error('Failed to dismiss alert');
    }
  };
  
  // 🎯 Show risk input for divergence alert
  const handleShowRiskInput = (alertId) => {
    setShowDivergenceRiskInput(prev => ({ ...prev, [alertId]: true }));
    setDivergenceRiskInput(prev => ({ ...prev, [alertId]: '100' })); // Default $100 risk
  };
  
  // 🎯 Activate trade from divergence alert - Creates actual tracked trade for ML learning & P&L
  const activateDivergenceTrade = async (alertId, pair, direction, riskAmount) => {
    try {
      const token = localStorage.getItem('auth_token');
      const risk = parseFloat(riskAmount) || 100;
      
      if (risk <= 0 || risk > 10000) {
        toast.error('Risk amount must be between $1 and $10,000');
        return;
      }
      
      toast.loading(`Activating ${pair} ${direction} trade with $${risk} risk...`);
      
      const response = await fetch(`${backendUrl}/api/divergence-alerts/${alertId}/activate-trade`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ risk_amount: risk })
      });
      
      const data = await response.json();
      toast.dismiss();
      
      if (data.success) {
        toast.success(`✅ Trade activated: ${pair} ${direction} @ ${data.trade.entry_price} (Risk: $${risk})`, {
          duration: 5000
        });
        // Clear risk input state
        setShowDivergenceRiskInput(prev => ({ ...prev, [alertId]: false }));
        setDivergenceRiskInput(prev => ({ ...prev, [alertId]: '' }));
        // Refresh divergence alerts (this one should be marked as activated)
        fetchDivergenceAlerts(false);
        // Refresh tracked trades analytics to show the new trade card
        fetchTrackedTradesAnalytics();
      } else if (data.blocked) {
        // 🔧 FIX: Handle blocked trades with proper message
        const reasons = data.reasons ? data.reasons.join(' | ') : 'Market conditions changed';
        const conditions = data.current_conditions;
        const conditionSummary = conditions 
          ? `\nMomentum: ${conditions.momentum_score} | Pullback: ${conditions.pullback_depth}% | 4H: ${conditions.htf_4h}`
          : '';
        
        toast.error(`🚫 Trade Blocked: ${data.message}${conditionSummary}`, {
          duration: 8000
        });
        
        // Refresh alerts to show updated conditions
        fetchDivergenceAlerts(false);
      } else {
        toast.error('Failed to activate trade: ' + (data.detail || data.error || data.message || 'Unknown error'));
      }
    } catch (error) {
      toast.dismiss();
      console.error('Error activating divergence trade:', error);
      toast.error('Failed to activate trade: ' + error.message);
    }
  };
  
  // Trigger manual divergence scan
  const triggerDivergenceScan = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      toast.loading('Scanning for divergence alerts...');
      const response = await fetch(`${backendUrl}/api/divergence-alerts/scan-now`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      toast.dismiss();
      if (data.success) {
        toast.success(`Scan complete! ${data.alerts_created || 0} new alerts found`);
        fetchDivergenceAlerts();
      } else {
        toast.error('Scan failed: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to trigger scan');
    }
  };

  // Dismiss a breakout alert
  const dismissBreakoutAlert = async (alertId) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/breakout-alerts/${alertId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Alert dismissed');
        fetchBreakoutAlerts(); // Refresh alerts
      }
    } catch (error) {
      console.error('Error dismissing alert:', error);
      toast.error('Failed to dismiss alert');
    }
  };

  // 🔧 NEW (Jan 26): Activate breakout trade with custom risk amount
  const activateBreakoutTrade = async (alertId, pair, direction, riskAmount) => {
    try {
      const token = localStorage.getItem('auth_token');
      const risk = parseFloat(riskAmount) || 100;
      
      if (risk <= 0 || risk > 10000) {
        toast.error('Risk amount must be between $1 and $10,000');
        return;
      }
      
      toast.loading(`Activating ${pair} ${direction} breakout trade with $${risk} risk...`);
      
      const response = await fetch(`${backendUrl}/api/breakout-alerts/${alertId}/activate-trade`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ risk_amount: risk })
      });
      
      const data = await response.json();
      toast.dismiss();
      
      if (data.success) {
        toast.success(`✅ Breakout Trade activated: ${pair} ${direction} @ ${data.trade.entry_price} (Risk: $${risk})`, {
          duration: 5000
        });
        // Clear risk input state
        setShowBreakoutRiskInput(prev => ({ ...prev, [alertId]: false }));
        setBreakoutRiskInput(prev => ({ ...prev, [alertId]: '' }));
        // Refresh breakout alerts
        fetchBreakoutAlerts();
        // Refresh tracked trades analytics
        fetchTrackedTradesAnalytics();
      } else if (data.blocked) {
        toast.error(`🚫 Trade Blocked: ${data.message}`, { duration: 8000 });
        fetchBreakoutAlerts();
      } else {
        toast.error('Failed to activate trade: ' + (data.detail || data.error || data.message || 'Unknown error'));
      }
    } catch (error) {
      toast.dismiss();
      console.error('Error activating breakout trade:', error);
      toast.error('Failed to activate trade: ' + error.message);
    }
  };

  // Trigger manual breakout scan
  const triggerBreakoutScan = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      toast.info('Scanning all pairs for breakout opportunities...');
      const response = await fetch(`${backendUrl}/api/breakout-alerts/scan-now`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Scan complete! Found ${data.alerts_created || 0} new opportunities`);
        fetchBreakoutAlerts(); // Refresh alerts
      } else {
        toast.error(data.message || 'Scan failed');
      }
    } catch (error) {
      console.error('Error triggering scan:', error);
      toast.error('Failed to trigger scan');
    }
  };

  const fetchCustomPerformance = async () => {
    if (!customStartDate || !customEndDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    setLoadingCustomPerformance(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/tracked-trades/custom-performance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          start_date: customStartDate,
          end_date: customEndDate
        })
      });
      const data = await response.json();
      if (data.success) {
        setCustomPerformance(data);
        toast.success(`Performance data loaded for ${data.period.days} days`);
      } else {
        toast.error('Failed to fetch custom performance data');
      }
    } catch (error) {
      console.error('Error fetching custom performance:', error);
      toast.error('Error fetching performance data');
    } finally {
      setLoadingCustomPerformance(false);
    }
  };

  const setQuickDateRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    
    setCustomStartDate(start.toISOString().split('T')[0]);
    setCustomEndDate(end.toISOString().split('T')[0]);
  };

  // 📒 Protocol Performance functions
  const fetchProtocolSettings = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/protocol-settings`);
      const data = await response.json();
      if (data.success && data.settings) setProtocolSettings(data.settings);
    } catch (error) {
      console.error('Error fetching protocol settings:', error);
    }
  };

  const saveProtocolSettings = async () => {
    setSavingProtocolSettings(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/protocol-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(protocolSettings)
      });
      const data = await response.json();
      if (data.success) {
        setProtocolSettings(data.settings);
        toast.success('Protocol settings saved');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSavingProtocolSettings(false);
    }
  };

  const updateMilestone = (index, field, value) => {
    const ms = [...protocolSettings.milestones];
    ms[index] = { ...ms[index], [field]: value === '' ? '' : parseFloat(value) };
    setProtocolSettings({ ...protocolSettings, milestones: ms });
  };

  const addMilestone = () => {
    setProtocolSettings({ ...protocolSettings, milestones: [...protocolSettings.milestones, { trigger_r: '', move_sl_to_r: '' }] });
  };

  const removeMilestone = (index) => {
    setProtocolSettings({ ...protocolSettings, milestones: protocolSettings.milestones.filter((_, i) => i !== index) });
  };

  const setProtocolQuickRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setProtocolStartDate(start.toISOString().split('T')[0]);
    setProtocolEndDate(end.toISOString().split('T')[0]);
  };

  const fetchProtocolPerformance = async () => {
    if (!protocolStartDate || !protocolEndDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    setLoadingProtocolPerformance(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/protocol-performance?start_date=${protocolStartDate}&end_date=${protocolEndDate}`);
      const data = await response.json();
      if (data.success) {
        setProtocolPerformance(data);
        toast.success(`Loaded ${data.overall.trades} notifications`);
      } else {
        toast.error('Failed to fetch performance');
      }
    } catch (error) {
      toast.error('Error fetching performance');
    } finally {
      setLoadingProtocolPerformance(false);
    }
  };

  const runExitSimulation = async () => {
    if (!protocolStartDate || !protocolEndDate) {
      toast.error('Please select both start and end dates');
      return;
    }
    setLoadingExitSim(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/protocol-performance/simulate?start_date=${protocolStartDate}&end_date=${protocolEndDate}&sl_tightness_pct=${slTightnessPct}&tp_r=${tpR}`);
      const data = await response.json();
      if (data.success) {
        setExitSim(data);
        toast.success(`Simulated ${data.overall.trades} trades`);
      } else {
        toast.error(data.error || 'Failed to run simulation');
      }
    } catch (error) {
      toast.error('Error running simulation');
    } finally {
      setLoadingExitSim(false);
    }
  };

  const downloadProtocolCSV = () => {
    if (!protocolPerformance || !protocolPerformance.trades) return;
    const headers = ['date', 'pair', 'scanner', 'direction', 'outcome', 'sl_pips', 'protocol_r', 'protocol_pnl_usd', 'full_potential_r', 'full_potential_pnl_usd'];
    const rows = protocolPerformance.trades.map((t) => headers.map((h) => t[h]).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `protocol-performance_${protocolStartDate}_to_${protocolEndDate}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const clearProtocolHistory = async () => {    if (!window.confirm('⚠️ This permanently deletes ALL logged notification performance data (resets the Protocol\'s Performance log to zero). This cannot be undone. Continue?')) {
      return;
    }
    try {
      const response = await fetch(`${backendUrl}/api/admin/protocol-performance/clear`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        setProtocolPerformance(null);
        toast.success(`History cleared — ${data.deleted} records deleted`);
      } else {
        toast.error('Failed to clear history');
      }
    } catch (error) {
      toast.error('Error clearing history');
    }
  };

  // Inline SVG dual-line equity curve (protocol vs left-to-run)
  const renderEquityCurve = (curve) => {
    if (!curve || curve.length < 2) {
      return <div style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Not enough data to plot an equity curve.</div>;
    }
    const W = 900, H = 280, PAD = 48;
    // Downsample to ~180 points for a clean line
    const step = Math.max(1, Math.floor(curve.length / 180));
    const pts = curve.filter((_, i) => i % step === 0 || i === curve.length - 1);
    const allVals = pts.flatMap((p) => [p.protocol_cum, p.full_potential_cum]).concat([0]);
    const minV = Math.min(...allVals);
    const maxV = Math.max(...allVals);
    const range = (maxV - minV) || 1;
    const x = (i) => PAD + (i / (pts.length - 1)) * (W - 2 * PAD);
    const y = (v) => PAD + (1 - (v - minV) / range) * (H - 2 * PAD);
    const line = (key) => pts.map((p, i) => `${x(i)},${y(p[key])}`).join(' ');
    const zeroY = y(0);
    return (
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }} data-testid="equity-curve-svg">
        <rect x="0" y="0" width={W} height={H} fill="#fafafa" rx="8" />
        {/* zero baseline */}
        {minV < 0 && maxV > 0 && (
          <line x1={PAD} y1={zeroY} x2={W - PAD} y2={zeroY} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="4 4" />
        )}
        {/* axes labels */}
        <text x={PAD - 8} y={y(maxV) + 4} textAnchor="end" fontSize="11" fill="#94a3b8">${Math.round(maxV).toLocaleString()}</text>
        <text x={PAD - 8} y={y(minV) + 4} textAnchor="end" fontSize="11" fill="#94a3b8">${Math.round(minV).toLocaleString()}</text>
        {/* full potential line (amber) */}
        <polyline fill="none" stroke="#d97706" strokeWidth="2" points={line('full_potential_cum')} />
        {/* protocol line (teal) */}
        <polyline fill="none" stroke="#0891b2" strokeWidth="2.5" points={line('protocol_cum')} />
        {/* legend */}
        <rect x={W - 230} y={14} width="14" height="3" fill="#0891b2" />
        <text x={W - 210} y={20} fontSize="12" fill="#475569">Protocol P&amp;L</text>
        <rect x={W - 230} y={34} width="14" height="3" fill="#d97706" />
        <text x={W - 210} y={40} fontSize="12" fill="#475569">Left to Run P&amp;L</text>
      </svg>
    );
  };

  // Fetch 6-hour session performance
  const fetchSessionPerformance = async () => {
    if (!customStartDate || !customEndDate) {
      toast.error('Please select both start and end dates first');
      return;
    }

    setLoadingSessionPerformance(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/tracked-trades/session-performance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          start_date: customStartDate,
          end_date: customEndDate
        })
      });
      const data = await response.json();
      if (data.success) {
        setSessionPerformance(data);
        toast.success(`Session analysis loaded for ${data.total_trades_analyzed} trades`);
      } else {
        toast.error('Failed to fetch session performance data');
      }
    } catch (error) {
      console.error('Error fetching session performance:', error);
      toast.error('Error fetching session data');
    } finally {
      setLoadingSessionPerformance(false);
    }
  };

  // Fetch ML Loss Analysis (Intelligent Loss Learning)
  const fetchMlLossAnalysis = async () => {
    setLoadingMlLossAnalysis(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/loss-analysis/summary`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setMlLossAnalysis(data);
        if (data.total_analyses > 0) {
          toast.success(`ML Loss Analysis loaded: ${data.total_analyses} losses analyzed`);
        } else {
          toast.info('No loss analyses yet - data will populate as trades close');
        }
      } else {
        toast.error('Failed to fetch ML loss analysis');
      }
    } catch (error) {
      console.error('Error fetching ML loss analysis:', error);
      toast.error('Error fetching loss analysis');
    } finally {
      setLoadingMlLossAnalysis(false);
    }
  };

  // Backfill loss analysis for existing trades
  const [backfillLoading, setBackfillLoading] = useState(false);
  const runLossAnalysisBackfill = async () => {
    setBackfillLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/loss-analysis/backfill`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`✅ Backfill complete! Analyzed ${data.analyzed_count} trades`);
        // Refresh the loss analysis data
        fetchMlLossAnalysis();
      } else {
        toast.error(`Backfill failed: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error running backfill:', error);
      toast.error('Error running loss analysis backfill');
    } finally {
      setBackfillLoading(false);
    }
  };

  // Fetch Deep Pattern Analysis
  const fetchDeepPatterns = async () => {
    setLoadingDeepPatterns(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/loss-analysis/deep-patterns`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setDeepPatterns(data);
        toast.success(`Deep analysis complete: ${data.total_analyzed} trades analyzed`);
      } else {
        toast.error('Failed to load deep patterns');
      }
    } catch (error) {
      console.error('Error fetching deep patterns:', error);
      toast.error('Error loading deep pattern analysis');
    } finally {
      setLoadingDeepPatterns(false);
    }
  };


  const fetchProbabilityAnalysis = async () => {
    setLoadingProbabilityAnalysis(true);
    setProbabilityAnalysisError(null);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setProbabilityAnalysisError('Not authenticated. Please log in again.');
        toast.error('Not authenticated. Please log in again.');
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/admin/probability-analysis`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setProbabilityAnalysis(data);
        setProbabilityAnalysisError(null);
        if (data.action_required) {
          toast.warning(`Threshold adjustment recommended: ${data.recommendation_reason}`);
        } else {
          toast.success('Probability analysis complete!');
        }
      } else {
        setProbabilityAnalysis(null);
        setProbabilityAnalysisError(data.message || 'Failed to analyze probability data');
        toast.error(data.message || 'Failed to analyze probability data');
      }
    } catch (error) {
      console.error('Error fetching probability analysis:', error);
      setProbabilityAnalysisError('Error connecting to server');
      toast.error('Error analyzing probability data');
    } finally {
      setLoadingProbabilityAnalysis(false);
    }
  };



  const handleApplyThreshold = async (newThreshold) => {
    if (!window.confirm(`Apply new threshold of ${newThreshold}%? This will filter out trades below this probability.`)) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/update-threshold`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ threshold: newThreshold })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`✅ Threshold updated to ${newThreshold}%!`);
        // Refresh analysis to show new current threshold
        fetchProbabilityAnalysis();
      } else {
        toast.error('Failed to update threshold');
      }
    } catch (error) {
      console.error('Error updating threshold:', error);
      toast.error('Error updating threshold');
    }
  };

  const handleCalculateAllPairs = async () => {
    if (!bulkRiskAmount) {
      toast.error('Please enter risk amount');
      return;
    }

    setLoadingBulkCalc(true);
    setBulkProgress({ current: 0, total: 24 });
    setBulkResults(null);

    // Store calculation start time in localStorage for persistence
    const calculationStartTime = Date.now();
    localStorage.setItem('bulkCalculationStartTime', calculationStartTime);
    localStorage.setItem('bulkCalculationParams', JSON.stringify({
      risk_amount: bulkRiskAmount,
      timeframe: bulkTimeframe
    }));

    try {
      const token = localStorage.getItem('auth_token');
      
      // Simulate progress with realistic 20-second intervals
      // Total time: ~8 minutes (24 pairs × 20 seconds)
      const progressInterval = setInterval(() => {
        setBulkProgress(prev => {
          if (prev.current < prev.total - 1) {
            return { ...prev, current: prev.current + 1 };
          }
          return prev;
        });
      }, 20000);  // Update every 20 seconds to match backend delay
      
      // Store interval ID for cleanup
      window.bulkCalcProgressInterval = progressInterval;

      const response = await axios.post(
        `${backendUrl}/api/calculate-all-pairs`,
        {
          risk_amount: parseFloat(bulkRiskAmount),
          account_balance: 10000, // Default value - not shown to user
          timeframe: bulkTimeframe
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          params: {
            risk_amount: parseFloat(bulkRiskAmount),
            account_balance: 10000, // Default value - not shown to user
            timeframe: bulkTimeframe
          }
        }
      );

      clearInterval(progressInterval);
      setBulkProgress({ current: 24, total: 24 });

      if (response.data.success) {
        setBulkResults(response.data);
        toast.success(`✅ Calculated ${response.data.successful} pairs successfully!`);
      } else {
        toast.error('Bulk calculation failed');
      }
    } catch (error) {
      console.error('Error calculating all pairs:', error);
      toast.error(error.response?.data?.detail || 'Error calculating pairs');
    } finally {
      // Clean up localStorage and intervals
      localStorage.removeItem('bulkCalculationStartTime');
      localStorage.removeItem('bulkCalculationParams');
      if (window.bulkCalcProgressInterval) {
        clearInterval(window.bulkCalcProgressInterval);
        window.bulkCalcProgressInterval = null;
      }
      setLoadingBulkCalc(false);
    }
  };

  const exportBulkResults = (format) => {
    if (!bulkResults) return;

    if (format === 'csv') {
      // Create CSV
      const headers = ['Pair', 'Status', 'Direction', 'Entry Price', 'Stop Loss', 'Take Profit', 'R:R Ratio', 'Probability %', 'Potential Profit', 'Lot Size', 'Order Type'];
      const rows = bulkResults.results.map(r => [
        r.pair,
        r.status,
        r.direction || '',
        r.entry_price || '',
        r.stop_loss || '',
        r.take_profit || '',
        r.rr_ratio || '',
        r.probability || '',
        r.potential_profit || '',
        r.lot_size || '',
        r.order_type || ''
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulk-calculation-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success('CSV exported successfully!');
    } else if (format === 'json') {
      // Create JSON
      const jsonContent = JSON.stringify(bulkResults, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulk-calculation-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      toast.success('JSON exported successfully!');
    }
  };

  const handleTradeCardClick = async (trade) => {
    // CRITICAL FIX: Store the trade_id to prevent race conditions
    // When API response returns, only update if this is still the selected trade
    const clickedTradeId = trade.trade_id;
    
    setSelectedTrade(trade);
    setShowTradeDetailModal(true);
    
    // For active trades, fetch current live stats
    if (trade.status === 'ACTIVE' || trade.status === 'PENDING') {
      setTradeDetailLoading(true);
      try {
        const token = localStorage.getItem('auth_token');
        const response = await axios.get(
          `${backendUrl}/api/trade-detail/${clickedTradeId}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        // RACE CONDITION FIX: Only update if user hasn't clicked a different trade
        // Use functional state update to get the current selectedTrade value
        setSelectedTrade(currentTrade => {
          // If the modal was closed or user clicked a different trade, don't update
          if (!currentTrade || currentTrade.trade_id !== clickedTradeId) {
            console.log('⚠️ Ignoring stale API response for trade:', clickedTradeId);
            return currentTrade;
          }
          // Merge the API response with the original trade data to ensure all fields are present
          console.log('✅ Updating trade details for:', clickedTradeId);
          return { ...currentTrade, ...response.data };
        });
      } catch (error) {
        console.error('Error fetching live trade details:', error);
      } finally {
        setTradeDetailLoading(false);
      }
    }
  };

  const fetchTradeStats = async () => {
    setLoadingTradeStats(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/trade-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setTradeStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching trade statistics:', error);
    } finally {
      setLoadingTradeStats(false);
    }
  };



  const fetchFamilyWhitelist = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/family-friends/list`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setFamilyWhitelist(data.whitelist || []);
      }
    } catch (error) {
      console.error('Error fetching family whitelist:', error);
    }
  };

  const handleAddToWhitelist = async (e) => {
    e.preventDefault();
    setWhitelistMessage('');
    
    if (!newFriend.name || !newFriend.relationship || !newFriend.email) {
      setWhitelistMessage('Please fill in all fields');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/family-friends/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          friend_name: newFriend.name,
          relationship: newFriend.relationship,
          email: newFriend.email
        })
      });

      const data = await response.json();
      
      if (data.success) {
        if (data.upgraded_existing) {
          setWhitelistMessage(`✅ ${data.message} Their subscription has been upgraded immediately!`);
        } else {
          setWhitelistMessage(`✅ ${data.message}`);
        }
        setNewFriend({ name: '', relationship: '', email: '' });
        fetchFamilyWhitelist();
      } else {
        setWhitelistMessage(`❌ ${data.message}`);
      }
    } catch (error) {
      setWhitelistMessage('❌ Error adding to whitelist');
      console.error('Error:', error);
    }
  };

  const fetchBlacklist = async () => {
    setLoadingBlacklist(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/admin/blacklist`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        const previousCount = blacklist.length;
        const newCount = data.blacklist.length;
        if (newCount > previousCount && previousCount > 0) {
          setNewBlacklistEntries(newCount - previousCount);
          setTimeout(() => setNewBlacklistEntries(0), 5000); // Clear flash after 5 seconds
        }
        setBlacklist(data.blacklist || []);
      }
    } catch (error) {
      console.error('Error fetching blacklist:', error);
    } finally {
      setLoadingBlacklist(false);
    }
  };

  const handleRemoveFromBlacklist = async (blacklistId) => {
    if (!window.confirm('Are you sure you want to remove this user from the blacklist?')) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/admin/remove-from-blacklist`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ blacklist_id: blacklistId })
      });

      const data = await response.json();
      if (data.success) {
        alert(`✅ ${data.message}`);
        fetchBlacklist();
      } else {
        alert(`❌ ${data.message || 'Failed to remove from blacklist'}`);
      }
    } catch (error) {
      console.error('Error removing from blacklist:', error);
      alert('❌ Error removing from blacklist');
    }
  };

  const handleRemoveFromWhitelist = async (whitelistId, friendName) => {
    if (!window.confirm(`Remove ${friendName} from whitelist? If they have an active account with free access, it will be revoked and converted to trial.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${backendUrl}/api/family-friends/remove/${whitelistId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        if (data.access_revoked) {
          setWhitelistMessage(`✅ ${data.message}`);
        } else {
          setWhitelistMessage(`✅ ${friendName} removed from whitelist`);
        }
        fetchFamilyWhitelist();
      } else {
        setWhitelistMessage(`❌ ${data.message}`);
      }
    } catch (error) {
      setWhitelistMessage('❌ Error removing from whitelist');
      console.error('Error:', error);
    }
  };

  const savePricing = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${backendUrl}/api/admin/update-pricing`, {
        tier1: tier1Prices,
        tier2: tier2Prices,
        black_friday: {
          price: blackFridayPrice,
          enabled: blackFridayEnabled
        }
      });

      if (response.data.success) {
        toast.success('Pricing updated successfully!');
      } else {
        toast.error('Failed to update pricing');
      }
    } catch (error) {
      console.error('Error saving pricing:', error);
      toast.error('Failed to save pricing');
    } finally {
      setLoading(false);
    }
  };

  const approveInviteRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/admin/invite-requests/${requestId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success(`User approved with 7-day trial!`);
        fetchInviteRequests();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve invite');
    }
  };

  const rejectInviteRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/admin/invite-requests/${requestId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        toast.success('Invite request rejected');
        fetchInviteRequests();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject invite');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
      padding: '2rem'
    }}>
      {/* Toast notifications */}
      <Toaster position="top-right" richColors />
      
      {/* 🚨 FLASHING BREAKOUT ALERT BANNER - Shows when breakouts detected */}
      {breakoutAlerts.length > 0 && (
        <div 
          className="breakout-banner"
          data-testid="breakout-alert-banner"
          onClick={() => {
            // Scroll to breakout alerts section
            document.getElementById('breakout-alerts-section')?.scrollIntoView({ behavior: 'smooth' });
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            padding: '1rem 2rem',
            color: 'white',
            fontWeight: '700',
            fontSize: '1.25rem',
            textAlign: 'center',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}
        >
          <span style={{ fontSize: '2rem', animation: 'flash 0.5s infinite' }}>⚡</span>
          <span>
            🔔 {breakoutAlerts.length} BREAKOUT ALERT{breakoutAlerts.length > 1 ? 'S' : ''} DETECTED! 
            {breakoutAlerts[0] && ` - ${breakoutAlerts[0].pair} ${breakoutAlerts[0].direction} (${breakoutAlerts[0].confidence}%)`}
          </span>
          <span style={{ fontSize: '2rem', animation: 'flash 0.5s infinite' }}>⚡</span>
          <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Click to view</span>
        </div>
      )}
      
      {/* 🚨 CURRENCY DIVERGENCE ALERT BANNER - Shows when violent move potential detected */}
      {divergenceAlerts.length > 0 && divergenceAlerts.some(a => a.alert_level === 'EXTREME' || a.alert_level === 'HIGH') && (
        <div 
          className="divergence-banner"
          data-testid="divergence-alert-banner"
          onClick={() => {
            document.getElementById('divergence-alerts-section')?.scrollIntoView({ behavior: 'smooth' });
          }}
          style={{
            position: 'fixed',
            top: breakoutAlerts.length > 0 ? '4rem' : 0,
            left: 0,
            right: 0,
            zIndex: 9998,
            padding: '1rem 2rem',
            background: 'linear-gradient(90deg, #dc2626, #ef4444, #dc2626)',
            backgroundSize: '200% 100%',
            animation: 'gradient-shift 2s ease infinite',
            color: 'white',
            fontWeight: '700',
            fontSize: '1.25rem',
            textAlign: 'center',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            boxShadow: '0 4px 20px rgba(220,38,38,0.5)'
          }}
        >
          <span style={{ fontSize: '2rem', animation: 'flash 0.3s infinite' }}>🚨</span>
          <span>
            ⚠️ VIOLENT MOVE ALERT: {divergenceAlerts.filter(a => a.alert_level === 'EXTREME' || a.alert_level === 'HIGH')[0]?.pair} {divergenceAlerts.filter(a => a.alert_level === 'EXTREME' || a.alert_level === 'HIGH')[0]?.suggested_direction} 
            - {divergenceAlerts.filter(a => a.alert_level === 'EXTREME' || a.alert_level === 'HIGH')[0]?.divergence_score}pt DIVERGENCE
          </span>
          <span style={{ fontSize: '2rem', animation: 'flash 0.3s infinite' }}>🚨</span>
          <span style={{ fontSize: '0.875rem', opacity: 0.9 }}>Click to view trade setup</span>
        </div>
      )}
      
      <div style={{ maxWidth: '1200px', margin: '0 auto', paddingTop: (breakoutAlerts.length > 0 ? 4 : 0) + (divergenceAlerts.some(a => a.alert_level === 'EXTREME' || a.alert_level === 'HIGH') ? 4 : 0) + 'rem' }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h1 style={{ color: 'white', fontSize: '2rem', fontWeight: '700' }}>
            ⚙️ Kaleb's Admin Dashboard
          </h1>
          <GuardianStatus />
          <Link to="/calculator" style={{
            padding: '0.75rem 1.5rem',
            background: 'white',
            color: '#f59e0b',
            textDecoration: 'none',
            borderRadius: '8px',
            fontWeight: '600',
            border: '2px solid rgba(255,255,255,0.3)'
          }}>
            ← Back to Trading
          </Link>
        </div>

        {/* Organized Tabs - Grouped by Category */}
        <div style={{ marginBottom: '2rem' }}>
          {/* Trading Analytics Section */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ 
              color: 'rgba(255,255,255,0.7)', 
              fontSize: '0.85rem', 
              fontWeight: '600', 
              marginBottom: '0.5rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}>
              📊 Trading Analytics
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {['protocolperf', 'performance', 'lossanalysis', 'probanalysis', 'filteredtrades', 'tradestats', 'mlprogress', 'tierstats', 'notificationlab'].map((tab) => (
                <button
                  key={tab}
                  data-testid={`admin-tab-${tab}`}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: activeTab === tab ? 'white' : 'rgba(255,255,255,0.2)',
                    color: activeTab === tab ? '#f59e0b' : 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {tab === 'protocolperf' && "📒 Protocol's Performance"}
                  {tab === 'performance' && '📈 Trade Performance'}
                  {tab === 'lossanalysis' && '📊 Win/Loss Analysis'}
                  {tab === 'probanalysis' && '🎯 Probability Analysis'}
                  {tab === 'filteredtrades' && '🚫 Filtered Trades'}
                  {tab === 'tradestats' && '📋 My Personal Stats'}
                  {tab === 'mlprogress' && '🧠 ML Learning Progress'}
                  {tab === 'tierstats' && '🚀 Ignition Tier Stats'}
                  {tab === 'notificationlab' && '🔬 Notification Lab'}
                </button>
              ))}
            </div>
          </div>

          {/* User Management Section */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ 
              color: 'rgba(255,255,255,0.7)', 
              fontSize: '0.85rem', 
              fontWeight: '600', 
              marginBottom: '0.5rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}>
              👥 User Management
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {['invites', 'analytics', 'family', 'blacklist'].map((tab) => (
                <button
                  key={tab}
                  data-testid={`admin-tab-${tab}`}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: activeTab === tab ? 'white' : 'rgba(255,255,255,0.2)',
                    color: activeTab === tab ? '#f59e0b' : 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    position: 'relative'
                  }}
                >
                  {tab === 'invites' && '📨 Invite Requests'}
                  {tab === 'analytics' && '📊 User Analytics'}
                  {tab === 'family' && '👨‍👩‍👧‍👦 Family & Friends'}
                  {tab === 'blacklist' && (
                    <>
                      🚫 Blacklist
                      {newBlacklistEntries > 0 && (
                        <span style={{
                          position: 'absolute',
                          top: '-5px',
                          right: '-5px',
                          background: '#ef4444',
                          color: 'white',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          animation: 'flash 1s infinite'
                        }}>
                          {newBlacklistEntries}
                        </span>
                      )}
                    </>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Business Settings Section */}
          <div>
            <div style={{ 
              color: 'rgba(255,255,255,0.7)', 
              fontSize: '0.85rem', 
              fontWeight: '600', 
              marginBottom: '0.5rem',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}>
              ⚙️ Business Settings
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {['pricing', 'stripe'].map((tab) => (
                <button
                  key={tab}
                  data-testid={`admin-tab-${tab}`}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: activeTab === tab ? 'white' : 'rgba(255,255,255,0.2)',
                    color: activeTab === tab ? '#f59e0b' : 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {tab === 'pricing' && '💰 Pricing Management'}
                  {tab === 'stripe' && '💳 Stripe Setup'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '2rem',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          {/* 📒 Protocol's Performance Tab */}
          {activeTab === 'protocolperf' && (
            <div data-testid="protocol-performance-tab">
              <h2 style={{ fontSize: '1.75rem', marginBottom: '0.5rem', color: '#0c4a6e' }}>
                📒 Protocol's Performance
              </h2>
              <p style={{ color: '#64748b', marginBottom: '2rem' }}>
                Every notification the protocol sends, scored against your Protocol Settings.
              </p>

              {/* ===== PROTOCOL SETTINGS ===== */}
              <div style={{ border: '2px solid #e2e8f0', borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem' }} data-testid="protocol-settings-panel">
                <h3 style={{ fontSize: '1.25rem', marginBottom: '1.25rem', color: '#0c4a6e' }}>⚙️ Protocol Settings</h3>
                <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem' }}>Account Size ($)</label>
                    <input
                      type="number"
                      data-testid="protocol-account-size-input"
                      value={protocolSettings.account_size}
                      onChange={(e) => setProtocolSettings({ ...protocolSettings, account_size: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                      style={{ padding: '0.6rem 0.85rem', border: '1px solid #cbd5e1', borderRadius: '8px', width: '180px', fontSize: '1rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem' }}>Risk per Trade (% of account)</label>
                    <input
                      type="number"
                      step="0.1"
                      data-testid="protocol-risk-pct-input"
                      value={protocolSettings.risk_pct}
                      onChange={(e) => setProtocolSettings({ ...protocolSettings, risk_pct: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                      style={{ padding: '0.6rem 0.85rem', border: '1px solid #cbd5e1', borderRadius: '8px', width: '180px', fontSize: '1rem' }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '0.6rem 1rem' }}>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Risk $ / trade (1R)</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#0369a1' }} data-testid="protocol-risk-usd">
                        ${(((parseFloat(protocolSettings.account_size) || 0) * (parseFloat(protocolSettings.risk_pct) || 0)) / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#475569', marginBottom: '0.75rem' }}>
                  Trailing Stop-Loss Milestones
                </div>
                <div style={{ marginBottom: '1rem' }} data-testid="protocol-milestones-list">
                  {protocolSettings.milestones.map((m, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.6rem', flexWrap: 'wrap' }} data-testid={`protocol-milestone-${i}`}>
                      <span style={{ color: '#64748b', fontSize: '0.9rem', minWidth: '90px' }}>Milestone {i + 1}:</span>
                      <span style={{ color: '#475569', fontSize: '0.9rem' }}>at</span>
                      <input
                        type="number"
                        step="0.1"
                        data-testid={`milestone-trigger-${i}`}
                        value={m.trigger_r}
                        onChange={(e) => updateMilestone(i, 'trigger_r', e.target.value)}
                        style={{ padding: '0.45rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: '6px', width: '80px' }}
                      />
                      <span style={{ color: '#475569', fontSize: '0.9rem' }}>R &rarr; move SL to</span>
                      <input
                        type="number"
                        step="0.1"
                        data-testid={`milestone-move-${i}`}
                        value={m.move_sl_to_r}
                        onChange={(e) => updateMilestone(i, 'move_sl_to_r', e.target.value)}
                        style={{ padding: '0.45rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: '6px', width: '80px' }}
                      />
                      <span style={{ color: '#475569', fontSize: '0.9rem' }}>R</span>
                      <button
                        onClick={() => removeMilestone(i)}
                        data-testid={`remove-milestone-${i}`}
                        style={{ padding: '0.35rem 0.7rem', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    onClick={addMilestone}
                    data-testid="add-milestone-button"
                    style={{ padding: '0.6rem 1.2rem', background: '#e0f2fe', color: '#0369a1', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                  >
                    + Add Milestone
                  </button>
                  <button
                    onClick={saveProtocolSettings}
                    disabled={savingProtocolSettings}
                    data-testid="save-protocol-settings-button"
                    style={{ padding: '0.6rem 1.5rem', background: '#0891b2', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}
                  >
                    {savingProtocolSettings ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>

              {/* ===== PERFORMANCE QUERY ===== */}
              <div style={{ border: '2px solid #e2e8f0', borderRadius: '12px', padding: '1.5rem' }} data-testid="performance-query-panel">
                <h3 style={{ fontSize: '1.25rem', marginBottom: '1.25rem', color: '#0c4a6e' }}>📅 Performance Query</h3>
                <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '0.5rem' }}>Quick Select:</div>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
                  {[{ d: 7, l: '7 Days' }, { d: 30, l: '30 Days' }, { d: 90, l: '90 Days' }, { d: 365, l: '365 Days' }].map((q) => (
                    <button
                      key={q.d}
                      onClick={() => setProtocolQuickRange(q.d)}
                      data-testid={`protocol-quick-${q.d}`}
                      style={{ padding: '0.6rem 1.25rem', background: '#e0f2fe', color: '#0369a1', border: '1px solid #7dd3fc', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                    >
                      {q.l}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem' }}>From:</label>
                    <input type="date" data-testid="protocol-start-date" value={protocolStartDate} onChange={(e) => setProtocolStartDate(e.target.value)} style={{ padding: '0.6rem', border: '1px solid #cbd5e1', borderRadius: '8px' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '0.35rem' }}>To:</label>
                    <input type="date" data-testid="protocol-end-date" value={protocolEndDate} onChange={(e) => setProtocolEndDate(e.target.value)} style={{ padding: '0.6rem', border: '1px solid #cbd5e1', borderRadius: '8px' }} />
                  </div>
                  <button
                    onClick={fetchProtocolPerformance}
                    disabled={loadingProtocolPerformance}
                    data-testid="fetch-protocol-performance-button"
                    style={{ padding: '0.7rem 1.75rem', background: '#0891b2', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '1rem' }}
                  >
                    {loadingProtocolPerformance ? 'Loading...' : 'Fetch Performance'}
                  </button>
                  <button
                    onClick={clearProtocolHistory}
                    data-testid="clear-history-button"
                    style={{ padding: '0.7rem 1.5rem', background: '#fff', color: '#dc2626', border: '2px solid #dc2626', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '1rem' }}
                  >
                    🗑️ Clear History
                  </button>
                </div>

                {/* ===== RESULTS ===== */}
                {protocolPerformance && (
                  <div style={{ marginTop: '2rem' }} data-testid="protocol-performance-results">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1.25rem' }}>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.25rem' }}>Protocol P&amp;L</div>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: protocolPerformance.overall.protocol_pnl_usd >= 0 ? '#059669' : '#dc2626' }} data-testid="protocol-pnl">
                          {protocolPerformance.overall.protocol_pnl_usd >= 0 ? '+' : ''}${protocolPerformance.overall.protocol_pnl_usd.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{protocolPerformance.overall.protocol_r >= 0 ? '+' : ''}{protocolPerformance.overall.protocol_r}R</div>
                      </div>
                      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1.25rem' }}>
                        <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.25rem' }}>Protocol Win Rate</div>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#0369a1' }} data-testid="protocol-win-rate">{protocolPerformance.overall.protocol_win_rate}%</div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{protocolPerformance.overall.trades} notifications</div>
                      </div>
                      <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: '10px', padding: '1.25rem' }}>
                        <div style={{ fontSize: '0.8rem', color: '#854d0e', marginBottom: '0.25rem' }}>🏃 Left to Run P&amp;L</div>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: protocolPerformance.overall.full_potential_pnl_usd >= 0 ? '#059669' : '#dc2626' }} data-testid="full-potential-pnl">
                          {protocolPerformance.overall.full_potential_pnl_usd >= 0 ? '+' : ''}${protocolPerformance.overall.full_potential_pnl_usd.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#a16207' }}>{protocolPerformance.overall.full_potential_r >= 0 ? '+' : ''}{protocolPerformance.overall.full_potential_r}R (full potential)</div>
                      </div>
                      <div style={{ background: '#fefce8', border: '1px solid #fde047', borderRadius: '10px', padding: '1.25rem' }}>
                        <div style={{ fontSize: '0.8rem', color: '#854d0e', marginBottom: '0.25rem' }}>🏃 Left to Run Win Rate</div>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#a16207' }} data-testid="full-potential-win-rate">{protocolPerformance.overall.full_potential_win_rate}%</div>
                      </div>
                    </div>

                    <h4 style={{ fontSize: '1.1rem', marginBottom: '0.75rem', color: '#0c4a6e' }}>Equity Curve — Protocol vs Left to Run</h4>
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '0.75rem', marginBottom: '1.5rem' }} data-testid="equity-curve-container">
                      {renderEquityCurve(protocolPerformance.equity_curve)}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                      <h4 style={{ fontSize: '1.1rem', margin: 0, color: '#0c4a6e' }}>Per-Scanner Breakdown</h4>
                      <button
                        onClick={downloadProtocolCSV}
                        data-testid="download-csv-button"
                        style={{ padding: '0.55rem 1.2rem', background: '#059669', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                      >
                        ⬇ Download CSV
                      </button>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }} data-testid="protocol-scanner-table">
                        <thead>
                          <tr style={{ background: '#f1f5f9', textAlign: 'left' }}>
                            <th style={{ padding: '0.6rem' }}>Scanner</th>
                            <th style={{ padding: '0.6rem' }}>Trades</th>
                            <th style={{ padding: '0.6rem' }}>Protocol Win %</th>
                            <th style={{ padding: '0.6rem' }}>Protocol P&amp;L</th>
                            <th style={{ padding: '0.6rem' }}>Run Win %</th>
                            <th style={{ padding: '0.6rem' }}>Run P&amp;L</th>
                          </tr>
                        </thead>
                        <tbody>
                          {protocolPerformance.by_scanner.map((s) => (
                            <tr key={s.scanner} style={{ borderBottom: '1px solid #e2e8f0' }} data-testid={`scanner-row-${s.scanner}`}>
                              <td style={{ padding: '0.6rem', fontWeight: '600' }}>{s.scanner}</td>
                              <td style={{ padding: '0.6rem' }}>{s.trades}</td>
                              <td style={{ padding: '0.6rem' }}>{s.protocol_win_rate}%</td>
                              <td style={{ padding: '0.6rem', color: s.protocol_pnl_usd >= 0 ? '#059669' : '#dc2626', fontWeight: '600' }}>{s.protocol_pnl_usd >= 0 ? '+' : ''}${s.protocol_pnl_usd.toLocaleString()}</td>
                              <td style={{ padding: '0.6rem' }}>{s.full_potential_win_rate}%</td>
                              <td style={{ padding: '0.6rem', color: s.full_potential_pnl_usd >= 0 ? '#059669' : '#dc2626', fontWeight: '600' }}>{s.full_potential_pnl_usd >= 0 ? '+' : ''}${s.full_potential_pnl_usd.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* 🧪 EXIT-STRATEGY SIMULATOR — read-only re-score with a tighter SL + fixed R:R TP */}
                <div style={{ marginTop: '2rem', border: '1px solid #bae6fd', background: '#f0f9ff', borderRadius: '12px', padding: '1.5rem' }} data-testid="exit-strategy-simulator">
                  <h3 style={{ fontSize: '1.2rem', margin: '0 0 0.35rem', color: '#0c4a6e' }}>🧪 Exit-Strategy Simulator</h3>
                  <p style={{ fontSize: '0.85rem', color: '#475569', margin: '0 0 1.25rem', maxWidth: '640px' }}>
                    Re-scores the SAME trades as if each used a <strong>tighter stop</strong> (a % of the protocol's stop)
                    with a <strong>fixed R:R take-profit</strong> measured from that tighter stop. Walks captured M5 intrabar
                    data to resolve whether the TP or the tight SL was hit first. Read-only — does not touch any scanner.
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem', alignItems: 'flex-end', marginBottom: '1.25rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '0.3rem' }}>SL tightness (% of protocol SL)</label>
                      <input
                        type="number" min="1" max="100" step="1" value={slTightnessPct}
                        onChange={(e) => setSlTightnessPct(Number(e.target.value))}
                        data-testid="sim-sl-tightness-input"
                        style={{ width: '160px', padding: '0.5rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.95rem' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8rem', color: '#64748b', marginBottom: '0.3rem' }}>Take-profit (R multiple of tight SL)</label>
                      <input
                        type="number" min="0.5" max="10" step="0.5" value={tpR}
                        onChange={(e) => setTpR(Number(e.target.value))}
                        data-testid="sim-tp-r-input"
                        style={{ width: '160px', padding: '0.5rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '0.95rem' }}
                      />
                    </div>
                    <button
                      onClick={runExitSimulation}
                      disabled={loadingExitSim}
                      data-testid="run-exit-sim-button"
                      style={{ padding: '0.65rem 1.5rem', background: '#0284c7', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.95rem' }}
                    >
                      {loadingExitSim ? 'Simulating...' : 'Run Simulation'}
                    </button>
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>e.g. 16% + 3R = your tight stop with a 1:3 target</div>
                  </div>

                  {exitSim && (
                    <div data-testid="exit-sim-results">
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
                        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1.1rem' }}>
                          <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.25rem' }}>Simulated P&amp;L</div>
                          <div style={{ fontSize: '1.6rem', fontWeight: '700', color: exitSim.overall.pnl_usd >= 0 ? '#059669' : '#dc2626' }} data-testid="sim-pnl">
                            {exitSim.overall.pnl_usd >= 0 ? '+' : ''}${exitSim.overall.pnl_usd.toLocaleString()}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{exitSim.overall.r >= 0 ? '+' : ''}{exitSim.overall.r}R</div>
                        </div>
                        <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '1.1rem' }}>
                          <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.25rem' }}>Simulated Win Rate</div>
                          <div style={{ fontSize: '1.6rem', fontWeight: '700', color: '#0369a1' }} data-testid="sim-win-rate">{exitSim.overall.win_rate}%</div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{exitSim.overall.trades} trades · {slTightnessPct}% SL · {tpR}R TP</div>
                        </div>
                      </div>
                      {(exitSim.overall.approx_trades > 0 || exitSim.overall.still_open > 0) && (
                        <div style={{ fontSize: '0.8rem', color: '#a16207', background: '#fefce8', border: '1px solid #fde047', borderRadius: '8px', padding: '0.6rem 0.9rem', marginBottom: '1rem' }}>
                          ⚠️ {exitSim.overall.approx_trades} trade(s) approximated (no M5 path — pre-upgrade data){exitSim.overall.still_open > 0 ? `; ${exitSim.overall.still_open} never hit TP or SL (marked-to-market)` : ''}. Fresh trades will be fully path-accurate.
                        </div>
                      )}
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }} data-testid="sim-scanner-table">
                          <thead>
                            <tr style={{ background: '#e0f2fe', textAlign: 'left' }}>
                              <th style={{ padding: '0.6rem' }}>Scanner</th>
                              <th style={{ padding: '0.6rem' }}>Trades</th>
                              <th style={{ padding: '0.6rem' }}>Sim Win %</th>
                              <th style={{ padding: '0.6rem' }}>Sim P&amp;L</th>
                              <th style={{ padding: '0.6rem' }}>Sim R</th>
                            </tr>
                          </thead>
                          <tbody>
                            {exitSim.by_scanner.map((s) => (
                              <tr key={s.scanner} style={{ borderBottom: '1px solid #e2e8f0' }} data-testid={`sim-scanner-row-${s.scanner}`}>
                                <td style={{ padding: '0.6rem', fontWeight: '600' }}>{s.scanner}</td>
                                <td style={{ padding: '0.6rem' }}>{s.trades}</td>
                                <td style={{ padding: '0.6rem' }}>{s.win_rate}%</td>
                                <td style={{ padding: '0.6rem', color: s.pnl_usd >= 0 ? '#059669' : '#dc2626', fontWeight: '600' }}>{s.pnl_usd >= 0 ? '+' : ''}${s.pnl_usd.toLocaleString()}</td>
                                <td style={{ padding: '0.6rem' }}>{s.r >= 0 ? '+' : ''}{s.r}R</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}


          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <div>
              <h2 style={{ fontSize: '1.75rem', marginBottom: '2rem', color: '#0c4a6e' }}>
                Manage Subscription Prices
              </h2>

              {/* Tier 1 Pricing */}
              <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#f0f9ff', borderRadius: '12px' }}>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: '#0369a1' }}>
                  Tier 1 (Demo Account) - First 60 Days
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <PriceInput label="7 Days" value={tier1Prices.d7} onChange={(v) => setTier1Prices({...tier1Prices, d7: v})} />
                  <PriceInput label="14 Days" value={tier1Prices.d14} onChange={(v) => setTier1Prices({...tier1Prices, d14: v})} />
                  <PriceInput label="30 Days" value={tier1Prices.d30} onChange={(v) => setTier1Prices({...tier1Prices, d30: v})} />
                </div>
              </div>

              {/* Tier 2 Pricing */}
              <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#fef3c7', borderRadius: '12px' }}>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '1rem', color: '#92400e' }}>
                  Tier 2 (Live Trading) - After 60 Days
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <PriceInput label="7 Days" value={tier2Prices.d7} onChange={(v) => setTier2Prices({...tier2Prices, d7: v})} />
                  <PriceInput label="14 Days" value={tier2Prices.d14} onChange={(v) => setTier2Prices({...tier2Prices, d14: v})} />
                  <PriceInput label="30 Days" value={tier2Prices.d30} onChange={(v) => setTier2Prices({...tier2Prices, d30: v})} />
                </div>
              </div>

              {/* Black Friday Special */}
              <div style={{ marginBottom: '2rem', padding: '1.5rem', background: '#fce7f3', borderRadius: '12px', border: '2px solid #ec4899' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ fontSize: '1.25rem', color: '#be185d' }}>
                    🎉 Black Friday Special - 12 Months
                  </h3>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={blackFridayEnabled}
                      onChange={(e) => setBlackFridayEnabled(e.target.checked)}
                      style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: '600', color: '#be185d' }}>Enable Offer</span>
                  </label>
                </div>
                <PriceInput label="12 Month Price" value={blackFridayPrice} onChange={setBlackFridayPrice} />
                <p style={{ fontSize: '0.875rem', color: '#9f1239', marginTop: '0.75rem', fontWeight: '600' }}>
                  ⚠️ Discount codes CANNOT be used with this offer
                </p>
              </div>

              {/* Save Button */}
              <button
                onClick={savePricing}
                disabled={loading}
                style={{
                  padding: '1rem 2rem',
                  background: loading ? '#94a3b8' : 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '1rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  width: '100%'
                }}
              >
                {loading ? 'Saving...' : '💾 Save All Pricing Changes'}
              </button>
            </div>
          )}

          {/* Stripe Setup Tab */}
          {activeTab === 'stripe' && (
            <div>
              <h2 style={{ fontSize: '1.75rem', marginBottom: '1.5rem', color: '#0c4a6e' }}>
                Stripe Configuration
              </h2>
              <div style={{ padding: '1.5rem', background: '#f8fafc', borderRadius: '12px' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#334155' }}>
                  Stripe API Key
                </label>
                <input
                  type="password"
                  value={stripeKey}
                  onChange={(e) => setStripeKey(e.target.value)}
                  placeholder="sk_live_..."
                  style={{
                    width: '100%',
                    padding: '0.875rem',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    marginBottom: '1rem'
                  }}
                />
                <button
                  style={{
                    padding: '0.875rem 2rem',
                    background: '#5469d4',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  💳 Save Stripe Key
                </button>
                <p style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '1rem' }}>
                  Get your API key from: <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" style={{ color: '#5469d4' }}>Stripe Dashboard</a>
                </p>
              </div>
            </div>
          )}

          {/* Invite Requests Tab */}
          {activeTab === 'invites' && (
            <div>
              <h2 style={{ fontSize: '1.75rem', marginBottom: '1.5rem', color: '#0c4a6e' }}>
                Pending Invite Requests ({inviteRequests.length})
              </h2>
              {inviteRequests.length === 0 ? (
                <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                  <div>No pending invite requests</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {inviteRequests.map((request) => (
                    <div key={request.request_id} style={{
                      padding: '1.5rem',
                      background: '#f8fafc',
                      borderRadius: '12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '1rem'
                    }}>
                      <div>
                        <div style={{ fontSize: '1.125rem', fontWeight: '600', color: '#0c4a6e' }}>
                          {request.full_name}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                          {request.email}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.25rem' }}>
                          📍 Post Code: {request.post_code}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                          Requested: {new Date(request.requested_at).toLocaleString()}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button
                          onClick={() => approveInviteRequest(request.request_id)}
                          style={{
                            padding: '0.75rem 1.5rem',
                            background: '#16a34a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          ✅ Approve (7-day trial)
                        </button>
                        <button
                          onClick={() => rejectInviteRequest(request.request_id)}
                          style={{
                            padding: '0.75rem 1.5rem',
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          ❌ Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div>
              <h2 style={{ fontSize: '1.75rem', marginBottom: '1.5rem', color: '#0c4a6e' }}>
                Platform Analytics
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
                <StatCard label="Total Users" value={analytics?.total_users || 0} icon="👥" color="#0891b2" />
                <StatCard label="Active Subscriptions" value={analytics?.active_subscriptions || 0} icon="✅" color="#16a34a" />
                <StatCard label="Total Revenue" value={`$${analytics?.total_revenue || 0}`} icon="💰" color="#f59e0b" />
                <StatCard label="Invite Requests" value={inviteRequests.length} icon="📨" color="#8b5cf6" />
                <StatCard label="Trial Users" value={analytics?.trial_users || 0} icon="🆓" color="#64748b" />
                <StatCard label="Conversion Rate" value={`${analytics?.conversion_rate || 0}%`} icon="📈" color="#ec4899" />
              </div>
            </div>
          )}

          {/* Performance Tab - Enhanced with Scenario Analytics */}
          {activeTab === 'performance' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.75rem', color: '#0c4a6e' }}>
                  📈 Trade Performance & Scenario Analytics
                </h2>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    onClick={() => window.open(`${backendUrl}/api/tracked-trades/export?format=csv`, '_blank')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    📥 Export CSV
                  </button>
                  <button
                    onClick={() => window.open(`${backendUrl}/api/tracked-trades/export?format=json`, '_blank')}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    📥 Export JSON
                  </button>
                </div>
              </div>

              {/* Automatic Probability Analyzer */}
              <div style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                borderRadius: '12px',
                marginBottom: '2rem',
                color: 'white',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', fontWeight: '700' }}>
                  🎯 Automatic Probability Analyzer
                </h3>
                <p style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '1rem' }}>
                  Analyzes your completed trades to determine optimal probability threshold and cut losses by 50-60%
                </p>
                
                <button
                  onClick={fetchProbabilityAnalysis}
                  disabled={loadingProbabilityAnalysis}
                  style={{
                    padding: '0.875rem 1.5rem',
                    background: loadingProbabilityAnalysis ? '#94a3b8' : 'white',
                    color: loadingProbabilityAnalysis ? 'white' : '#d97706',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '700',
                    cursor: loadingProbabilityAnalysis ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {loadingProbabilityAnalysis ? '⏳ Analyzing...' : '📊 Analyze Probability Performance'}
                </button>

                {/* Error State */}
                {probabilityAnalysisError && (
                  <div style={{ 
                    marginTop: '1rem', 
                    padding: '1rem', 
                    background: 'rgba(239, 68, 68, 0.2)', 
                    borderRadius: '8px',
                    border: '1px solid rgba(239, 68, 68, 0.5)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>⚠️</span>
                      <span style={{ fontWeight: '600' }}>{probabilityAnalysisError}</span>
                    </div>
                    <p style={{ fontSize: '0.8rem', marginTop: '0.5rem', opacity: 0.9 }}>
                      This typically happens when there are no completed trades to analyze. 
                      Complete some trades first, then try again.
                    </p>
                  </div>
                )}

                {probabilityAnalysis && probabilityAnalysis.success && (
                  <div style={{ marginTop: '1.5rem', background: 'rgba(255, 255, 255, 0.15)', padding: '1.5rem', borderRadius: '8px' }}>
                    {/* Key Metrics Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>Total Trades Analyzed</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{probabilityAnalysis.total_trades_analyzed}</div>
                        <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                          {probabilityAnalysis.total_wins}W / {probabilityAnalysis.total_losses}L
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>Overall Win Rate</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{probabilityAnalysis.overall_win_rate}%</div>
                      </div>
                      <div style={{
                        background: 'rgba(16, 185, 129, 0.2)',
                        padding: '0.5rem',
                        borderRadius: '6px'
                      }}>
                        <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>Avg Win Probability</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{probabilityAnalysis.avg_win_probability}%</div>
                      </div>
                      <div style={{
                        background: 'rgba(239, 68, 68, 0.2)',
                        padding: '0.5rem',
                        borderRadius: '6px'
                      }}>
                        <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>Avg Loss Probability</div>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{probabilityAnalysis.avg_loss_probability}%</div>
                      </div>
                    </div>

                    {/* Probability Gap Analysis */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.2)',
                      padding: '1rem',
                      borderRadius: '8px',
                      marginBottom: '1.5rem',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>
                        Probability Gap (Win Avg - Loss Avg)
                      </div>
                      <div style={{ fontSize: '2rem', fontWeight: '700' }}>
                        {probabilityAnalysis.probability_gap > 0 ? '+' : ''}{probabilityAnalysis.probability_gap}%
                      </div>
                      <div style={{ fontSize: '0.75rem', opacity: 0.8, marginTop: '0.25rem' }}>
                        {probabilityAnalysis.probability_gap >= 2 
                          ? '✅ Significant gap - Threshold adjustment possible' 
                          : '⚠️ Small gap - Need more data or gap too narrow'}
                      </div>
                    </div>

                    {/* Threshold Recommendation */}
                    <div style={{
                      background: probabilityAnalysis.action_required 
                        ? 'rgba(239, 68, 68, 0.2)' 
                        : 'rgba(16, 185, 129, 0.2)',
                      padding: '1rem',
                      borderRadius: '8px',
                      marginBottom: '1.5rem',
                      border: `2px solid ${probabilityAnalysis.action_required ? 'rgba(239, 68, 68, 0.4)' : 'rgba(16, 185, 129, 0.4)'}`
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '1rem' }}>
                        <div>
                          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Current Threshold</div>
                          <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>{probabilityAnalysis.current_threshold}%</div>
                        </div>
                        <div style={{ fontSize: '2rem' }}>→</div>
                        <div>
                          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Recommended Threshold</div>
                          <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>
                            {probabilityAnalysis.recommended_threshold}%
                            {probabilityAnalysis.action_required && ' ⚠️'}
                          </div>
                        </div>
                        {probabilityAnalysis.action_required && (
                          <button
                            onClick={() => handleApplyThreshold(probabilityAnalysis.recommended_threshold)}
                            style={{
                              padding: '0.75rem 1.5rem',
                              background: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontWeight: '700',
                              fontSize: '0.875rem',
                              cursor: 'pointer',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              transition: 'all 0.2s'
                            }}
                          >
                            ✅ Apply {probabilityAnalysis.recommended_threshold}%
                          </button>
                        )}
                      </div>
                      
                      <div style={{ fontSize: '0.875rem', marginTop: '0.75rem' }}>
                        <strong>{probabilityAnalysis.action_required ? '⚠️ Recommendation:' : '✅ Status:'}</strong>
                        <div style={{ marginTop: '0.25rem', opacity: 0.95 }}>
                          {probabilityAnalysis.recommendation_reason}
                        </div>
                      </div>

                      {probabilityAnalysis.action_required && (
                        <div style={{ 
                          fontSize: '0.875rem', 
                          marginTop: '0.75rem',
                          padding: '1rem',
                          background: 'rgba(16, 185, 129, 0.2)',
                          borderRadius: '8px',
                          border: '2px solid rgba(16, 185, 129, 0.4)'
                        }}>
                          <div style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.5rem', color: '#065f46' }}>
                            💰 ${probabilityAnalysis.potential_profit_impact.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </div>
                          <div style={{ fontSize: '0.875rem', opacity: 0.95 }}>
                            <strong>Extra Profit You Would Have Made</strong> if threshold was set at {probabilityAnalysis.recommended_threshold}%
                          </div>
                          <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
                            <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                              Would filter: <strong>{probabilityAnalysis.potential_loss_reduction_count} losses</strong> ({probabilityAnalysis.potential_loss_reduction_percentage}% reduction)
                              {probabilityAnalysis.filtered_wins_count > 0 && (
                                <span> · Also filters {probabilityAnalysis.filtered_wins_count} wins (collateral)</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Sample Trades Display */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      {/* Top Winning Trades */}
                      <div>
                        <div style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.5rem', opacity: 0.9 }}>
                          ✅ Sample Winning Trades (Top 5)
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {probabilityAnalysis.top_winning_trades && probabilityAnalysis.top_winning_trades.slice(0, 5).map((trade, idx) => (
                            <div key={idx} style={{
                              background: 'rgba(16, 185, 129, 0.2)',
                              padding: '0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              display: 'flex',
                              justifyContent: 'space-between'
                            }}>
                              <span>{trade.pair}</span>
                              <span style={{ fontWeight: '700' }}>{trade.probability}%</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Top Losing Trades */}
                      <div>
                        <div style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.5rem', opacity: 0.9 }}>
                          ❌ Sample Losing Trades (Top 5)
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {probabilityAnalysis.top_losing_trades && probabilityAnalysis.top_losing_trades.slice(0, 5).map((trade, idx) => (
                            <div key={idx} style={{
                              background: 'rgba(239, 68, 68, 0.2)',
                              padding: '0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              display: 'flex',
                              justifyContent: 'space-between'
                            }}>
                              <span>{trade.pair}</span>
                              <span style={{ fontWeight: '700' }}>{trade.probability}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 🧠 ML Loss Learning Analysis */}
              <div style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                borderRadius: '12px',
                marginBottom: '2rem',
                color: 'white'
              }}>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', fontWeight: '700' }}>
                  🧠 ML Loss Learning Analysis
                </h3>
                <p style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '1rem' }}>
                  Intelligent analysis of WHY trades lose - helps filter doomed trades from good setups
                </p>
                
                <button
                  onClick={fetchMlLossAnalysis}
                  disabled={loadingMlLossAnalysis}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: loadingMlLossAnalysis ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.2)',
                    color: 'white',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '700',
                    cursor: loadingMlLossAnalysis ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    width: '100%'
                  }}
                >
                  {loadingMlLossAnalysis ? '⏳ Loading...' : '🔍 Analyze Loss Patterns'}
                </button>

                {mlLossAnalysis && (
                  <div style={{ marginTop: '1.5rem', background: 'rgba(255, 255, 255, 0.15)', padding: '1.5rem', borderRadius: '8px' }}>
                    
                    {mlLossAnalysis.total_analyses === 0 ? (
                      <div style={{ textAlign: 'center', padding: '1rem' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
                        <div style={{ fontWeight: '600' }}>No Loss Analyses Yet</div>
                        <div style={{ fontSize: '0.875rem', opacity: 0.8, marginTop: '0.5rem' }}>
                          Loss analyses are created automatically when trades close as losses. 
                          Check back after some trades complete.
                        </div>
                        <button
                          onClick={runLossAnalysisBackfill}
                          disabled={backfillLoading}
                          data-testid="backfill-loss-analysis-btn"
                          style={{
                            marginTop: '1rem',
                            padding: '0.75rem 1.5rem',
                            background: backfillLoading ? '#666' : 'linear-gradient(135deg, #f39c12, #e67e22)',
                            border: 'none',
                            borderRadius: '8px',
                            color: 'white',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            cursor: backfillLoading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {backfillLoading ? '⏳ Analyzing...' : '🔄 Backfill Existing Losses'}
                        </button>
                        <div style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: '0.5rem' }}>
                          Click to analyze all existing losing trades
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Key Metrics */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>Total Losses Analyzed</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{mlLossAnalysis.total_analyses}</div>
                          </div>
                          <div style={{ textAlign: 'center', background: 'rgba(255,0,0,0.2)', padding: '0.5rem', borderRadius: '6px' }}>
                            <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>Should Have Filtered</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                              {mlLossAnalysis.filter_effectiveness?.trades_that_should_have_been_filtered || 0}
                            </div>
                            <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                              ({mlLossAnalysis.filter_effectiveness?.percentage || 0}%)
                            </div>
                          </div>
                          <div style={{ textAlign: 'center', background: 'rgba(0,255,0,0.2)', padding: '0.5rem', borderRadius: '6px' }}>
                            <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>Potential Savings</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                              {mlLossAnalysis.filter_effectiveness?.potential_savings || '$0'}
                            </div>
                          </div>
                        </div>

                        {/* Loss Type Breakdown */}
                        <div style={{ marginBottom: '1.5rem' }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.75rem' }}>
                            📊 Loss Type Breakdown
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem' }}>
                            {mlLossAnalysis.loss_type_breakdown && Object.entries(mlLossAnalysis.loss_type_breakdown).map(([type, count]) => (
                              <div key={type} style={{
                                padding: '0.75rem',
                                background: type === 'DOOMED_TRADE' ? 'rgba(255,0,0,0.3)' : 
                                           type === 'MARKET_EVENT' ? 'rgba(255,165,0,0.3)' :
                                           type === 'BAD_TIMING' ? 'rgba(255,255,0,0.3)' :
                                           'rgba(100,149,237,0.3)',
                                borderRadius: '6px',
                                textAlign: 'center'
                              }}>
                                <div style={{ fontSize: '1.25rem', fontWeight: '700' }}>{count}</div>
                                <div style={{ fontSize: '0.65rem', opacity: 0.9 }}>
                                  {type.replace(/_/g, ' ')}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* High Risk Pairs */}
                        {mlLossAnalysis.high_risk_pairs && mlLossAnalysis.high_risk_pairs.length > 0 && (
                          <div style={{ marginBottom: '1.5rem' }}>
                            <div style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.75rem' }}>
                              ⚠️ High Risk Pairs (Elevated DOOMED Trade Rate)
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                              {mlLossAnalysis.high_risk_pairs.slice(0, 5).map((pair, idx) => (
                                <div key={idx} style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '0.5rem 0.75rem',
                                  background: 'rgba(255,0,0,0.2)',
                                  borderRadius: '4px',
                                  fontSize: '0.8rem'
                                }}>
                                  <span style={{ fontWeight: '600' }}>{pair.pair}</span>
                                  <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                    <span>🎯 {pair.doomed_rate}% doomed</span>
                                    <span style={{ 
                                      background: 'rgba(255,0,0,0.4)', 
                                      padding: '0.25rem 0.5rem', 
                                      borderRadius: '4px',
                                      fontWeight: '700'
                                    }}>
                                      {pair.probability_adjustment}% adj
                                    </span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Recent Lessons */}
                        {mlLossAnalysis.recent_lessons && mlLossAnalysis.recent_lessons.length > 0 && (
                          <div>
                            <div style={{ fontSize: '0.875rem', fontWeight: '700', marginBottom: '0.75rem' }}>
                              📚 Recent Lessons Learned
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '200px', overflowY: 'auto' }}>
                              {mlLossAnalysis.recent_lessons.slice(0, 5).map((lesson, idx) => (
                                <div key={idx} style={{
                                  padding: '0.75rem',
                                  background: 'rgba(255,255,255,0.1)',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem'
                                }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                    <span style={{ fontWeight: '600' }}>{lesson.pair}</span>
                                    <span style={{ 
                                      background: lesson.loss_type === 'DOOMED_TRADE' ? 'rgba(255,0,0,0.4)' : 'rgba(255,165,0,0.4)',
                                      padding: '0.125rem 0.5rem',
                                      borderRadius: '4px',
                                      fontSize: '0.65rem'
                                    }}>
                                      {lesson.loss_type?.replace(/_/g, ' ')}
                                    </span>
                                  </div>
                                  <div style={{ opacity: 0.9 }}>{lesson.lesson}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Recommendations */}
                        {mlLossAnalysis.recommendations && (
                          <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(0,100,255,0.2)', borderRadius: '6px' }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: '700', marginBottom: '0.5rem' }}>💡 ML Recommendations</div>
                            {mlLossAnalysis.recommendations.map((rec, idx) => (
                              <div key={idx} style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.25rem' }}>
                                • {rec}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* 🔬 Deep Pattern Analysis - Why Do Trades Fail? */}
              <div style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                borderRadius: '12px',
                marginBottom: '2rem',
                color: 'white'
              }}>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  🔬 Deep Pattern Analysis
                </h3>
                <p style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '1rem' }}>
                  Analyzes WHY trades fail - identifies patterns by session, RSI, trend, direction, and more
                </p>
                
                <button
                  onClick={fetchDeepPatterns}
                  disabled={loadingDeepPatterns}
                  data-testid="fetch-deep-patterns-btn"
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: loadingDeepPatterns ? '#666' : 'rgba(255,255,255,0.2)',
                    border: '2px solid white',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: loadingDeepPatterns ? 'not-allowed' : 'pointer',
                    marginBottom: '1rem'
                  }}
                >
                  {loadingDeepPatterns ? '⏳ Analyzing...' : '🔍 Run Deep Analysis'}
                </button>

                {deepPatterns && (
                  <div style={{ marginTop: '1rem', background: 'rgba(0,0,0,0.2)', padding: '1rem', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.875rem', marginBottom: '1rem', opacity: 0.9 }}>
                      Analyzed {deepPatterns.total_analyzed} losing trades
                    </div>

                    {/* Insights - Most Important */}
                    {deepPatterns.insights && deepPatterns.insights.length > 0 && (
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h4 style={{ fontSize: '1rem', marginBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: '0.5rem' }}>
                          ⚠️ Key Insights ({deepPatterns.insights.length})
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {deepPatterns.insights.map((insight, idx) => (
                            <div key={idx} style={{
                              padding: '0.75rem',
                              background: insight.severity === 'HIGH' ? 'rgba(255,0,0,0.3)' : 'rgba(255,165,0,0.3)',
                              borderRadius: '6px',
                              borderLeft: `4px solid ${insight.severity === 'HIGH' ? '#ff4444' : '#ffa500'}`
                            }}>
                              <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                                {insight.severity === 'HIGH' ? '🔴' : '🟡'} {insight.message}
                              </div>
                              <div style={{ fontSize: '0.75rem', opacity: 0.9, marginTop: '0.25rem' }}>
                                💡 {insight.recommendation}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Pair Deep Analysis */}
                    {deepPatterns.pair_deep_analysis && deepPatterns.pair_deep_analysis.length > 0 && (
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h4 style={{ fontSize: '1rem', marginBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: '0.5rem' }}>
                          📊 Pair Analysis (Sorted by Doomed Rate)
                        </h4>
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                          {deepPatterns.pair_deep_analysis.slice(0, 10).map((pair, idx) => (
                            <div key={idx} style={{
                              padding: '0.75rem',
                              background: pair.doomed_rate > 60 ? 'rgba(255,0,0,0.2)' : pair.doomed_rate > 40 ? 'rgba(255,165,0,0.2)' : 'rgba(0,255,0,0.1)',
                              borderRadius: '6px',
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr 1fr',
                              gap: '0.5rem',
                              fontSize: '0.8rem'
                            }}>
                              <div>
                                <div style={{ fontWeight: '700' }}>{pair.pair}</div>
                                <div style={{ opacity: 0.8 }}>{pair.doomed_rate}% doomed ({pair.doomed_count}/{pair.total_losses})</div>
                              </div>
                              <div>
                                <div>BUY: {pair.buy_stats.doomed_rate}% doomed</div>
                                <div>SELL: {pair.sell_stats.doomed_rate}% doomed</div>
                              </div>
                              <div>
                                {pair.direction_bias && (
                                  <div style={{ color: '#ffcc00', fontWeight: '600' }}>⚠️ {pair.direction_bias}</div>
                                )}
                                {pair.worst_session && (
                                  <div style={{ opacity: 0.8 }}>Worst: {pair.worst_session}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Session Analysis */}
                    {deepPatterns.session_analysis && (
                      <div style={{ marginBottom: '1.5rem' }}>
                        <h4 style={{ fontSize: '1rem', marginBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: '0.5rem' }}>
                          🕐 Session Analysis
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
                          {Object.entries(deepPatterns.session_analysis).map(([session, data]) => (
                            <div key={session} style={{
                              padding: '0.5rem',
                              background: data.doomed_rate > 50 ? 'rgba(255,0,0,0.3)' : 'rgba(255,255,255,0.1)',
                              borderRadius: '6px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontWeight: '600', textTransform: 'capitalize' }}>{session.replace('_', ' ')}</div>
                              <div style={{ fontSize: '0.75rem' }}>{data.losses} losses</div>
                              <div style={{ fontSize: '0.875rem', fontWeight: '700', color: data.doomed_rate > 50 ? '#ff6b6b' : '#4ecdc4' }}>
                                {data.doomed_rate}% doomed
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Smart Filters Generated */}
                    {deepPatterns.smart_filters && deepPatterns.smart_filters.length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <h4 style={{ fontSize: '1rem', marginBottom: '0.75rem', borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: '0.5rem' }}>
                          🎯 Suggested Smart Filters ({deepPatterns.smart_filters.length})
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {deepPatterns.smart_filters.map((filter, idx) => (
                            <div key={idx} style={{
                              padding: '0.5rem 0.75rem',
                              background: 'rgba(255,255,255,0.1)',
                              borderRadius: '6px',
                              fontSize: '0.8rem'
                            }}>
                              <span style={{ fontWeight: '600' }}>{filter.filter_type}:</span> {filter.condition} → <span style={{ color: filter.action === 'BLOCK' ? '#ff4444' : '#ffa500' }}>{filter.action}</span>
                              <div style={{ opacity: 0.8, fontSize: '0.75rem' }}>{filter.reason}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Top Recommendations */}
                    {deepPatterns.top_recommendations && deepPatterns.top_recommendations.length > 0 && (
                      <div>
                        <h4 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>🎯 Top Recommendations</h4>
                        <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem' }}>
                          {deepPatterns.top_recommendations.map((rec, idx) => (
                            <li key={idx} style={{ marginBottom: '0.25rem' }}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Calculate All Pairs - Data Collection Feature */}
              <div style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px',
                marginBottom: '2rem',
                color: 'white'
              }}>
                <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem', fontWeight: '700' }}>
                  🎯 Calculate All Pairs - Data Collection
                </h3>
                <p style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.25rem' }}>
                  Calculate trades for all 24 pairs at once for comprehensive market analysis
                </p>
                <p style={{ fontSize: '0.75rem', opacity: 0.8, marginBottom: '1rem', fontStyle: 'italic' }}>
                  ⏱️ Estimated time: ~12 minutes (24 pairs, 30 seconds between each for API safety)
                </p>
                
                {!tradingStatus.calculator_enabled && (
                  <div style={{ 
                    padding: '0.75rem 1rem', 
                    background: tradingStatus.block_due_to_news ? 'rgba(239, 68, 68, 0.2)' : 'rgba(251, 146, 60, 0.2)', 
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    border: tradingStatus.block_due_to_news ? '2px solid rgba(239, 68, 68, 0.5)' : '2px solid rgba(251, 146, 60, 0.5)'
                  }}>
                    <strong style={{ color: tradingStatus.block_due_to_news ? '#991b1b' : '#9a3412' }}>
                      {tradingStatus.block_due_to_news ? '🚫 Calculator Disabled - High Risk News' : '🔒 Calculator Disabled - Rollover Period'}
                    </strong>
                    <p style={{ fontSize: '0.875rem', margin: '0.25rem 0 0 0', color: tradingStatus.block_due_to_news ? '#991b1b' : '#9a3412' }}>
                      {tradingStatus.block_due_to_news 
                        ? `${tradingStatus.news_recommendation}. Trading is disabled during high-impact news events to protect your capital.`
                        : 'Calculator is disabled during the daily rollover period (21:00-23:05 UTC) for system maintenance and data integrity.'
                      }
                    </p>
                    {tradingStatus.news_note && tradingStatus.block_due_to_news && (
                      <p style={{ fontSize: '0.75rem', margin: '0.5rem 0 0 0', opacity: 0.8, fontStyle: 'italic', color: tradingStatus.block_due_to_news ? '#991b1b' : '#9a3412' }}>
                        {tradingStatus.news_note}
                      </p>
                    )}
                  </div>
                )}
                
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
                    <label style={{ fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                      Risk Amount ($):
                    </label>
                    <input
                      type="number"
                      value={bulkRiskAmount}
                      onChange={(e) => setBulkRiskAmount(e.target.value)}
                      placeholder="100"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        fontSize: '1rem',
                        background: 'rgba(255,255,255,0.9)',
                        color: '#1e293b'
                      }}
                    />
                  </div>
                  
                  <div style={{ flex: '1 1 180px', minWidth: '160px' }}>
                    <label style={{ fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                      Timeframe:
                    </label>
                    <select
                      value={bulkTimeframe}
                      onChange={(e) => setBulkTimeframe(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        borderRadius: '8px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        fontSize: '1rem',
                        background: 'rgba(255,255,255,0.9)',
                        color: '#1e293b'
                      }}
                    >
                      <option value="1D">Daily</option>
                      <option value="4H">4 Hour</option>
                      <option value="1H">1 Hour</option>
                    </select>
                  </div>
                  
                  <button
                    onClick={handleCalculateAllPairs}
                    disabled={true}
                    style={{
                      padding: '0.75rem 2rem',
                      background: '#94a3b8',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '700',
                      fontSize: '1rem',
                      cursor: 'not-allowed',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
                      transition: 'all 0.2s',
                      opacity: 0.6
                    }}
                  >
                    🔒 Temporarily Disabled
                  </button>
                  
                  {/* Close All Trades Button - REMOVED DUE TO P&L CALCULATION ISSUES */}
                  {/* Button removed on Dec 8, 2025 - was closing trades at incorrect prices */}

                  {/* Manual Rollover Closure Button - GREEN for ML Training */}
                  <button
                    onClick={handleManualRolloverClosure}
                    disabled={closingRollover}
                    style={{
                      padding: '0.75rem 2rem',
                      background: closingRollover ? '#94a3b8' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      border: '2px solid #059669',
                      borderRadius: '8px',
                      fontWeight: '700',
                      fontSize: '1rem',
                      cursor: closingRollover ? 'not-allowed' : 'pointer',
                      boxShadow: closingRollover ? '0 4px 6px rgba(0,0,0,0.2)' : '0 4px 12px rgba(16, 185, 129, 0.4)',
                      transition: 'all 0.2s',
                      marginLeft: '1rem',
                      textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }}
                    onMouseEnter={(e) => {
                      if (!closingRollover) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = closingRollover ? '0 4px 6px rgba(0,0,0,0.2)' : '0 4px 12px rgba(16, 185, 129, 0.4)';
                    }}
                  >
                    {closingRollover ? '⏳ Closing for Rollover...' : '🟢 Rollover Closure (ML)'}
                  </button>

                  {/* Reset Carousel Button - Archive completed trades */}
                  <button
                    onClick={handleResetCarousel}
                    style={{
                      padding: '0.75rem 2rem',
                      background: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '700',
                      fontSize: '1rem',
                      cursor: 'pointer',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
                      transition: 'all 0.2s',
                      marginLeft: '1rem'
                    }}
                  >
                    🔄 Reset Carousel to $0
                  </button>

                  {/* Delete ALL Trades Button - Permanent deletion */}
                  <button
                    onClick={handleDeleteAllTrades}
                    style={{
                      padding: '0.75rem 2rem',
                      background: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '700',
                      fontSize: '1rem',
                      cursor: 'pointer',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
                      transition: 'all 0.2s',
                      marginLeft: '1rem'
                    }}
                  >
                    🗑️ Delete ALL Trades
                  </button>
                </div>
                
                {/* Progress Indicator */}
                {loadingBulkCalc && (
                  <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(255,255,255,0.2)', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <div>
                        <span style={{ fontWeight: '600', display: 'block' }}>Processing pairs...</span>
                        <span style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                          ⏱️ Est. time remaining: ~{Math.ceil((bulkProgress.total - bulkProgress.current) * 10 / 60)} min {((bulkProgress.total - bulkProgress.current) * 10) % 60} sec
                        </span>
                      </div>
                      <span style={{ fontWeight: '700', fontSize: '1.1rem' }}>
                        {bulkProgress.current}/{bulkProgress.total}
                      </span>
                    </div>
                    <div style={{ width: '100%', height: '8px', background: 'rgba(255,255,255,0.3)', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{
                        width: `${(bulkProgress.current / bulkProgress.total) * 100}%`,
                        height: '100%',
                        background: '#10b981',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                )}
                
                {/* Results Display */}
                {bulkResults && (
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <div>
                        <span style={{ fontWeight: '700', fontSize: '1.1rem' }}>
                          ✅ {bulkResults.successful} Successful | ❌ {bulkResults.failed} Failed | 📊 Total: {bulkResults.total_pairs}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => exportBulkResults('csv')}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          📥 Export CSV
                        </button>
                        <button
                          onClick={() => exportBulkResults('json')}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontSize: '0.875rem'
                          }}
                        >
                          📥 Export JSON
                        </button>
                      </div>
                    </div>
                    
                    {/* MARKET ORDER ALERTS - Sync with Broker/MT5 */}
                    {(() => {
                      const marketOrders = bulkResults.results.filter(r => 
                        r.status === 'success' && 
                        r.is_instantly_active === true
                      );
                      
                      if (marketOrders.length > 0) {
                        return (
                          <div style={{ 
                            marginBottom: '1.5rem',
                            padding: '1.5rem',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            borderRadius: '12px',
                            border: '3px solid #047857',
                            boxShadow: '0 8px 16px rgba(16, 185, 129, 0.3)',
                            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                          }}>
                            <div style={{ 
                              fontSize: '1.5rem', 
                              fontWeight: '800', 
                              color: 'white', 
                              marginBottom: '1rem',
                              textAlign: 'center',
                              textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}>
                              🚨 SYNC WITH BROKER/MT5 🚨
                            </div>
                            <div style={{
                              background: 'rgba(255, 255, 255, 0.95)',
                              borderRadius: '8px',
                              padding: '1rem'
                            }}>
                              {marketOrders.map((order, idx) => (
                                <div 
                                  key={idx}
                                  style={{
                                    padding: '1rem',
                                    marginBottom: idx < marketOrders.length - 1 ? '0.75rem' : '0',
                                    background: order.direction === 'BUY' ? '#dcfce7' : '#fee2e2',
                                    borderRadius: '8px',
                                    border: `2px solid ${order.direction === 'BUY' ? '#15803d' : '#dc2626'}`
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                      <div style={{ 
                                        fontSize: '1.25rem', 
                                        fontWeight: '800', 
                                        color: order.direction === 'BUY' ? '#15803d' : '#dc2626',
                                        marginBottom: '0.25rem'
                                      }}>
                                        {order.direction} MARKET ORDER
                                      </div>
                                      <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#0c4a6e' }}>
                                        {order.pair}
                                      </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                      <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>
                                        Entry: <span style={{ fontWeight: '700', color: '#0c4a6e' }}>{order.entry_price?.toFixed(5)}</span>
                                      </div>
                                      <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>
                                        SL: <span style={{ fontWeight: '700', color: '#ef4444' }}>{order.stop_loss?.toFixed(5)}</span>
                                      </div>
                                      <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                                        TP: <span style={{ fontWeight: '700', color: '#10b981' }}>{order.take_profit?.toFixed(5)}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div style={{ 
                                    marginTop: '0.75rem', 
                                    paddingTop: '0.75rem', 
                                    borderTop: '1px solid rgba(0,0,0,0.1)',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    fontSize: '0.875rem'
                                  }}>
                                    <div>
                                      <span style={{ color: '#64748b' }}>Lot Size: </span>
                                      <span style={{ fontWeight: '700', color: '#7c3aed' }}>{order.lot_size}</span>
                                    </div>
                                    <div>
                                      <span style={{ color: '#64748b' }}>Probability: </span>
                                      <span style={{ fontWeight: '700', color: '#10b981' }}>{order.probability}%</span>
                                    </div>
                                    <div>
                                      <span style={{ color: '#64748b' }}>Potential: </span>
                                      <span style={{ fontWeight: '700', color: '#10b981' }}>${order.potential_profit?.toFixed(2)}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div style={{
                              marginTop: '1rem',
                              padding: '0.75rem',
                              background: 'rgba(255, 255, 255, 0.2)',
                              borderRadius: '8px',
                              color: 'white',
                              fontSize: '0.875rem',
                              textAlign: 'center',
                              fontWeight: '600'
                            }}>
                              ⚡ These trades are instantly active - Execute immediately on MT5
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                    
                    {/* Results Table */}
                    <div style={{ maxHeight: '400px', overflowY: 'auto', background: 'white', borderRadius: '8px', padding: '1rem' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e2e8f0', color: '#0c4a6e' }}>
                            <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '700' }}>Pair</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '700' }}>Status</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '700' }}>Direction</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '700' }}>Entry</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '700' }}>SL</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '700' }}>TP</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '700' }}>R:R</th>
                            <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '700' }}>Prob %</th>
                            <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '700' }}>Pot. Profit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkResults.results.map((result, index) => (
                            <tr key={index} style={{ borderBottom: '1px solid #e2e8f0' }}>
                              <td style={{ padding: '0.75rem', fontWeight: '600', color: '#0c4a6e' }}>{result.pair}</td>
                              <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                {result.status === 'success' ? (
                                  <span style={{ color: '#10b981', fontWeight: '700' }}>✅</span>
                                ) : (
                                  <span style={{ color: '#ef4444', fontWeight: '700' }}>❌</span>
                                )}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                {result.direction ? (
                                  <span style={{
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '6px',
                                    fontWeight: '600',
                                    background: result.direction === 'BUY' ? '#dcfce7' : '#fee2e2',
                                    color: result.direction === 'BUY' ? '#15803d' : '#dc2626'
                                  }}>
                                    {result.direction}
                                  </span>
                                ) : '-'}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', color: '#475569' }}>
                                {result.entry_price ? result.entry_price.toFixed(5) : '-'}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', color: '#ef4444' }}>
                                {result.stop_loss ? result.stop_loss.toFixed(5) : '-'}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', color: '#10b981' }}>
                                {result.take_profit ? result.take_profit.toFixed(5) : '-'}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: '#7c3aed' }}>
                                {result.rr_ratio || '-'}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600', color: result.probability >= 70 ? '#10b981' : '#f59e0b' }}>
                                {result.probability ? `${result.probability}%` : '-'}
                              </td>
                              <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600', color: '#10b981' }}>
                                {result.potential_profit ? `$${result.potential_profit.toFixed(2)}` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              {/* Custom Date Range Query Section */}
              <div style={{
                padding: '1.5rem',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '2px solid #e2e8f0',
                marginBottom: '2rem'
              }}>
                <h3 style={{ fontSize: '1.25rem', color: '#0c4a6e', marginBottom: '1rem' }}>
                  📅 Performance Query
                </h3>
                
                {/* Quick Select Buttons */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                    Quick Select:
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button onClick={() => setQuickDateRange(7)} style={{
                      padding: '0.5rem 1rem', background: '#e0f2fe', color: '#0c4a6e', border: '1px solid #0891b2',
                      borderRadius: '6px', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '600'
                    }}>7 Days</button>
                    <button onClick={() => setQuickDateRange(30)} style={{
                      padding: '0.5rem 1rem', background: '#e0f2fe', color: '#0c4a6e', border: '1px solid #0891b2',
                      borderRadius: '6px', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '600'
                    }}>30 Days</button>
                    <button onClick={() => setQuickDateRange(90)} style={{
                      padding: '0.5rem 1rem', background: '#e0f2fe', color: '#0c4a6e', border: '1px solid #0891b2',
                      borderRadius: '6px', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '600'
                    }}>90 Days</button>
                    <button onClick={() => setQuickDateRange(365)} style={{
                      padding: '0.5rem 1rem', background: '#e0f2fe', color: '#0c4a6e', border: '1px solid #0891b2',
                      borderRadius: '6px', fontSize: '0.875rem', cursor: 'pointer', fontWeight: '600'
                    }}>365 Days</button>
                  </div>
                </div>

                {/* Date Pickers */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '1rem', alignItems: 'end' }}>
                  <div>
                    <label style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                      From:
                    </label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem', display: 'block', fontWeight: '600' }}>
                      To:
                    </label>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      max={new Date().toISOString().split('T')[0]}
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        fontSize: '0.875rem'
                      }}
                    />
                  </div>
                  <button
                    onClick={fetchCustomPerformance}
                    disabled={loadingCustomPerformance}
                    style={{
                      padding: '0.75rem 2rem',
                      background: loadingCustomPerformance ? '#94a3b8' : '#0891b2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: loadingCustomPerformance ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem'
                    }}
                  >
                    {loadingCustomPerformance ? 'Loading...' : 'Fetch Performance'}
                  </button>
                </div>

                {/* Results Display */}
                {customPerformance && (
                  <div style={{ marginTop: '1.5rem', padding: '1.5rem', background: 'white', borderRadius: '8px', border: '2px solid #0891b2' }}>
                    <h4 style={{ fontSize: '1rem', color: '#0c4a6e', marginBottom: '1rem', fontWeight: '700' }}>
                      {customPerformance.period.start_date} to {customPerformance.period.end_date} ({customPerformance.period.days} days)
                    </h4>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                      <div style={{ padding: '1rem', background: '#ecfdf5', borderRadius: '8px' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#059669' }}>
                          {customPerformance.performance.win_rate}%
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>Win Rate</div>
                      </div>
                      
                      <div style={{ padding: '1rem', background: '#eff6ff', borderRadius: '8px' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: customPerformance.performance.total_profit >= 0 ? '#059669' : '#dc2626' }}>
                          {customPerformance.performance.total_profit >= 0 ? '+' : ''}${customPerformance.performance.total_profit.toLocaleString()}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>Total P/L</div>
                      </div>
                      
                      <div style={{ padding: '1rem', background: '#fef3c7', borderRadius: '8px' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#92400e' }}>
                          {customPerformance.performance.total_trades}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>Total Trades</div>
                      </div>
                      
                      <div style={{ padding: '1rem', background: '#f0fdf4', borderRadius: '8px' }}>
                        <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#166534' }}>
                          {customPerformance.performance.won} / {customPerformance.performance.lost}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '600' }}>Won / Lost</div>
                      </div>
                    </div>

                    {customPerformance.performance.best_day && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.875rem' }}>
                        <div style={{ padding: '0.75rem', background: '#d1fae5', borderRadius: '6px' }}>
                          <strong>Best Day:</strong> {customPerformance.performance.best_day.date}<br/>
                          Win Rate: {customPerformance.performance.best_day.win_rate}% | 
                          P/L: ${customPerformance.performance.best_day.profit}
                        </div>
                        <div style={{ padding: '0.75rem', background: '#fee2e2', borderRadius: '6px' }}>
                          <strong>Worst Day:</strong> {customPerformance.performance.worst_day.date}<br/>
                          Win Rate: {customPerformance.performance.worst_day.win_rate}% | 
                          P/L: ${customPerformance.performance.worst_day.profit}
                        </div>
                      </div>
                    )}

                    {/* 6-Hour Session Analysis Button */}
                    <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                      <button
                        onClick={fetchSessionPerformance}
                        disabled={loadingSessionPerformance}
                        style={{
                          padding: '0.75rem 1.5rem',
                          background: loadingSessionPerformance ? '#94a3b8' : '#7c3aed',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          fontWeight: '600',
                          cursor: loadingSessionPerformance ? 'not-allowed' : 'pointer',
                          fontSize: '0.875rem'
                        }}
                      >
                        {loadingSessionPerformance ? 'Analyzing Sessions...' : '🕐 Analyze 6-Hour Sessions'}
                      </button>
                    </div>

                    {/* Session Performance Results */}
                    {sessionPerformance && (
                      <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f5f3ff', borderRadius: '8px', border: '2px solid #7c3aed' }}>
                        <h5 style={{ fontSize: '1rem', color: '#5b21b6', marginBottom: '1rem', fontWeight: '700' }}>
                          🕐 6-Hour Trading Session Performance (Adelaide Time)
                        </h5>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {sessionPerformance.sessions.map((session, idx) => (
                            <div 
                              key={session.session}
                              style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                padding: '0.75rem 1rem',
                                background: session.profit >= 0 ? '#ecfdf5' : '#fef2f2',
                                borderRadius: '6px',
                                border: `2px solid ${session.profit >= 0 ? '#10b981' : '#ef4444'}`,
                                fontSize: '0.875rem'
                              }}
                            >
                              <div style={{ fontWeight: '600', color: '#374151' }}>
                                {session.time_range}
                                <span style={{ marginLeft: '0.5rem', color: '#6b7280', fontWeight: '400' }}>
                                  ({session.name})
                                </span>
                              </div>
                              <div style={{ 
                                fontWeight: '700', 
                                fontSize: '1rem',
                                color: session.profit >= 0 ? '#059669' : '#dc2626'
                              }}>
                                {session.profit >= 0 ? '+' : ''}${session.profit.toLocaleString()}
                                <span style={{ marginLeft: '0.75rem', fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>
                                  ({session.won}W/{session.lost}L)
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Best/Worst Session Summary */}
                        {sessionPerformance.best_session && sessionPerformance.worst_session && (
                          <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.8rem' }}>
                            <div style={{ padding: '0.5rem', background: '#d1fae5', borderRadius: '4px', textAlign: 'center' }}>
                              <strong>🏆 Best:</strong> {sessionPerformance.best_session.time_range}<br/>
                              +${sessionPerformance.best_session.profit.toLocaleString()} | {sessionPerformance.best_session.win_rate}% WR
                            </div>
                            <div style={{ padding: '0.5rem', background: '#fee2e2', borderRadius: '4px', textAlign: 'center' }}>
                              <strong>⚠️ Worst:</strong> {sessionPerformance.worst_session.time_range}<br/>
                              ${sessionPerformance.worst_session.profit.toLocaleString()} | {sessionPerformance.worst_session.win_rate}% WR
                            </div>
                          </div>
                        )}
                        
                        <div style={{ marginTop: '0.75rem', fontSize: '0.7rem', color: '#6b7280', textAlign: 'center' }}>
                          Analyzed {sessionPerformance.total_trades_analyzed} trades • Adelaide, SA Time (ACDT)
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* ═══════════════════════════════════════════════════════════════════════════ */}
              {/* LIVE SCANNER SECTIONS - ALWAYS VISIBLE (independent of tracked trades) */}
              {/* ═══════════════════════════════════════════════════════════════════════════ */}
              
              {/* 🚨 CURRENCY STRENGTH DIVERGENCE ALERTS - Violent Move Predictions */}
              <div 
                id="divergence-alerts-section"
                style={{ 
                  marginBottom: '2rem', 
                  padding: '1.5rem', 
                  background: divergenceAlerts.length > 0 
                    ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 50%, #fee2e2 100%)' 
                    : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)', 
                  borderRadius: '16px', 
                  border: divergenceAlerts.some(a => a.alert_level === 'EXTREME') ? '4px solid #dc2626' : divergenceAlerts.length > 0 ? '3px solid #f59e0b' : '2px solid #d1d5db', 
                  boxShadow: divergenceAlerts.some(a => a.alert_level === 'EXTREME')
                    ? '0 0 30px rgba(220, 38, 38, 0.5), 0 4px 20px rgba(0,0,0,0.1)' 
                    : '0 4px 20px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1f2937', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    🚨 Currency Strength Divergence
                    {divergenceAlerts.length > 0 && (
                      <span style={{ 
                        background: divergenceAlerts.some(a => a.alert_level === 'EXTREME') ? '#dc2626' : '#f59e0b', 
                        color: 'white', 
                        padding: '0.25rem 0.75rem', 
                        borderRadius: '20px', 
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        animation: divergenceAlerts.some(a => a.alert_level === 'EXTREME' || a.alert_level === 'HIGH') ? 'flash 0.5s infinite' : 'none'
                      }}>
                        {divergenceAlerts.length} ALERT{divergenceAlerts.length > 1 ? 'S' : ''}
                      </span>
                    )}
                  </h3>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setShowDivergencePerformance(!showDivergencePerformance)}
                      data-testid="toggle-divergence-performance-btn"
                      style={{
                        padding: '0.5rem 1rem',
                        background: showDivergencePerformance ? 'linear-gradient(135deg, #1d4ed8, #1e40af)' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      📊 Performance {showDivergencePerformance ? '▲' : '▼'}
                    </button>
                    <button
                      onClick={triggerDivergenceScan}
                      data-testid="trigger-divergence-scan-btn"
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      🔍 Scan Divergence
                    </button>
                  </div>
                </div>

                {loadingDivergenceAlerts ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#4b5563' }}>
                    Loading divergence alerts...
                  </div>
                ) : divergenceAlerts.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', background: 'rgba(255,255,255,0.5)', borderRadius: '12px' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>💪</div>
                    <div style={{ fontWeight: '600', color: '#4b5563' }}>No Extreme Divergence Detected</div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                      Scanner checks every 5 minutes for 80+ point currency strength divergence
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {divergenceAlerts.map((alert, index) => (
                      <div 
                        key={alert.alert_id || index}
                        style={{
                          background: 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
                          padding: '1.25rem',
                          borderRadius: '16px',
                          border: '2px solid rgba(255,255,255,0.2)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                        }}
                      >
                        {/* Header Row: Pair, Direction, Level, Score */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '1.5rem', fontWeight: '800', color: 'white' }}>
                              {alert.pair}
                            </span>
                            <span style={{
                              background: alert.suggested_direction === 'BUY' ? '#22c55e' : '#ef4444',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '6px',
                              fontSize: '0.875rem',
                              fontWeight: '700',
                              color: 'white'
                            }}>
                              {alert.suggested_direction === 'BUY' ? '📈' : '📉'} {alert.suggested_direction}
                            </span>
                            <span style={{
                              background: alert.alert_level === 'EXTREME' ? '#a855f7' : alert.alert_level === 'HIGH' ? '#f97316' : '#6b7280',
                              color: 'white',
                              padding: '0.25rem 0.75rem',
                              borderRadius: '20px',
                              fontSize: '0.75rem',
                              fontWeight: '700'
                            }}>
                              {alert.alert_level}
                            </span>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '1.75rem', fontWeight: '800', color: 'white' }}>
                              {alert.divergence_score}
                            </div>
                            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.7)', textAlign: 'right' }}>POINT<br/>DIVERGENCE</div>
                          </div>
                        </div>
                        
                        {/* Move Potential */}
                        <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)', marginBottom: '0.75rem', fontWeight: '600' }}>
                          {alert.move_potential || 'ELEVATED MOVE POTENTIAL'}
                        </div>
                        
                        {/* Currency Strength Grid */}
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '1fr 1fr', 
                          gap: '0.75rem', 
                          background: 'rgba(0,0,0,0.25)', 
                          padding: '0.75rem', 
                          borderRadius: '12px',
                          marginBottom: '0.75rem'
                        }}>
                          <div>
                            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.25rem' }}>
                              {alert.pair?.slice(0, 3)} STRENGTH
                            </div>
                            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'white' }}>
                              {alert.base_strength > 0 ? '+' : ''}{alert.base_strength}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.25rem' }}>
                              {alert.pair?.slice(3, 6)} STRENGTH
                            </div>
                            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'white' }}>
                              {alert.quote_strength > 0 ? '+' : ''}{alert.quote_strength}
                            </div>
                          </div>
                        </div>
                        
                        {/* Interpretation */}
                        <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.9)', marginBottom: '0.75rem' }}>
                          💡 {alert.interpretation}
                        </div>

                        {/* Entry Quality & Momentum Info */}
                        {alert.entry_timing && (
                          <div style={{
                            background: alert.entry_timing.entry_quality >= 7 
                              ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                              : alert.entry_timing.entry_quality >= 5
                                ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
                                : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            padding: '0.75rem',
                            borderRadius: '12px',
                            marginBottom: '0.75rem'
                          }}>
                            {/* Entry Quality Header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontSize: '1.1rem' }}>
                                  {alert.entry_timing.entry_quality >= 7 ? '✅' : alert.entry_timing.entry_quality >= 5 ? '📊' : '⚠️'}
                                </span>
                                <span style={{ fontWeight: '700', color: 'white', fontSize: '0.9rem' }}>
                                  {alert.entry_timing.entry_quality >= 7 ? '📊 GOOD ENTRY' : alert.entry_timing.entry_quality >= 5 ? '📊 DECENT ENTRY' : '⚠️ CAUTION'}
                                </span>
                              </div>
                              <div style={{
                                background: 'rgba(0,0,0,0.3)',
                                padding: '0.2rem 0.6rem',
                                borderRadius: '20px',
                                fontSize: '0.75rem',
                                fontWeight: '700',
                                color: 'white'
                              }}>
                                Entry Quality: {alert.entry_timing.entry_quality}/10
                              </div>
                            </div>
                            
                            {/* Assessment */}
                            <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.9)', marginBottom: '0.5rem' }}>
                              {alert.entry_timing.assessment}
                            </div>
                            
                            {/* Recent Move */}
                            {alert.entry_timing.recent_move !== undefined && (
                              <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)', marginBottom: '0.5rem' }}>
                                📉 Recent Move: {alert.entry_timing.recent_move > 0 ? '+' : ''}{alert.entry_timing.recent_move}%
                              </div>
                            )}
                            
                            {/* Momentum Details Grid */}
                            {alert.entry_timing.momentum !== undefined && (
                              <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(3, 1fr)', 
                                gap: '0.5rem', 
                                background: 'rgba(0,0,0,0.25)',
                                padding: '0.5rem',
                                borderRadius: '8px'
                              }}>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.6)' }}>Momentum</div>
                                  <div style={{ 
                                    fontSize: '0.9rem', 
                                    fontWeight: '700', 
                                    color: alert.entry_timing.momentum > 0 ? '#22c55e' : '#ef4444' 
                                  }}>
                                    {alert.entry_timing.momentum > 0 ? '+' : ''}{alert.entry_timing.momentum}
                                  </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.6)' }}>Pullback</div>
                                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: 'white' }}>
                                    {alert.entry_timing.pullback_percentage?.toFixed(0) || 0}%
                                  </div>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                  <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.6)' }}>Status</div>
                                  <div style={{ fontSize: '0.7rem', fontWeight: '600', color: '#fbbf24' }}>
                                    {alert.entry_timing.momentum_status || 'N/A'}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Activation Controls - Mobile Friendly */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                            <button
                              onClick={() => dismissDivergenceAlert(alert.alert_id)}
                              style={{
                                padding: '0.75rem 1rem',
                                background: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                border: '1px solid rgba(255,255,255,0.3)',
                                borderRadius: '8px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                flex: '0 0 auto'
                              }}
                            >
                              Dismiss
                            </button>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flex: 1 }}>
                              <span style={{ color: 'white', fontWeight: '600' }}>$</span>
                              <input
                                type="number"
                                placeholder="1000"
                                defaultValue="1000"
                                id={`divergence-risk-${alert.alert_id}`}
                                style={{
                                  flex: 1,
                                  padding: '0.75rem',
                                  borderRadius: '8px',
                                  border: '2px solid rgba(255,255,255,0.3)',
                                  background: 'white',
                                  fontSize: '1rem',
                                  fontWeight: '600',
                                  minWidth: '60px'
                                }}
                              />
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const riskInput = document.getElementById(`divergence-risk-${alert.alert_id}`);
                              const risk = parseFloat(riskInput?.value) || 1000;
                              activateDivergenceTrade(alert.alert_id, alert.pair, alert.suggested_direction, risk);
                            }}
                            style={{
                              padding: '0.75rem 1rem',
                              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontWeight: '700',
                              cursor: 'pointer',
                              width: '100%',
                              fontSize: '0.95rem'
                            }}
                          >
                            ✅ Activate with ${document.getElementById(`divergence-risk-${alert.alert_id}`)?.value || '1000'} Risk
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 🚨 ACTIVE BREAKOUT ALERTS - Shows pending opportunities with trade parameters */}
              <div 
                id="breakout-alerts-section"
                className={breakoutAlerts.length > 0 ? 'breakout-alert-active' : ''}
                style={{ 
                  marginBottom: '2rem', 
                  padding: '1.5rem', 
                  background: breakoutAlerts.length > 0 
                    ? 'linear-gradient(135deg, #fef3c7 0%, #fee2e2 50%, #fef3c7 100%)' 
                    : 'linear-gradient(135deg, #fef3c7 0%, #fbbf24 100%)', 
                  borderRadius: '16px', 
                  border: breakoutAlerts.length > 0 ? '4px solid #ef4444' : '3px solid #f59e0b', 
                  boxShadow: breakoutAlerts.length > 0 
                    ? '0 0 30px rgba(239, 68, 68, 0.5), 0 4px 20px rgba(245, 158, 11, 0.3)' 
                    : '0 4px 20px rgba(245, 158, 11, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#78350f', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    ⚡🔔 Active Breakout Alerts
                    {breakoutAlerts.length > 0 && (
                      <span style={{ 
                        background: '#dc2626', 
                        color: 'white', 
                        padding: '0.25rem 0.75rem', 
                        borderRadius: '20px', 
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        animation: 'flash 0.5s infinite'
                      }}>
                        🚨 {breakoutAlerts.length} NEW - ACTION REQUIRED
                      </span>
                    )}
                  </h3>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => setShowBreakoutPerformance(!showBreakoutPerformance)}
                      data-testid="toggle-breakout-performance-btn"
                      style={{
                        padding: '0.5rem 1rem',
                        background: showBreakoutPerformance ? 'linear-gradient(135deg, #1d4ed8, #1e40af)' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      📊 Performance {showBreakoutPerformance ? '▲' : '▼'}
                    </button>
                    <button
                      onClick={triggerBreakoutScan}
                      data-testid="trigger-breakout-scan-btn"
                      style={{
                        padding: '0.5rem 1rem',
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.875rem'
                      }}
                    >
                      🔍 Scan Now
                    </button>
                  </div>
                </div>

                {loadingBreakoutAlerts ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#78350f' }}>
                    Loading breakout alerts...
                  </div>
                ) : breakoutAlerts.length === 0 && pendingRetests.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', background: 'rgba(255,255,255,0.5)', borderRadius: '12px' }}>
                    <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🔍</div>
                    <div style={{ fontWeight: '600', color: '#78350f' }}>No Active Breakout Alerts</div>
                    <div style={{ fontSize: '0.875rem', color: '#92400e', marginTop: '0.5rem' }}>
                      Scanner checks every 5 minutes for 85%+ confidence breakouts
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {/* 👀 PENDING RETESTS SECTION - Breakouts waiting for retest entry */}
                    {pendingRetests.length > 0 && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ 
                          fontSize: '1rem', 
                          fontWeight: '700', 
                          color: '#1e40af', 
                          marginBottom: '0.75rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          👀 Watching for Retest ({pendingRetests.length})
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {pendingRetests.map((retest, index) => (
                            <div 
                              key={retest.retest_id || index}
                              style={{
                                background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                                padding: '1rem',
                                borderRadius: '12px',
                                border: '2px solid #3b82f6',
                                boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)'
                              }}
                            >
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span style={{ fontSize: '1.25rem' }}>👀</span>
                                  <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1e40af' }}>{retest.pair}</span>
                                  <span style={{ 
                                    display: 'inline-block',
                                    background: retest.direction === 'BUY' ? '#22c55e' : '#ef4444',
                                    color: 'white',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    fontWeight: '700'
                                  }}>
                                    {retest.direction}
                                  </span>
                                  <span style={{ 
                                    background: '#fef3c7',
                                    color: '#92400e',
                                    padding: '0.2rem 0.5rem',
                                    borderRadius: '4px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600'
                                  }}>
                                    {retest.confidence}%
                                  </span>
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                  ⏳ Waiting for price to retest {retest.breakout_level}
                                </div>
                              </div>
                              <div style={{ 
                                marginTop: '0.5rem', 
                                fontSize: '0.8rem', 
                                color: '#1e40af',
                                background: 'rgba(255,255,255,0.5)',
                                padding: '0.5rem',
                                borderRadius: '6px'
                              }}>
                                <strong>Breakout Level:</strong> {retest.breakout_level} | 
                                <strong> Detected:</strong> {new Date(retest.created_at).toLocaleTimeString()}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* 🎯 RETEST ENTRY ALERTS - Ready to trade */}
                    {breakoutAlerts.length > 0 && (
                      <div style={{ 
                        fontSize: '1rem', 
                        fontWeight: '700', 
                        color: '#065f46', 
                        marginBottom: '0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        🎯 Retest Entry Alerts ({breakoutAlerts.length})
                      </div>
                    )}
                    
                    {breakoutAlerts.map((alert, index) => (
                      <div 
                        key={alert.alert_id || index}
                        style={{
                          background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                          padding: '1.25rem',
                          borderRadius: '16px',
                          border: '3px solid #fbbf24',
                          boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '1.5rem' }}>⚡</span>
                            <div>
                              <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#78350f' }}>{alert.pair}</div>
                              <div style={{ 
                                display: 'inline-block',
                                background: alert.direction === 'BUY' ? '#22c55e' : '#ef4444',
                                color: 'white',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '6px',
                                fontSize: '0.875rem',
                                fontWeight: '700',
                                marginTop: '0.25rem'
                              }}>
                                {alert.direction}
                              </div>
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{
                              background: alert.confidence >= 90 ? '#22c55e' : '#3b82f6',
                              color: 'white',
                              padding: '0.5rem 1rem',
                              borderRadius: '20px',
                              fontSize: '1rem',
                              fontWeight: '700'
                            }}>
                              {alert.confidence}% Confidence
                            </div>
                          </div>
                        </div>
                        
                        {/* 🕐 Entry Timing Recommendation */}
                        {alert.entry_recommendation && (
                          <div style={{ 
                            background: alert.entry_recommendation === 'ENTER_NOW' ? 'linear-gradient(135deg, #22c55e, #16a34a)' :
                                       alert.entry_recommendation === 'WAIT_NEXT_CANDLE' ? 'linear-gradient(135deg, #f59e0b, #d97706)' :
                                       'linear-gradient(135deg, #3b82f6, #2563eb)',
                            color: 'white',
                            padding: '0.75rem 1rem',
                            borderRadius: '10px',
                            marginBottom: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <span style={{ fontSize: '1.25rem' }}>{alert.entry_timing_emoji || '🕐'}</span>
                            <div>
                              <div style={{ fontWeight: '700', fontSize: '0.95rem' }}>
                                {alert.entry_recommendation === 'ENTER_NOW' ? 'Enter Now' :
                                 alert.entry_recommendation === 'WAIT_NEXT_CANDLE' ? 'Wait for Next Candle' :
                                 alert.entry_recommendation === 'ENTER_ON_RETRACEMENT' ? 'Enter on Pullback' :
                                 alert.entry_recommendation === 'WAIT_FOR_PULLBACK' ? 'Wait for Retracement' :
                                 alert.entry_recommendation}
                              </div>
                              {alert.entry_recommendation_reason && (
                                <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                                  {alert.entry_recommendation_reason}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Trade Parameters */}
                        <div style={{ 
                          background: '#1e293b', 
                          padding: '1rem', 
                          borderRadius: '12px',
                          marginBottom: '1rem',
                          color: 'white'
                        }}>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.5rem' }}>
                            📋 COPY TO MT5:
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                            <div>
                              <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>ENTRY</div>
                              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#22c55e' }}>{alert.recommended_entry || alert.entry_price || 'N/A'}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>STOP LOSS</div>
                              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#ef4444' }}>{alert.recommended_sl || alert.stop_loss || 'N/A'}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>TAKE PROFIT</div>
                              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#3b82f6' }}>{alert.recommended_tp || alert.take_profit || 'N/A'}</div>
                            </div>
                          </div>
                          {alert.current_price && (
                            <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #334155' }}>
                              <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>CURRENT PRICE</div>
                              <div style={{ fontSize: '1rem', fontWeight: '600', color: 'white' }}>{alert.current_price}</div>
                            </div>
                          )}
                        </div>

                        {/* Conditions Met */}
                        {alert.conditions_met !== undefined && (
                          <div style={{ fontSize: '0.875rem', color: '#78350f', marginBottom: '1rem' }}>
                            <strong>Conditions Met ({alert.conditions_met}/{alert.max_conditions || 6}):</strong>{' '}
                            {alert.conditions ? 
                              Object.entries(alert.conditions)
                                .filter(([_, value]) => value === true)
                                .map(([key]) => key.replace(/_/g, ' '))
                                .join(', ') || 'None'
                              : 'consolidation, volatility, key level, session, market strength, currency strength aligned'
                            }
                          </div>
                        )}

                        {/* Activation Controls - Mobile Friendly */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ color: '#78350f', fontWeight: '600' }}>$</span>
                            <input
                              type="number"
                              placeholder="1000"
                              defaultValue="1000"
                              id={`breakout-risk-${alert.alert_id}`}
                              style={{
                                flex: 1,
                                padding: '0.75rem',
                                borderRadius: '8px',
                                border: '2px solid #fbbf24',
                                fontSize: '1rem',
                                fontWeight: '600',
                                minWidth: '80px'
                              }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => dismissBreakoutAlert(alert.alert_id)}
                              style={{
                                padding: '0.75rem 1rem',
                                background: '#64748b',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                flex: '1 1 auto',
                                minWidth: '100px'
                              }}
                            >
                              Dismiss
                            </button>
                            <button
                              onClick={() => {
                                const riskInput = document.getElementById(`breakout-risk-${alert.alert_id}`);
                                const risk = parseFloat(riskInput?.value) || 1000;
                                activateBreakoutTrade(alert.alert_id, alert.pair, alert.direction, risk);
                              }}
                              style={{
                                padding: '0.75rem 1rem',
                                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: '700',
                                cursor: 'pointer',
                                flex: '2 1 auto',
                                minWidth: '140px'
                              }}
                            >
                              ✅ Activate Trade
                            </button>
                          </div>
                        </div>

                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '0.5rem' }}>
                          ⏰ Expires: {new Date(alert.expires_at).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ═══════════════════════════════════════════════════════════════════════════ */}
              {/* TRACKED TRADES ANALYTICS - Only shows when trades exist */}
              {/* ═══════════════════════════════════════════════════════════════════════════ */}
              
              {loadingAnalytics ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                  Loading analytics...
                </div>
              ) : trackedTradesAnalytics && (trackedTradesAnalytics.overall || trackedTradesAnalytics.recent_trades) ? (
                <div>
                  {/* Overall Stats - Only show if overall data exists */}
                  {trackedTradesAnalytics.overall && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                      <div style={{ padding: '1.5rem', background: '#eff6ff', borderRadius: '12px', border: '2px solid #3b82f6' }}>
                        <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1e40af' }}>
                          {trackedTradesAnalytics.overall.total_trades}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Total Tracked Trades</div>
                      </div>
                      
                      <div style={{ padding: '1.5rem', background: '#d1fae5', borderRadius: '12px', border: '2px solid #10b981' }}>
                        <div style={{ fontSize: '2rem', fontWeight: '700', color: '#065f46' }}>
                          {trackedTradesAnalytics.overall.won}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Winning Trades</div>
                      </div>
                      
                      <div style={{ padding: '1.5rem', background: '#fee2e2', borderRadius: '12px', border: '2px solid #ef4444' }}>
                        <div style={{ fontSize: '2rem', fontWeight: '700', color: '#991b1b' }}>
                          {trackedTradesAnalytics.overall.lost}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Losing Trades</div>
                      </div>
                      
                      <div style={{ padding: '1.5rem', background: '#fef3c7', borderRadius: '12px', border: '2px solid #f59e0b' }}>
                        <div style={{ fontSize: '2rem', fontWeight: '700', color: '#92400e' }}>
                          {trackedTradesAnalytics.overall.win_rate}%
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Win Rate</div>
                      </div>
                    </div>
                  )}

                  {/* Scenario Outcomes Section */}
                  {trackedTradesAnalytics.scenario_outcomes && trackedTradesAnalytics.scenario_outcomes.total_linked_trades > 0 && (
                    <div style={{ marginBottom: '2rem', padding: '2rem', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderRadius: '16px', border: '3px solid #f59e0b' }}>
                      <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1rem', color: '#92400e' }}>
                        🎯 Scenario Outcome Analytics (Scalp + Limit Orders)
                      </h3>
                      
                      {/* Recommendation Banner */}
                      <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.8)', borderRadius: '12px', marginBottom: '1.5rem', border: '2px solid #f59e0b' }}>
                        <div style={{ fontSize: '1rem', fontWeight: '600', color: '#78350f' }}>
                          {trackedTradesAnalytics.scenario_outcomes.recommendation}
                        </div>
                      </div>

                      {/* Scenario Breakdown */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                        <div style={{ padding: '1.5rem', background: '#fee2e2', borderRadius: '12px', border: '2px solid #ef4444' }}>
                          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#991b1b' }}>
                            {trackedTradesAnalytics.scenario_outcomes.percentages.scalp_through_sl || 0}%
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#7f1d1d', fontWeight: '600', marginTop: '0.5rem' }}>
                            Scalp Through SL
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#991b1b', marginTop: '0.25rem' }}>
                            ({trackedTradesAnalytics.scenario_outcomes.counts.scalp_through_sl} trades)
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#7f1d1d', marginTop: '0.5rem', fontStyle: 'italic' }}>
                            Should use TP-only
                          </div>
                        </div>

                        <div style={{ padding: '1.5rem', background: '#d1fae5', borderRadius: '12px', border: '2px solid #10b981' }}>
                          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#065f46' }}>
                            {trackedTradesAnalytics.scenario_outcomes.percentages.perfect_combo || 0}%
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#064e3b', fontWeight: '600', marginTop: '0.5rem' }}>
                            Perfect Combo
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#065f46', marginTop: '0.25rem' }}>
                            ({trackedTradesAnalytics.scenario_outcomes.counts.perfect_combo} trades)
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#064e3b', marginTop: '0.5rem', fontStyle: 'italic' }}>
                            Both trades won ✅
                          </div>
                        </div>

                        <div style={{ padding: '1.5rem', background: '#dbeafe', borderRadius: '12px', border: '2px solid #3b82f6' }}>
                          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#1e40af' }}>
                            {trackedTradesAnalytics.scenario_outcomes.percentages.partial_scalp || 0}%
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#1e3a8a', fontWeight: '600', marginTop: '0.5rem' }}>
                            Partial Scalp
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#1e40af', marginTop: '0.25rem' }}>
                            ({trackedTradesAnalytics.scenario_outcomes.counts.partial_scalp} trades)
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#1e3a8a', marginTop: '0.5rem', fontStyle: 'italic' }}>
                            60% trailing SL
                          </div>
                        </div>

                        <div style={{ padding: '1.5rem', background: '#f1f5f9', borderRadius: '12px', border: '2px solid #64748b' }}>
                          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#334155' }}>
                            {trackedTradesAnalytics.scenario_outcomes.percentages.failed || 0}%
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#475569', fontWeight: '600', marginTop: '0.5rem' }}>
                            Failed
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#334155', marginTop: '0.25rem' }}>
                            ({trackedTradesAnalytics.scenario_outcomes.counts.failed} trades)
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#475569', marginTop: '0.5rem', fontStyle: 'italic' }}>
                            Both failed ❌
                          </div>
                        </div>
                      </div>

                      <div style={{ fontSize: '0.875rem', color: '#78350f', fontWeight: '600' }}>
                        Total Linked Trades (Scalp + Limit): {trackedTradesAnalytics.scenario_outcomes.total_linked_trades}
                      </div>
                    </div>
                  )}

                  {/* Order Type Performance */}
                  {trackedTradesAnalytics.order_type_performance && Object.keys(trackedTradesAnalytics.order_type_performance).length > 0 && (
                    <div style={{ marginBottom: '2rem' }}>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem', color: '#0f172a' }}>
                        📊 Order Type Performance
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                        {Object.entries(trackedTradesAnalytics.order_type_performance).map(([orderType, stats]) => (
                          <div key={orderType} style={{
                            padding: '1.5rem',
                            background: '#f8fafc',
                            borderRadius: '12px',
                            border: '2px solid #e2e8f0'
                          }}>
                            <div style={{ fontSize: '1.125rem', fontWeight: '700', color: '#0f172a', marginBottom: '0.75rem' }}>
                              {orderType}
                            </div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: stats.win_rate >= 60 ? '#10b981' : stats.win_rate >= 40 ? '#f59e0b' : '#ef4444' }}>
                              {stats.win_rate}%
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.5rem' }}>
                              Won: {stats.won} | Lost: {stats.lost} | Pending: {stats.pending}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Trailing Stop Loss Benefits */}
                  {trackedTradesAnalytics.trailing_stop_benefits && trackedTradesAnalytics.trailing_stop_benefits.count > 0 && (
                    <div style={{ marginBottom: '2rem', padding: '2rem', background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', borderRadius: '16px', border: '3px solid #10b981' }}>
                      <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1rem', color: '#065f46' }}>
                        📈 Trailing Stop Loss Power
                      </h3>
                      
                      <div style={{ padding: '1.5rem', background: 'rgba(255, 255, 255, 0.9)', borderRadius: '12px', marginBottom: '1.5rem', border: '2px solid #10b981' }}>
                        <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#059669', marginBottom: '0.5rem' }}>
                          ${trackedTradesAnalytics.trailing_stop_benefits.total_potential_captured}
                        </div>
                        <div style={{ fontSize: '1rem', color: '#065f46', fontWeight: '600', marginBottom: '0.75rem' }}>
                          {trackedTradesAnalytics.trailing_stop_benefits.message}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#047857', fontStyle: 'italic' }}>
                          {trackedTradesAnalytics.trailing_stop_benefits.explanation}
                        </div>
                      </div>

                      {trackedTradesAnalytics.trailing_stop_benefits.trades && trackedTradesAnalytics.trailing_stop_benefits.trades.length > 0 && (
                        <div>
                          <h4 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '0.75rem', color: '#065f46' }}>
                            Trades That Would Have Benefited:
                          </h4>
                          <div style={{ display: 'grid', gap: '0.75rem' }}>
                            {trackedTradesAnalytics.trailing_stop_benefits.trades.map((trade, idx) => (
                              <div key={idx} style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.7)', borderRadius: '8px', border: '1px solid #6ee7b7' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div>
                                    <span style={{ fontWeight: '700', color: '#065f46' }}>{trade.pair}</span>
                                    <span style={{ marginLeft: '1rem', fontSize: '0.875rem', color: '#047857' }}>
                                      Peak: {trade.peak_reached}%
                                    </span>
                                  </div>
                                  <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#059669' }}>
                                      +${trade.would_have_captured}
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#047857' }}>
                                      {trade.trailing_sl_info}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}


 


                  {/* Recent Trades */}
                  {trackedTradesAnalytics.recent_trades && trackedTradesAnalytics.recent_trades.length > 0 && (
                    <div>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '1rem', color: '#0f172a' }}>
                        🕒 Tracked Trades
                      </h3>
                      
                      {/* LIVE Total P/L - Shows TODAY'S stats (resets daily at rollover) */}
                      {trackedTradesAnalytics.daily && (trackedTradesAnalytics.daily.active > 0 || trackedTradesAnalytics.daily.won > 0 || trackedTradesAnalytics.daily.lost > 0) && (
                        <div style={{
                          padding: '1.5rem',
                          background: trackedTradesAnalytics.daily.live_total_pnl >= 0 ? 
                            'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' : 
                            'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                          borderRadius: '12px',
                          border: `3px solid ${trackedTradesAnalytics.daily.live_total_pnl >= 0 ? '#10b981' : '#ef4444'}`,
                          marginBottom: '1.5rem',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          position: 'relative'
                        }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                              💰 LIVE Total Profit & Loss
                              <span style={{ fontSize: '0.625rem', background: '#3b82f6', color: 'white', padding: '0.125rem 0.5rem', borderRadius: '4px', fontWeight: '700' }}>TODAY</span>
                            </div>
                            <div style={{
                              fontSize: '2.5rem',
                              fontWeight: '800',
                              color: trackedTradesAnalytics.daily.live_total_pnl >= 0 ? '#065f46' : '#991b1b'
                            }}>
                              {/* 🚨🚨🚨 CRITICAL PROTECTION - DO NOT MODIFY 🚨🚨🚨 */}
                              {/* During rollover (active=0), ALWAYS show $0.00 */}
                              {/* This is what user expects - no active trades = $0 LIVE P&L */}
                              {/* DO NOT change this to show daily closed P&L or cumulative */}
                              {trackedTradesAnalytics.daily.active === 0 ? (
                                '$0.00'
                              ) : (
                                `${trackedTradesAnalytics.daily.live_total_pnl >= 0 ? '+' : ''}$${trackedTradesAnalytics.daily.live_total_pnl?.toFixed(2) || '0.00'}`
                              )}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}>
                              <div>
                                {trackedTradesAnalytics.daily.won} wins • {trackedTradesAnalytics.daily.lost} losses • {trackedTradesAnalytics.daily.active} active • {trackedTradesAnalytics.daily.win_rate.toFixed(1)}% win rate
                              </div>
                              {trackedTradesAnalytics.daily.unrealized_pnl !== undefined && trackedTradesAnalytics.daily.active > 0 && (
                                <div style={{ fontSize: '0.7rem', fontWeight: '600', color: trackedTradesAnalytics.daily.unrealized_pnl >= 0 ? '#059669' : '#dc2626' }}>
                                  Unrealized: {trackedTradesAnalytics.daily.unrealized_pnl >= 0 ? '+' : ''}${trackedTradesAnalytics.daily.unrealized_pnl.toFixed(2)} from {trackedTradesAnalytics.daily.active} active trades
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Trade Status Filter Tabs */}
                      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #e5e7eb', paddingBottom: '0.5rem', flexWrap: 'wrap' }}>
                        {(() => {
                          const activeTrades = trackedTradesAnalytics.recent_trades.filter(t => t.status === 'ACTIVE').length;
                          const closedTrades = trackedTradesAnalytics.recent_trades.filter(t => ['WON', 'LOST', 'BREAKEVEN', 'LOW_CONFIDENCE_LOSS'].includes(t.status)).length;
                          const pendingTrades = trackedTradesAnalytics.recent_trades.filter(t => t.status === 'PENDING').length;
                          
                          return (
                            <>
                              <button
                                onClick={() => setTradeFilterTab('active')}
                                style={{
                                  padding: '0.5rem 1.5rem',
                                  background: tradeFilterTab === 'active' ? '#10b981' : 'transparent',
                                  color: tradeFilterTab === 'active' ? 'white' : '#64748b',
                                  border: 'none',
                                  borderRadius: '8px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem'
                                }}
                              >
                                🟢 Active
                                <span style={{
                                  background: tradeFilterTab === 'active' ? 'rgba(255,255,255,0.3)' : '#e5e7eb',
                                  padding: '0.125rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: '700'
                                }}>
                                  {activeTrades}
                                </span>
                              </button>
                              <button
                                onClick={() => setTradeFilterTab('closed')}
                                style={{
                                  padding: '0.5rem 1.5rem',
                                  background: tradeFilterTab === 'closed' ? '#6b7280' : 'transparent',
                                  color: tradeFilterTab === 'closed' ? 'white' : '#64748b',
                                  border: 'none',
                                  borderRadius: '8px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem'
                                }}
                              >
                                ⚫ Closed
                                <span style={{
                                  background: tradeFilterTab === 'closed' ? 'rgba(255,255,255,0.3)' : '#e5e7eb',
                                  padding: '0.125rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: '700'
                                }}>
                                  {closedTrades}
                                </span>
                              </button>
                              <button
                                onClick={() => setTradeFilterTab('pending')}
                                style={{
                                  padding: '0.5rem 1.5rem',
                                  background: tradeFilterTab === 'pending' ? '#fbbf24' : 'transparent',
                                  color: tradeFilterTab === 'pending' ? 'white' : '#64748b',
                                  border: 'none',
                                  borderRadius: '8px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem'
                                }}
                              >
                                🔵 Pending
                                <span style={{
                                  background: tradeFilterTab === 'pending' ? 'rgba(255,255,255,0.3)' : '#e5e7eb',
                                  padding: '0.125rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: '700'
                                }}>
                                  {pendingTrades}
                                </span>
                              </button>
                            </>
                          );
                        })()}
                      </div>

                      <style>
                        {`
                          @keyframes pulsate-green {
                            0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
                            50% { box-shadow: 0 0 20px 10px rgba(16, 185, 129, 0.3); }
                            100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
                          }
                          @keyframes pulsate-red {
                            0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
                            50% { box-shadow: 0 0 20px 10px rgba(239, 68, 68, 0.3); }
                            100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7); }
                          }
                          @keyframes pulse {
                            0%, 100% { opacity: 1; }
                            50% { opacity: 0.85; }
                          }
                        `}
                      </style>
                      <div style={{ display: 'grid', gap: '1rem' }}>
                        {trackedTradesAnalytics.recent_trades
                          .filter(trade => {
                            if (tradeFilterTab === 'active') {
                              return trade.status === 'ACTIVE';
                            } else if (tradeFilterTab === 'closed') {
                              return ['WON', 'LOST', 'BREAKEVEN', 'LOW_CONFIDENCE_LOSS'].includes(trade.status);
                            } else if (tradeFilterTab === 'pending') {
                              return trade.status === 'PENDING';
                            }
                            return true;
                          })
                          .map((trade, index) => {
                          // Determine card styling based on trade status
                          let cardStyle = {
                            padding: '1.5rem',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          };

                          // Check if trade is completed (WON, LOST, BREAKEVEN, LOW_CONFIDENCE_LOSS)
                          const isCompleted = ['WON', 'LOST', 'BREAKEVEN', 'LOW_CONFIDENCE_LOSS'].includes(trade.status);
                          
                          if (isCompleted) {
                            // All completed trades: Grey
                            cardStyle.background = '#e5e7eb';
                            cardStyle.border = '2px solid #d1d5db';
                          } else if (trade.status === 'ACTIVE') {
                            // Active trades: Check if in profit or loss
                            const currentPrice = trade.current_price || trade.entry_price;
                            const isInProfit = trade.direction === 'BUY' 
                              ? currentPrice > trade.entry_price 
                              : currentPrice < trade.entry_price;
                            
                            if (isInProfit) {
                              // Active in profit: Pulsating green
                              cardStyle.background = '#d1fae5';
                              cardStyle.border = '3px solid #10b981';
                              cardStyle.animation = 'pulsate-green 2s ease-in-out infinite';
                            } else {
                              // Active in loss: Pulsating red
                              cardStyle.background = '#fee2e2';
                              cardStyle.border = '3px solid #ef4444';
                              cardStyle.animation = 'pulsate-red 2s ease-in-out infinite';
                            }
                          } else {
                            // PENDING trades: Keep original styling (yellow-ish)
                            cardStyle.background = '#fef3c7';
                            cardStyle.border = '2px solid #fbbf24';
                          }

                          return (
                            <div 
                              key={index} 
                              onClick={() => handleTradeCardClick(trade)}
                              style={cardStyle}
                              onMouseEnter={(e) => {
                                if (!isCompleted && trade.status !== 'ACTIVE') {
                                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(8, 145, 178, 0.2)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isCompleted && trade.status !== 'ACTIVE') {
                                  e.currentTarget.style.boxShadow = 'none';
                                }
                              }}
                            >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '1.125rem', fontWeight: '700' }}>{trade.pair}</span>
                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '6px',
                                  fontSize: '0.875rem',
                                  fontWeight: '600',
                                  background: trade.direction === 'BUY' ? '#d1fae5' : '#fee2e2',
                                  color: trade.direction === 'BUY' ? '#065f46' : '#991b1b'
                                }}>
                                  {trade.direction}
                                </span>
                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '6px',
                                  fontSize: '0.875rem',
                                  fontWeight: '600',
                                  background: trade.status === 'WON' ? '#d1fae5' : trade.status === 'LOST' ? '#fee2e2' : '#fef3c7',
                                  color: trade.status === 'WON' ? '#065f46' : trade.status === 'LOST' ? '#991b1b' : '#92400e'
                                }}>
                                  {trade.status}
                                </span>
                                {trade.trade_type && trade.trade_type !== 'SINGLE' && (
                                  <span style={{
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    background: '#dbeafe',
                                    color: '#1e40af'
                                  }}>
                                    {trade.trade_type}
                                  </span>
                                )}
                                {trade.scenario_outcome && (
                                  <span style={{
                                    padding: '0.25rem 0.75rem',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: '600',
                                    background: '#fef3c7',
                                    color: '#92400e'
                                  }}>
                                    {trade.scenario_outcome.replace(/_/g, ' ').toUpperCase()}
                                  </span>
                                )}
                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: '700',
                                  background: trade.probability ? (trade.probability >= 80 ? '#dcfce7' : trade.probability >= 70 ? '#fef3c7' : '#fee2e2') : '#f1f5f9',
                                  color: trade.probability ? (trade.probability >= 80 ? '#166534' : trade.probability >= 70 ? '#92400e' : '#991b1b') : '#64748b'
                                }}>
                                  📊 {trade.probability ? `${trade.probability}%` : 'N/A'}
                                </span>
                                {/* 📈 MOMENTUM STRENGTH INDICATOR - Shows real-time momentum for active trades */}
                                {trade.status === 'ACTIVE' && trade.momentum && (
                                  <span 
                                    style={{
                                      padding: '0.25rem 0.75rem',
                                      borderRadius: '6px',
                                      fontSize: '0.75rem',
                                      fontWeight: '700',
                                      background: trade.momentum.momentum_color + '20',
                                      color: trade.momentum.momentum_color,
                                      border: `2px solid ${trade.momentum.momentum_color}`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.25rem'
                                    }}
                                    title={trade.momentum.details}
                                  >
                                    {trade.momentum.momentum_status === 'STRONG_MOMENTUM' && '🚀'}
                                    {trade.momentum.momentum_status === 'MODERATE' && '📈'}
                                    {trade.momentum.momentum_status === 'PULLBACK_HEALTHY' && '🔄'}
                                    {trade.momentum.momentum_status === 'PULLBACK_WATCH' && '👀'}
                                    {trade.momentum.momentum_status === 'PULLBACK_WARNING' && '⚠️'}
                                    {trade.momentum.momentum_status === 'CONSOLIDATING' && '📊'}
                                    {trade.momentum.momentum_status === 'SETTLING' && '⚠️'}
                                    {trade.momentum.momentum_status === 'WEAKENING' && '🔻'}
                                    {trade.momentum.momentum_status === 'REVERSAL_WARNING' && '🚨'}
                                    {trade.momentum.momentum_status === 'REVERSAL_CONFIRMED' && '🛑'}
                                    {trade.momentum.momentum_score > 0 ? '+' : ''}{trade.momentum.momentum_score}
                                    <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>
                                      {trade.momentum.momentum_status.replace(/_/g, ' ')}
                                    </span>
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                                {new Date(trade.calculated_at).toLocaleDateString()}
                              </div>
                            </div>
                            
                            {/* Momentum Details Bar for Active Trades */}
                            {trade.status === 'ACTIVE' && trade.momentum && (
                              <div style={{
                                background: trade.momentum.momentum_color + '10',
                                border: `1px solid ${trade.momentum.momentum_color}40`,
                                borderRadius: '8px',
                                padding: '0.75rem',
                                marginBottom: '0.75rem',
                                fontSize: '0.8rem'
                              }}>
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  flexWrap: 'wrap',
                                  gap: '0.5rem'
                                }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <span style={{ fontWeight: '700', color: trade.momentum.momentum_color }}>
                                      {trade.momentum.recommendation === 'HOLD' && '✅ HOLD - Let profits run'}
                                      {trade.momentum.recommendation === 'WATCH' && '👀 WATCH - Monitor closely'}
                                      {trade.momentum.recommendation === 'CONSIDER_CLOSING' && '⚠️ Consider closing'}
                                      {trade.momentum.recommendation === 'EXIT' && '🛑 EXIT - Close position'}
                                    </span>
                                  </div>
                                  <div style={{ 
                                    display: 'flex', 
                                    gap: '1rem', 
                                    color: '#64748b',
                                    fontSize: '0.75rem',
                                    alignItems: 'center'
                                  }}>
                                    <span>Short: {trade.momentum.short_term_change > 0 ? '+' : ''}{trade.momentum.short_term_change?.toFixed(2)}%</span>
                                    <span>Med: {trade.momentum.medium_term_change > 0 ? '+' : ''}{trade.momentum.medium_term_change?.toFixed(2)}%</span>
                                    <span style={{ 
                                      background: '#3b82f620', 
                                      padding: '0.15rem 0.5rem', 
                                      borderRadius: '4px',
                                      color: '#3b82f6',
                                      fontWeight: '600'
                                    }}>
                                      🔄 {momentumCountdown}s
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Main analysis message */}
                                <div style={{ 
                                  marginTop: '0.5rem', 
                                  color: '#475569',
                                  fontSize: '0.75rem',
                                  lineHeight: '1.4'
                                }}>
                                  {trade.momentum.details}
                                </div>
                                
                                {/* Expanded Analysis Section */}
                                {trade.momentum.analysis && (
                                  <div style={{
                                    marginTop: '0.75rem',
                                    paddingTop: '0.75rem',
                                    borderTop: `1px solid ${trade.momentum.momentum_color}30`,
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                                    gap: '0.5rem',
                                    fontSize: '0.7rem',
                                    color: '#64748b'
                                  }}>
                                    {/* Trend Strength */}
                                    <div style={{ background: '#f8fafc', padding: '0.4rem', borderRadius: '4px' }}>
                                      <div style={{ fontWeight: '600', color: '#334155' }}>📊 Trend Strength</div>
                                      <div>
                                        {trade.momentum.analysis.trend_strength?.toFixed(0) || 0}% 
                                        {trade.momentum.analysis.is_trending ? ' (Trending)' : ' (Ranging)'}
                                      </div>
                                      <div>{trade.momentum.analysis.trend_direction || 'N/A'}</div>
                                    </div>
                                    
                                    {/* HTF Confirmation */}
                                    <div style={{ background: '#f8fafc', padding: '0.4rem', borderRadius: '4px' }}>
                                      <div style={{ fontWeight: '600', color: '#334155' }}>📈 HTF Alignment</div>
                                      <div style={{ color: trade.momentum.analysis.htf_aligned ? '#10b981' : '#f97316' }}>
                                        {trade.momentum.analysis.htf_aligned ? '✓ Aligned' : '✗ Not Aligned'}
                                      </div>
                                      <div>{trade.momentum.analysis.htf_details?.join(' | ') || 'N/A'}</div>
                                    </div>
                                    
                                    {/* Pullback Analysis */}
                                    {trade.momentum.analysis.pullback && (
                                      <div style={{ background: '#f8fafc', padding: '0.4rem', borderRadius: '4px' }}>
                                        <div style={{ fontWeight: '600', color: '#334155' }}>🔄 Pullback</div>
                                        <div>
                                          {trade.momentum.analysis.pullback.is_pullback 
                                            ? `${trade.momentum.analysis.pullback.pullback_depth?.toFixed(0)}% (${trade.momentum.analysis.pullback.fib_level})`
                                            : trade.momentum.analysis.pullback.pullback_status || 'None'}
                                        </div>
                                        <div style={{ 
                                          color: trade.momentum.analysis.pullback.is_healthy_pullback ? '#10b981' : '#ef4444'
                                        }}>
                                          {trade.momentum.analysis.pullback.is_pullback && 
                                            (trade.momentum.analysis.pullback.is_healthy_pullback ? 'Healthy' : 'Deep - Caution')}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Volume */}
                                    {trade.momentum.analysis.volume?.has_volume_data && (
                                      <div style={{ background: '#f8fafc', padding: '0.4rem', borderRadius: '4px' }}>
                                        <div style={{ fontWeight: '600', color: '#334155' }}>📊 Volume</div>
                                        <div>{trade.momentum.analysis.volume.volume_signal}</div>
                                        <div>{trade.momentum.analysis.volume.volume_ratio?.toFixed(1)}x avg</div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', fontSize: '0.875rem', color: '#64748b' }}>
                              <div><strong>Entry:</strong> {trade.entry_price}</div>
                              <div><strong>SL:</strong> {trade.stop_loss}</div>
                              <div><strong>TP:</strong> {trade.take_profit}</div>
                              {trade.actual_profit_loss && (
                                <div style={{ color: trade.actual_profit_loss > 0 ? '#10b981' : '#ef4444', fontWeight: '600' }}>
                                  <strong>P/L:</strong> ${trade.actual_profit_loss.toFixed(2)}
                                </div>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '600' }}>No tracked trades yet</div>
                  <div style={{ marginTop: '0.5rem' }}>Start calculating trades to see performance analytics</div>
                </div>
              )}
              
              {/* ═══════════════════════════════════════════════════════════════════════════ */}
              {/* ALWAYS VISIBLE SECTIONS - Show even when no tracked trades */}
              {/* These sections have their own data sources (divergence & breakout analytics) */}
              {/* ═══════════════════════════════════════════════════════════════════════════ */}
              
              {/* Divergence Trades Performance - Toggle visibility with button */}
              {showDivergencePerformance && (
              <div style={{ marginBottom: '2rem', marginTop: '2rem', padding: '2rem', background: 'linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)', borderRadius: '16px', border: '3px solid #ec4899' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1.5rem', color: '#831843', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  🚨 Currency Strength Divergence Performance
                </h3>
                
                {loadingDivergenceAnalytics ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                    Loading divergence analytics...
                  </div>
                ) : divergenceAnalytics ? (
                  <div>
                    {/* Divergence Stats Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                      <div style={{ padding: '1.25rem', background: '#eff6ff', borderRadius: '12px', border: '2px solid #3b82f6' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#1e40af' }}>
                          {divergenceAnalytics.total_divergences_detected}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Total Detected</div>
                      </div>
                      
                      <div style={{ padding: '1.25rem', background: '#d1fae5', borderRadius: '12px', border: '2px solid #10b981' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#065f46' }}>
                          {divergenceAnalytics.successful_divergences}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Successful Trades</div>
                      </div>
                      
                      <div style={{ padding: '1.25rem', background: '#fce7f3', borderRadius: '12px', border: '2px solid #ec4899' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#831843' }}>
                          {divergenceAnalytics.success_rate}%
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Success Rate</div>
                      </div>
                      
                      <div style={{ padding: '1.25rem', background: '#dbeafe', borderRadius: '12px', border: '2px solid #3b82f6' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#1e40af' }}>
                          {divergenceAnalytics.win_rate}%
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Win Rate</div>
                      </div>
                      
                      <div style={{ padding: '1.25rem', background: '#fef3c7', borderRadius: '12px', border: '2px solid #f59e0b' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#92400e' }}>
                          {divergenceAnalytics.avg_pips_gained}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Avg Pips</div>
                      </div>
                      
                      <div style={{ padding: '1.25rem', background: divergenceAnalytics.total_profit_loss >= 0 ? '#d1fae5' : '#fee2e2', borderRadius: '12px', border: `2px solid ${divergenceAnalytics.total_profit_loss >= 0 ? '#10b981' : '#ef4444'}` }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: divergenceAnalytics.total_profit_loss >= 0 ? '#065f46' : '#991b1b' }}>
                          ${divergenceAnalytics.total_profit_loss}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Total P/L</div>
                      </div>
                    </div>
                    
                    {/* Win/Loss Breakdown */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                      <div style={{ padding: '1rem', background: '#d1fae5', borderRadius: '12px', border: '2px solid #10b981' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#065f46' }}>
                          ✅ {divergenceAnalytics.total_wins}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#064e3b', fontWeight: '600' }}>Wins (Hit TP)</div>
                      </div>
                      
                      <div style={{ padding: '1rem', background: '#fee2e2', borderRadius: '12px', border: '2px solid #ef4444' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#991b1b' }}>
                          ❌ {divergenceAnalytics.total_losses}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#7f1d1d', fontWeight: '600' }}>Losses (Hit SL)</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                    No divergence trade data available yet. Activate divergence trades to see performance.
                  </div>
                )}
              </div>
              )}
              
              {/* Breakout Trades Performance - Toggle visibility with button */}
              {showBreakoutPerformance && (
              <div style={{ marginBottom: '2rem', padding: '2rem', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', borderRadius: '16px', border: '3px solid #eab308' }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '1rem', color: '#713f12', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  ⚡ Automated Breakout Trades Performance
                </h3>
                
                {/* Version Tabs */}
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => { setBreakoutVersion('all'); fetchBreakoutAnalytics('all'); }}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      border: 'none',
                      background: breakoutVersion === 'all' ? '#713f12' : '#fef3c7',
                      color: breakoutVersion === 'all' ? 'white' : '#713f12',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    📊 All
                  </button>
                  <button
                    onClick={() => { setBreakoutVersion('v1'); fetchBreakoutAnalytics('v1'); }}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      border: 'none',
                      background: breakoutVersion === 'v1' ? '#713f12' : '#fef3c7',
                      color: breakoutVersion === 'v1' ? 'white' : '#713f12',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    ⚡ V1 Breakout
                  </button>
                  <button
                    onClick={() => { setBreakoutVersion('v2'); fetchBreakoutAnalytics('v2'); }}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      border: 'none',
                      background: breakoutVersion === 'v2' ? '#713f12' : '#fef3c7',
                      color: breakoutVersion === 'v2' ? 'white' : '#713f12',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    🎯 V2 Retest
                  </button>
                </div>
                
                {/* Version Label */}
                {breakoutAnalytics?.version_label && (
                  <div style={{ 
                    marginBottom: '1rem', 
                    padding: '0.5rem 1rem', 
                    background: breakoutVersion === 'v2' ? '#10b981' : breakoutVersion === 'v1' ? '#3b82f6' : '#713f12',
                    color: 'white',
                    borderRadius: '8px',
                    display: 'inline-block',
                    fontWeight: '600'
                  }}>
                    {breakoutVersion === 'v2' ? '🎯 ' : breakoutVersion === 'v1' ? '⚡ ' : '📊 '}
                    {breakoutAnalytics.version_label}
                  </div>
                )}
                
                {loadingBreakoutAnalytics ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                    Loading breakout analytics...
                  </div>
                ) : breakoutAnalytics ? (
                  <div>
                    {/* Breakout Stats Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                      <div style={{ padding: '1.25rem', background: '#eff6ff', borderRadius: '12px', border: '2px solid #3b82f6' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#1e40af' }}>
                          {breakoutAnalytics.total_breakouts_detected}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Total Detected</div>
                      </div>
                      
                      <div style={{ padding: '1.25rem', background: '#d1fae5', borderRadius: '12px', border: '2px solid #10b981' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#065f46' }}>
                          {breakoutAnalytics.successful_breakouts}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Successful (S/R Break)</div>
                      </div>
                      
                      <div style={{ padding: '1.25rem', background: '#fef3c7', borderRadius: '12px', border: '2px solid #f59e0b' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#92400e' }}>
                          {breakoutAnalytics.success_rate}%
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Success Rate</div>
                      </div>
                      
                      <div style={{ padding: '1.25rem', background: '#dbeafe', borderRadius: '12px', border: '2px solid #3b82f6' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#1e40af' }}>
                          {breakoutAnalytics.win_rate}%
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Win Rate</div>
                      </div>
                      
                      <div style={{ padding: '1.25rem', background: '#fef3c7', borderRadius: '12px', border: '2px solid #f59e0b' }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#92400e' }}>
                          {breakoutAnalytics.avg_pips_gained}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Avg Pips</div>
                      </div>
                      
                      <div style={{ padding: '1.25rem', background: breakoutAnalytics.total_profit_loss >= 0 ? '#d1fae5' : '#fee2e2', borderRadius: '12px', border: `2px solid ${breakoutAnalytics.total_profit_loss >= 0 ? '#10b981' : '#ef4444'}` }}>
                        <div style={{ fontSize: '1.75rem', fontWeight: '700', color: breakoutAnalytics.total_profit_loss >= 0 ? '#065f46' : '#991b1b' }}>
                          ${breakoutAnalytics.total_profit_loss}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Total P/L</div>
                      </div>
                    </div>
                    
                    {/* Win/Loss Breakdown */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                      <div style={{ padding: '1rem', background: '#d1fae5', borderRadius: '12px', border: '2px solid #10b981' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#065f46' }}>
                          ✅ {breakoutAnalytics.total_wins}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#064e3b', fontWeight: '600' }}>Wins (Hit TP)</div>
                      </div>
                      
                      <div style={{ padding: '1rem', background: '#fee2e2', borderRadius: '12px', border: '2px solid #ef4444' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#991b1b' }}>
                          ❌ {breakoutAnalytics.total_losses}
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#7f1d1d', fontWeight: '600' }}>Losses (Hit SL)</div>
                      </div>
                    </div>
                    
                    {/* V1 vs V2 Comparison Card */}
                    {breakoutComparison && (
                      <div style={{ 
                        marginTop: '1.5rem', 
                        padding: '1.5rem', 
                        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', 
                        borderRadius: '12px',
                        color: 'white'
                      }}>
                        <h4 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          📈 V1 vs V2 Comparison
                        </h4>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                          {/* V1 Stats */}
                          <div style={{ padding: '1rem', background: 'rgba(59, 130, 246, 0.2)', borderRadius: '8px', border: '1px solid #3b82f6' }}>
                            <div style={{ fontSize: '0.875rem', color: '#93c5fd', marginBottom: '0.5rem' }}>⚡ V1 Breakout Entry</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{breakoutComparison.comparison?.v1_breakout_entry?.win_rate || 0}%</div>
                            <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                              {breakoutComparison.comparison?.v1_breakout_entry?.wins || 0}W / {breakoutComparison.comparison?.v1_breakout_entry?.losses || 0}L
                            </div>
                          </div>
                          
                          {/* V2 Stats */}
                          <div style={{ padding: '1rem', background: 'rgba(16, 185, 129, 0.2)', borderRadius: '8px', border: '1px solid #10b981' }}>
                            <div style={{ fontSize: '0.875rem', color: '#6ee7b7', marginBottom: '0.5rem' }}>🎯 V2 Retest Entry</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                              {breakoutComparison.comparison?.v2_retest_entry?.total_completed > 0 
                                ? `${breakoutComparison.comparison?.v2_retest_entry?.win_rate}%` 
                                : 'Collecting...'}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                              {breakoutComparison.comparison?.v2_retest_entry?.wins || 0}W / {breakoutComparison.comparison?.v2_retest_entry?.losses || 0}L
                            </div>
                          </div>
                        </div>
                        
                        {/* Improvement Message */}
                        <div style={{ 
                          marginTop: '1rem', 
                          padding: '0.75rem', 
                          background: 'rgba(250, 204, 21, 0.1)', 
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                          color: '#fcd34d'
                        }}>
                          💡 {breakoutComparison.improvement?.message || 'Collecting data...'}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                    <div style={{ fontSize: '1.125rem', fontWeight: '600' }}>No breakout data yet</div>
                    <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>The system scans for breakouts every 5 minutes</div>
                  </div>
                )}
              </div>
              )}

            </div>
          )}

          {/* Win/Loss Analysis Tab */}
          {activeTab === 'lossanalysis' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.75rem', color: '#0c4a6e' }}>
                  📊 Win/Loss Analysis
                </h2>
                <button
                  onClick={() => {
                    fetchWinAnalysis();
                    fetchLossAnalysis();
                    fetchComparisonStats();
                    fetchResetInfo();
                  }}
                  disabled={loadingWinAnalysis || loadingLossAnalysis || loadingComparisonStats}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: (loadingWinAnalysis || loadingLossAnalysis || loadingComparisonStats) ? '#94a3b8' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: (loadingWinAnalysis || loadingLossAnalysis || loadingComparisonStats) ? 'not-allowed' : 'pointer'
                  }}
                >
                  {(loadingWinAnalysis || loadingLossAnalysis || loadingComparisonStats) ? '⏳ Loading...' : '🔄 Refresh All'}
                </button>
              </div>

              {/* Reset Info Banner */}
              {resetInfo && (
                <div>
                  <div style={{ 
                    background: resetInfo.in_rollover_period ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', 
                    padding: '1rem', 
                    borderRadius: '8px', 
                    marginBottom: resetInfo.recent_learning_adjustments?.length > 0 ? '1rem' : '1.5rem',
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      {resetInfo.in_rollover_period ? (
                        <div>
                          <strong>⏰ Rollover Period Active</strong>
                          <div style={{ fontSize: '0.875rem', marginTop: '0.25rem', opacity: 0.9 }}>
                            Analysis will reset & learning system adjusting probabilities during {resetInfo.rollover_period}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <strong>📅 Next Reset:</strong> {new Date(resetInfo.next_reset).toLocaleString()}
                          <div style={{ fontSize: '0.875rem', marginTop: '0.25rem', opacity: 0.9 }}>
                            {resetInfo.last_reset ? `Last reset: ${new Date(resetInfo.last_reset).toLocaleString()} (${resetInfo.last_reset_count} trades archived)` : 'No previous reset'}
                          </div>
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: '2rem' }}>{resetInfo.in_rollover_period ? '⏳' : '📊'}</div>
                  </div>
                </div>
              )}

              {/* Sub-tabs */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0', paddingBottom: '0.5rem' }}>
                {['wins', 'losses', 'comparison'].map((subTab) => (
                  <button
                    key={subTab}
                    onClick={() => setAnalysisSubTab(subTab)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: analysisSubTab === subTab ? '#0891b2' : 'transparent',
                      color: analysisSubTab === subTab ? 'white' : '#64748b',
                      border: 'none',
                      borderRadius: '8px 8px 0 0',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {subTab === 'wins' && '✅ Wins'}
                    {subTab === 'losses' && '❌ Losses'}
                    {subTab === 'comparison' && '⚖️ Comparison'}
                  </button>
                ))}
              </div>

              <WinLossAnalysisContent
                analysisSubTab={analysisSubTab}
                winAnalysis={winAnalysis}
                lossAnalysis={lossAnalysis}
                comparisonStats={comparisonStats}
                loadingWinAnalysis={loadingWinAnalysis}
                loadingLossAnalysis={loadingLossAnalysis}
                loadingComparisonStats={loadingComparisonStats}
              />
            </div>
          )}


          {/* Weekly Probability Analysis Tab */}
          {activeTab === 'probanalysis' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.75rem', color: '#0c4a6e' }}>
                  🎯 Weekly Probability Win Rate Analysis
                </h2>
                <button
                  onClick={() => fetchWeeklyProbAnalysis(probDayFilter)}
                  disabled={loadingWeeklyProb}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: loadingWeeklyProb ? '#94a3b8' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: loadingWeeklyProb ? 'not-allowed' : 'pointer'
                  }}
                >
                  {loadingWeeklyProb ? '⏳ Loading...' : '🔄 Refresh'}
                </button>
              </div>

              {/* Week Info Banner */}
              {weeklyProbAnalysis?.week_info && (
                <div style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  padding: '1rem 1.5rem',
                  borderRadius: '12px',
                  marginBottom: '1.5rem',
                  color: 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '1rem'
                }}>
                  <div>
                    <div style={{ fontSize: '1.125rem', fontWeight: '700' }}>
                      📅 Week: {weeklyProbAnalysis.week_info.start} to {weeklyProbAnalysis.week_info.end}
                    </div>
                    <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>
                      {weeklyProbAnalysis.week_info.timezone} • Resets every Monday 6:30 AM
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span style={{ fontWeight: '600' }}>Filter:</span>
                    <span style={{
                      background: 'rgba(255,255,255,0.2)',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      fontWeight: '700'
                    }}>
                      {weeklyProbAnalysis.week_info.current_filter}
                    </span>
                  </div>
                </div>
              )}

              {/* Day Filter Buttons */}
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap',
                marginBottom: '1.5rem'
              }}>
                {['all', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map((day) => (
                  <button
                    key={day}
                    onClick={() => {
                      setProbDayFilter(day);
                      fetchWeeklyProbAnalysis(day);
                    }}
                    style={{
                      padding: '0.75rem 1.25rem',
                      background: probDayFilter === day ? '#f59e0b' : '#e5e7eb',
                      color: probDayFilter === day ? 'white' : '#374151',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {day === 'all' ? '📊 Whole Week' : day.charAt(0).toUpperCase() + day.slice(1)}
                  </button>
                ))}
              </div>

              {loadingWeeklyProb ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
                  <div>Loading probability analysis...</div>
                </div>
              ) : weeklyProbAnalysis ? (
                <div>
                  {/* Week Summary Cards */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '1rem',
                    marginBottom: '2rem'
                  }}>
                    <div style={{
                      background: '#f0fdf4',
                      padding: '1.25rem',
                      borderRadius: '12px',
                      border: '2px solid #22c55e',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '2rem', fontWeight: '700', color: '#15803d' }}>
                        {weeklyProbAnalysis.week_summary?.total_trades || 0}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Total Trades</div>
                    </div>
                    <div style={{
                      background: '#ecfdf5',
                      padding: '1.25rem',
                      borderRadius: '12px',
                      border: '2px solid #10b981',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '2rem', fontWeight: '700', color: '#065f46' }}>
                        {weeklyProbAnalysis.week_summary?.wins || 0}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Wins</div>
                    </div>
                    <div style={{
                      background: '#fef2f2',
                      padding: '1.25rem',
                      borderRadius: '12px',
                      border: '2px solid #ef4444',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '2rem', fontWeight: '700', color: '#991b1b' }}>
                        {weeklyProbAnalysis.week_summary?.losses || 0}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Losses</div>
                    </div>
                    <div style={{
                      background: '#fffbeb',
                      padding: '1.25rem',
                      borderRadius: '12px',
                      border: '2px solid #f59e0b',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '2rem', fontWeight: '700', color: '#92400e' }}>
                        {weeklyProbAnalysis.week_summary?.win_rate || 0}%
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Win Rate</div>
                    </div>
                    <div style={{
                      background: weeklyProbAnalysis.week_summary?.total_pnl >= 0 ? '#f0fdf4' : '#fef2f2',
                      padding: '1.25rem',
                      borderRadius: '12px',
                      border: `2px solid ${weeklyProbAnalysis.week_summary?.total_pnl >= 0 ? '#22c55e' : '#ef4444'}`,
                      textAlign: 'center'
                    }}>
                      <div style={{
                        fontSize: '1.75rem',
                        fontWeight: '700',
                        color: weeklyProbAnalysis.week_summary?.total_pnl >= 0 ? '#15803d' : '#991b1b'
                      }}>
                        ${weeklyProbAnalysis.week_summary?.total_pnl?.toFixed(2) || '0.00'}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Total P&L</div>
                    </div>
                  </div>

                  {/* Probability Buckets Table */}
                  <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    overflow: 'hidden',
                    marginBottom: '2rem'
                  }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #0c4a6e 0%, #1e3a5f 100%)',
                      padding: '1rem 1.5rem',
                      color: 'white'
                    }}>
                      <h3 style={{ margin: 0, fontSize: '1.25rem' }}>
                        📊 Win Rate by Individual Probability Percentage
                      </h3>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '700', color: '#0c4a6e', borderBottom: '2px solid #e5e7eb' }}>Probability Range</th>
                            <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#15803d', borderBottom: '2px solid #e5e7eb' }}>Wins</th>
                            <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#dc2626', borderBottom: '2px solid #e5e7eb' }}>Losses</th>
                            <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#0c4a6e', borderBottom: '2px solid #e5e7eb' }}>Total</th>
                            <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#0c4a6e', borderBottom: '2px solid #e5e7eb' }}>Win Rate</th>
                            <th style={{ padding: '1rem', textAlign: 'right', fontWeight: '700', color: '#0c4a6e', borderBottom: '2px solid #e5e7eb' }}>P&L</th>
                            <th style={{ padding: '1rem', textAlign: 'center', fontWeight: '700', color: '#0c4a6e', borderBottom: '2px solid #e5e7eb' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {weeklyProbAnalysis.probability_buckets?.length > 0 ? (
                            weeklyProbAnalysis.probability_buckets
                              .filter(bucket => {
                                // Filter to only show probabilities within execution window (81-87%)
                                const probValue = parseInt(bucket.range);
                                return probValue >= 81 && probValue <= 87;
                              })
                              .map((bucket, idx) => (
                              <tr key={idx} style={{
                                background: idx % 2 === 0 ? 'white' : '#f8fafc',
                                borderBottom: '1px solid #e5e7eb'
                              }}>
                                <td style={{ padding: '1rem', fontWeight: '600', color: '#0c4a6e' }}>
                                  {bucket.range}
                                </td>
                                <td style={{ padding: '1rem', textAlign: 'center', color: '#15803d', fontWeight: '600' }}>
                                  {bucket.wins}
                                </td>
                                <td style={{ padding: '1rem', textAlign: 'center', color: '#dc2626', fontWeight: '600' }}>
                                  {bucket.losses}
                                </td>
                                <td style={{ padding: '1rem', textAlign: 'center', fontWeight: '600' }}>
                                  {bucket.total}
                                </td>
                                <td style={{ padding: '1rem', textAlign: 'center' }}>
                                  <span style={{
                                    background: bucket.win_rate >= 60 ? '#dcfce7' : bucket.win_rate >= 50 ? '#fef3c7' : '#fee2e2',
                                    color: bucket.win_rate >= 60 ? '#15803d' : bucket.win_rate >= 50 ? '#92400e' : '#991b1b',
                                    padding: '0.375rem 0.75rem',
                                    borderRadius: '6px',
                                    fontWeight: '700'
                                  }}>
                                    {bucket.win_rate}%
                                  </span>
                                </td>
                                <td style={{
                                  padding: '1rem',
                                  textAlign: 'right',
                                  fontWeight: '600',
                                  color: bucket.total_pnl >= 0 ? '#15803d' : '#dc2626'
                                }}>
                                  ${bucket.total_pnl?.toFixed(2)}
                                </td>
                                <td style={{ padding: '1rem', textAlign: 'center' }}>
                                  <span style={{
                                    background: bucket.status === 'BEST' ? '#fef3c7' :
                                               bucket.status === 'Good' ? '#dcfce7' :
                                               bucket.status === 'OK' ? '#fef9c3' :
                                               bucket.status === 'Poor' ? '#fee2e2' :
                                               bucket.status === 'Avoid' ? '#fecaca' : '#f1f5f9',
                                    color: bucket.status === 'BEST' ? '#92400e' :
                                           bucket.status === 'Good' ? '#15803d' :
                                           bucket.status === 'OK' ? '#854d0e' :
                                           bucket.status === 'Poor' ? '#991b1b' :
                                           bucket.status === 'Avoid' ? '#7f1d1d' : '#64748b',
                                    padding: '0.375rem 0.75rem',
                                    borderRadius: '6px',
                                    fontWeight: '600',
                                    fontSize: '0.875rem'
                                  }}>
                                    {bucket.status_emoji} {bucket.status}
                                  </span>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                                No trades this week yet. Start trading to see probability analysis!
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Daily Breakdown */}
                  <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    border: '2px solid #e5e7eb',
                    overflow: 'hidden',
                    marginBottom: '2rem'
                  }}>
                    <div style={{
                      background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                      padding: '1rem 1.5rem',
                      color: 'white'
                    }}>
                      <h3 style={{ margin: 0, fontSize: '1.25rem' }}>
                        📅 Daily Breakdown
                      </h3>
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                      gap: '0.5rem',
                      padding: '1rem'
                    }}>
                      {weeklyProbAnalysis.daily_breakdown?.map((day, idx) => (
                        <div
                          key={idx}
                          onClick={() => {
                            if (day.total > 0) {
                              setProbDayFilter(day.day.toLowerCase());
                              fetchWeeklyProbAnalysis(day.day.toLowerCase());
                            }
                          }}
                          style={{
                            background: probDayFilter === day.day.toLowerCase() ? '#f59e0b' :
                                       day.total === 0 ? '#f1f5f9' :
                                       day.win_rate >= 60 ? '#dcfce7' :
                                       day.win_rate >= 50 ? '#fef3c7' : '#fee2e2',
                            padding: '1rem',
                            borderRadius: '10px',
                            textAlign: 'center',
                            cursor: day.total > 0 ? 'pointer' : 'default',
                            transition: 'all 0.2s ease',
                            border: probDayFilter === day.day.toLowerCase() ? '2px solid #d97706' : '2px solid transparent'
                          }}
                        >
                          <div style={{
                            fontWeight: '700',
                            color: probDayFilter === day.day.toLowerCase() ? 'white' : '#0c4a6e',
                            marginBottom: '0.5rem'
                          }}>
                            {day.day.slice(0, 3)}
                          </div>
                          <div style={{
                            fontSize: '0.875rem',
                            color: probDayFilter === day.day.toLowerCase() ? 'rgba(255,255,255,0.9)' : '#64748b'
                          }}>
                            {day.total > 0 ? (
                              <>
                                <div>{day.wins}W / {day.losses}L</div>
                                <div style={{ fontWeight: '600' }}>{day.win_rate}%</div>
                                <div style={{
                                  color: probDayFilter === day.day.toLowerCase() ? 'white' :
                                         day.pnl >= 0 ? '#15803d' : '#dc2626',
                                  fontWeight: '600'
                                }}>
                                  ${day.pnl?.toFixed(0)}
                                </div>
                              </>
                            ) : (
                              <span>No trades</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recommendations */}
                  {weeklyProbAnalysis.recommendations && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                      gap: '1rem'
                    }}>
                      <div style={{
                        background: '#f0fdf4',
                        padding: '1.5rem',
                        borderRadius: '12px',
                        border: '2px solid #22c55e'
                      }}>
                        <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#15803d', marginBottom: '0.5rem' }}>
                          🏆 Best Performing
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#065f46' }}>
                          {weeklyProbAnalysis.recommendations.best_performing?.range || 'Need more data'}
                        </div>
                        <div style={{ color: '#64748b' }}>
                          {weeklyProbAnalysis.recommendations.best_performing?.win_rate > 0
                            ? `${weeklyProbAnalysis.recommendations.best_performing.win_rate}% win rate`
                            : 'Collect more trades'}
                        </div>
                      </div>
                      <div style={{
                        background: '#fef2f2',
                        padding: '1.5rem',
                        borderRadius: '12px',
                        border: '2px solid #ef4444'
                      }}>
                        <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#dc2626', marginBottom: '0.5rem' }}>
                          ⚠️ Worst Performing
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#991b1b' }}>
                          {weeklyProbAnalysis.recommendations.worst_performing?.range || 'Need more data'}
                        </div>
                        <div style={{ color: '#64748b' }}>
                          {weeklyProbAnalysis.recommendations.worst_performing?.win_rate > 0
                            ? `${weeklyProbAnalysis.recommendations.worst_performing.win_rate}% win rate - Consider filtering`
                            : 'Collect more trades'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎯</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '600' }}>No data yet</div>
                  <div style={{ marginTop: '0.5rem' }}>Start trading to see probability analysis</div>
                </div>
              )}
            </div>
          )}


          {/* Filtered Trades Analysis Tab */}
          {activeTab === 'filteredtrades' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <h2 style={{ fontSize: '1.75rem', color: '#0c4a6e' }}>
                  🚫 Filtered Trades Analysis
                </h2>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {/* Filter Type Selector */}
                  <select
                    value={filteredTradesFilter}
                    onChange={(e) => {
                      setFilteredTradesFilter(e.target.value);
                      fetchFilteredTradesAnalysis(e.target.value);
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      border: '2px solid #0ea5e9',
                      background: 'white',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    <option value="all">All Filtered</option>
                    <option value="news">News Filtered Only</option>
                    <option value="probability">Probability Filtered Only</option>
                  </select>
                  <button
                    onClick={() => fetchFilteredTradesAnalysis(filteredTradesFilter)}
                    disabled={loadingFilteredTrades}
                    style={{
                      padding: '0.5rem 1rem',
                      background: loadingFilteredTrades ? '#94a3b8' : '#0ea5e9',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: loadingFilteredTrades ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loadingFilteredTrades ? '⏳ Loading...' : '🔄 Refresh'}
                  </button>
                </div>
              </div>

              {/* Explanation Card */}
              <div style={{
                background: 'linear-gradient(135deg, #dbeafe 0%, #e0f2fe 100%)',
                padding: '1rem 1.5rem',
                borderRadius: '12px',
                marginBottom: '1.5rem',
                border: '2px solid #0ea5e9'
              }}>
                <div style={{ fontWeight: '700', color: '#0c4a6e', marginBottom: '0.5rem' }}>
                  📊 What is this?
                </div>
                <div style={{ color: '#334155', fontSize: '0.9rem', lineHeight: '1.6' }}>
                  This shows trades that were <strong>rejected</strong> by the system's filters and tracks what <strong>would have happened</strong> if they were executed.
                  <br />
                  <strong>📰 News Filtered:</strong> Trades rejected because they conflicted with upcoming high-impact news.
                  <br />
                  <strong>📉 Probability Filtered:</strong> Trades rejected because probability was outside the 81-87% execution window.
                </div>
              </div>

              {loadingFilteredTrades ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
                  Loading filtered trades analysis...
                </div>
              ) : filteredTradesAnalysis ? (
                <>
                  {/* Summary Cards */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '1rem',
                    marginBottom: '2rem'
                  }}>
                    {/* Total Filtered */}
                    <div style={{
                      background: 'white',
                      padding: '1.25rem',
                      borderRadius: '12px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.5rem' }}>Total Filtered</div>
                      <div style={{ fontSize: '2rem', fontWeight: '700', color: '#0c4a6e' }}>
                        {filteredTradesAnalysis.total_filtered}
                      </div>
                    </div>

                    {/* News Filtered */}
                    <div style={{
                      background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                      padding: '1.25rem',
                      borderRadius: '12px',
                      textAlign: 'center',
                      border: '2px solid #3b82f6'
                    }}>
                      <div style={{ fontSize: '0.85rem', color: '#1e40af', marginBottom: '0.5rem' }}>📰 News Filtered</div>
                      <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1e3a8a' }}>
                        {filteredTradesAnalysis.summary?.news_filtered || 0}
                      </div>
                    </div>

                    {/* Probability Filtered */}
                    <div style={{
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                      padding: '1.25rem',
                      borderRadius: '12px',
                      textAlign: 'center',
                      border: '2px solid #f59e0b'
                    }}>
                      <div style={{ fontSize: '0.85rem', color: '#92400e', marginBottom: '0.5rem' }}>📉 Prob. Filtered</div>
                      <div style={{ fontSize: '2rem', fontWeight: '700', color: '#78350f' }}>
                        {filteredTradesAnalysis.summary?.probability_filtered || 0}
                      </div>
                    </div>

                    {/* Hypothetical Wins */}
                    <div style={{
                      background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                      padding: '1.25rem',
                      borderRadius: '12px',
                      textAlign: 'center',
                      border: '2px solid #22c55e'
                    }}>
                      <div style={{ fontSize: '0.85rem', color: '#166534', marginBottom: '0.5rem' }}>Would Have Won</div>
                      <div style={{ fontSize: '2rem', fontWeight: '700', color: '#14532d' }}>
                        {filteredTradesAnalysis.summary?.hypothetical_wins || 0}
                      </div>
                    </div>

                    {/* Hypothetical Losses */}
                    <div style={{
                      background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                      padding: '1.25rem',
                      borderRadius: '12px',
                      textAlign: 'center',
                      border: '2px solid #ef4444'
                    }}>
                      <div style={{ fontSize: '0.85rem', color: '#991b1b', marginBottom: '0.5rem' }}>Would Have Lost</div>
                      <div style={{ fontSize: '2rem', fontWeight: '700', color: '#7f1d1d' }}>
                        {filteredTradesAnalysis.summary?.hypothetical_losses || 0}
                      </div>
                    </div>

                    {/* Money Saved */}
                    <div style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      padding: '1.25rem',
                      borderRadius: '12px',
                      textAlign: 'center',
                      color: 'white'
                    }}>
                      <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.5rem' }}>💰 Filter Saved You</div>
                      <div style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                        {filteredTradesAnalysis.summary?.filter_saved_you || '$0.00'}
                      </div>
                    </div>
                  </div>

                  {/* Filter Accuracy */}
                  {filteredTradesAnalysis.summary?.filter_accuracy && filteredTradesAnalysis.summary.filter_accuracy !== 'N/A' && (
                    <div style={{
                      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                      padding: '1rem 1.5rem',
                      borderRadius: '12px',
                      marginBottom: '1.5rem',
                      border: '2px solid #22c55e',
                      textAlign: 'center'
                    }}>
                      <span style={{ fontSize: '1rem', color: '#166534' }}>
                        🎯 <strong>Filter Accuracy: {filteredTradesAnalysis.summary.filter_accuracy}</strong> of filtered trades would have been losses
                      </span>
                    </div>
                  )}

                  {/* Trades List */}
                  {filteredTradesAnalysis.trades && filteredTradesAnalysis.trades.length > 0 ? (
                    <div style={{
                      background: 'white',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                    }}>
                      <div style={{
                        background: '#0c4a6e',
                        color: 'white',
                        padding: '1rem 1.5rem',
                        fontWeight: '700'
                      }}>
                        Recent Filtered Trades (Last 100)
                      </div>
                      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        {filteredTradesAnalysis.trades.map((trade, index) => (
                          <div
                            key={trade.trade_id || index}
                            style={{
                              padding: '1rem 1.5rem',
                              borderBottom: '1px solid #e2e8f0',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '0.75rem',
                              background: index % 2 === 0 ? 'white' : '#f8fafc'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                              {/* Pair & Direction */}
                              <div>
                                <span style={{ fontWeight: '700', color: '#0c4a6e', marginRight: '0.5rem' }}>
                                  {trade.pair}
                                </span>
                                <span style={{
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  fontWeight: '700',
                                  background: trade.direction === 'BUY' ? '#dcfce7' : '#fee2e2',
                                  color: trade.direction === 'BUY' ? '#166534' : '#991b1b'
                                }}>
                                  {trade.direction}
                                </span>
                              </div>
                              
                              {/* Probability */}
                              <span style={{
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                background: '#f1f5f9',
                                color: '#475569'
                              }}>
                                {trade.probability}%
                              </span>
                            </div>

                            {/* Filter Reason */}
                            <div style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '6px',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              background: trade.filter_reason === 'NEWS_CONFLICT' ? '#dbeafe' : '#fef3c7',
                              color: trade.filter_reason === 'NEWS_CONFLICT' ? '#1e40af' : '#92400e',
                              maxWidth: '300px',
                              textAlign: 'center'
                            }}>
                              {trade.filter_reason === 'NEWS_CONFLICT' ? (
                                <>📰 {trade.news_event || 'News Conflict'}</>
                              ) : (
                                <>📉 {trade.filter_message}</>
                              )}
                            </div>

                            {/* Hypothetical Outcome */}
                            <div style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '6px',
                              fontSize: '0.8rem',
                              fontWeight: '700',
                              background: trade.hypothetical_outcome === 'WON' 
                                ? '#dcfce7' 
                                : trade.hypothetical_outcome === 'LOST'
                                ? '#fee2e2'
                                : '#f1f5f9',
                              color: trade.hypothetical_outcome === 'WON'
                                ? '#166534'
                                : trade.hypothetical_outcome === 'LOST'
                                ? '#991b1b'
                                : '#64748b'
                            }}>
                              {trade.hypothetical_outcome === 'WON' && '✅ Would Win'}
                              {trade.hypothetical_outcome === 'LOST' && '❌ Would Lose'}
                              {!trade.hypothetical_outcome && '⏳ Tracking...'}
                              {trade.hypothetical_pnl && ` (${trade.hypothetical_pnl > 0 ? '+' : ''}$${trade.hypothetical_pnl.toFixed(2)})`}
                            </div>

                            {/* Date */}
                            <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                              {trade.calculated_at ? new Date(trade.calculated_at).toLocaleDateString() : 'N/A'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{
                      background: 'white',
                      padding: '3rem',
                      borderRadius: '12px',
                      textAlign: 'center',
                      color: '#64748b'
                    }}>
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎯</div>
                      <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>No filtered trades yet</div>
                      <div style={{ fontSize: '0.9rem' }}>
                        When trades are rejected by the news or probability filters, they'll appear here so you can see what would have happened.
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{
                  background: 'white',
                  padding: '3rem',
                  borderRadius: '12px',
                  textAlign: 'center',
                  color: '#64748b'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
                  <div>Click "Refresh" to load filtered trades analysis</div>
                </div>
              )}
            </div>
          )}


          {/* Trade Statistics Tab - 17 Metrics */}
          {activeTab === 'tradestats' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ fontSize: '1.75rem', color: '#0c4a6e' }}>
                  📊 My Trading Statistics (Last 30 Days)
                </h2>
                <button
                  onClick={fetchTradeStats}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  🔄 Refresh Stats
                </button>
              </div>

              {loadingTradeStats ? (
                <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                  Loading statistics...
                </div>
              ) : tradeStats ? (
                <div>
                  {tradeStats.total_trades === 0 ? (
                    <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📭</div>
                      <div>No closed trades in the last 30 days</div>
                    </div>
                  ) : (
                    <>
                      {/* Trading Volume Section */}
                      <div style={{ marginBottom: '2.5rem' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0f172a', marginBottom: '1rem' }}>
                          📈 Trading Volume
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                          <div style={{ padding: '1.5rem', background: '#eff6ff', borderRadius: '12px', border: '2px solid #3b82f6' }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1e40af' }}>{tradeStats.total_trades}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Total Trades</div>
                          </div>
                          <div style={{ padding: '1.5rem', background: '#d1fae5', borderRadius: '12px', border: '2px solid #10b981' }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#065f46' }}>{tradeStats.profit_trades}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Profit Trades</div>
                          </div>
                          <div style={{ padding: '1.5rem', background: '#fee2e2', borderRadius: '12px', border: '2px solid #ef4444' }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#991b1b' }}>{tradeStats.loss_trades}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Loss Trades</div>
                          </div>
                          <div style={{ padding: '1.5rem', background: '#f3f4f6', borderRadius: '12px', border: '2px solid #9ca3af' }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#374151' }}>{tradeStats.long_trades}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Long Trades</div>
                          </div>
                          <div style={{ padding: '1.5rem', background: '#f3f4f6', borderRadius: '12px', border: '2px solid #9ca3af' }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#374151' }}>{tradeStats.short_trades}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Short Trades</div>
                          </div>
                        </div>
                      </div>

                      {/* Financial Performance Section */}
                      <div style={{ marginBottom: '2.5rem' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0f172a', marginBottom: '1rem' }}>
                          💰 Financial Performance
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                          <div style={{ padding: '1.5rem', background: '#d1fae5', borderRadius: '12px', border: '2px solid #10b981' }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#065f46' }}>${tradeStats.gross_profit.toFixed(2)}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Gross Profit</div>
                          </div>
                          <div style={{ padding: '1.5rem', background: '#fee2e2', borderRadius: '12px', border: '2px solid #ef4444' }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#991b1b' }}>${tradeStats.gross_loss.toFixed(2)}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Gross Loss</div>
                          </div>
                          <div style={{ 
                            padding: '1.5rem', 
                            background: tradeStats.net_profit >= 0 ? '#d1fae5' : '#fee2e2', 
                            borderRadius: '12px', 
                            border: `2px solid ${tradeStats.net_profit >= 0 ? '#10b981' : '#ef4444'}` 
                          }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: tradeStats.net_profit >= 0 ? '#065f46' : '#991b1b' }}>
                              ${tradeStats.net_profit.toFixed(2)}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Total Net Profit</div>
                          </div>
                        </div>
                      </div>

                      {/* Performance Metrics Section */}
                      <div style={{ marginBottom: '2.5rem' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0f172a', marginBottom: '1rem' }}>
                          🎯 Performance Metrics
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                          <div style={{ padding: '1.5rem', background: '#fef3c7', borderRadius: '12px', border: '2px solid #f59e0b' }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#92400e' }}>{tradeStats.profit_factor.toFixed(2)}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Profit Factor</div>
                          </div>
                          <div style={{ padding: '1.5rem', background: '#dbeafe', borderRadius: '12px', border: '2px solid #3b82f6' }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#1e40af' }}>{tradeStats.win_rate.toFixed(1)}%</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Win Rate</div>
                          </div>
                        </div>
                      </div>

                      {/* Best & Worst Section */}
                      <div style={{ marginBottom: '2.5rem' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0f172a', marginBottom: '1rem' }}>
                          📉 Best & Worst
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                          <div style={{ padding: '1.5rem', background: '#d1fae5', borderRadius: '12px', border: '2px solid #10b981' }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#065f46' }}>${tradeStats.best_profit.toFixed(2)}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Best Profit</div>
                          </div>
                          <div style={{ padding: '1.5rem', background: '#fee2e2', borderRadius: '12px', border: '2px solid #ef4444' }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#991b1b' }}>${tradeStats.biggest_loss.toFixed(2)}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Biggest Loss</div>
                          </div>
                        </div>
                      </div>

                      {/* Averages Section */}
                      <div style={{ marginBottom: '2.5rem' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0f172a', marginBottom: '1rem' }}>
                          📊 Averages
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                          <div style={{ padding: '1.5rem', background: '#d1fae5', borderRadius: '12px', border: '2px solid #10b981' }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#065f46' }}>${tradeStats.avg_profit.toFixed(2)}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Avg. Profit</div>
                          </div>
                          <div style={{ padding: '1.5rem', background: '#fee2e2', borderRadius: '12px', border: '2px solid #ef4444' }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#991b1b' }}>${tradeStats.avg_loss.toFixed(2)}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Avg. Loss</div>
                          </div>
                          <div style={{ 
                            padding: '1.5rem', 
                            background: tradeStats.expectancy >= 0 ? '#d1fae5' : '#fee2e2', 
                            borderRadius: '12px', 
                            border: `2px solid ${tradeStats.expectancy >= 0 ? '#10b981' : '#ef4444'}` 
                          }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: tradeStats.expectancy >= 0 ? '#065f46' : '#991b1b' }}>
                              ${tradeStats.expectancy.toFixed(2)}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Expectancy</div>
                          </div>
                        </div>
                      </div>

                      {/* Trade Details Section */}
                      <div>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0f172a', marginBottom: '1rem' }}>
                          ⏱️ Trade Details
                        </h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                          <div style={{ padding: '1.5rem', background: '#f3f4f6', borderRadius: '12px', border: '2px solid #9ca3af' }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#374151' }}>{tradeStats.avg_trade_size.toFixed(2)}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Avg. Trade Size</div>
                          </div>
                          <div style={{ padding: '1.5rem', background: '#f3f4f6', borderRadius: '12px', border: '2px solid #9ca3af' }}>
                            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#374151' }}>{tradeStats.avg_trade_duration}</div>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>Avg. Duration</div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                  <div>Click "Refresh Stats" to load your trading statistics</div>
                </div>
              )}
            </div>
          )}

          {/* ML Learning Progress Tab */}
          {activeTab === 'mlprogress' && (
            <div>
              <MLLearningProgress />
            </div>
          )}

          {activeTab === 'tierstats' && (
            <div>
              <EarlyIgnitionTierStats />
            </div>
          )}

          {activeTab === 'notificationlab' && (
            <div>
              <NotificationLab />
            </div>
          )}

          {/* Family & Friends Tab */}
          {activeTab === 'family' && <FamilyAccounts />}

          {/* Blacklist Tab */}
          {activeTab === 'blacklist' && (
            <div>
              <h2 style={{ fontSize: '1.75rem', marginBottom: '1rem', color: '#dc2626' }}>
                🚫 Blacklisted Users
              </h2>
              <p style={{ color: '#64748b', marginBottom: '2rem' }}>
                Users who requested refunds are automatically blacklisted. Their email and details are tracked to prevent re-registration abuse.
              </p>

              {loadingBlacklist ? (
                <div style={{
                  padding: '3rem',
                  textAlign: 'center',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  color: '#64748b'
                }}>
                  Loading blacklist...
                </div>
              ) : blacklist.length === 0 ? (
                <div style={{
                  padding: '3rem',
                  textAlign: 'center',
                  background: '#f0fdf4',
                  borderRadius: '12px',
                  color: '#16a34a',
                  border: '2px solid #86efac'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                    No blacklisted users
                  </div>
                  <div style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    All users are in good standing
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{
                    marginBottom: '1.5rem',
                    padding: '1rem',
                    background: '#fee2e2',
                    borderRadius: '8px',
                    border: '2px solid #ef4444',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontSize: '1.125rem', fontWeight: '700', color: '#991b1b' }}>
                        Total Blacklisted: {blacklist.length}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#991b1b', marginTop: '0.25rem' }}>
                        These users cannot re-register with the same details
                      </div>
                    </div>
                    <button
                      onClick={fetchBlacklist}
                      style={{
                        padding: '0.5rem 1rem',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600'
                      }}
                    >
                      🔄 Refresh
                    </button>
                  </div>

                  <div style={{ display: 'grid', gap: '1rem' }}>
                    {blacklist.map((entry) => (
                      <div key={entry.blacklist_id} style={{
                        padding: '1.5rem',
                        background: '#fef2f2',
                        borderRadius: '12px',
                        border: '2px solid #fca5a5'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                              <span style={{ fontSize: '2rem' }}>🚫</span>
                              <div>
                                <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#991b1b' }}>
                                  {entry.full_name || entry.email}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: '#dc2626' }}>
                                  {entry.email}
                                </div>
                              </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.875rem' }}>
                              <div>
                                <span style={{ fontWeight: '600', color: '#64748b' }}>User ID:</span> {entry.user_id}
                              </div>
                              {entry.phone && (
                                <div>
                                  <span style={{ fontWeight: '600', color: '#64748b' }}>Phone:</span> {entry.phone}
                                </div>
                              )}
                              <div>
                                <span style={{ fontWeight: '600', color: '#64748b' }}>Reason:</span> {entry.reason}
                              </div>
                              <div>
                                <span style={{ fontWeight: '600', color: '#64748b' }}>Blacklisted:</span>{' '}
                                {new Date(entry.blacklisted_at).toLocaleDateString()}
                              </div>
                              {entry.refund_amount && (
                                <div>
                                  <span style={{ fontWeight: '600', color: '#64748b' }}>Refund Amount:</span> ${(entry.refund_amount / 100).toFixed(2)}
                                </div>
                              )}
                              {entry.subscription_end_date && (
                                <div>
                                  <span style={{ fontWeight: '600', color: '#64748b' }}>Access Until:</span>{' '}
                                  {new Date(entry.subscription_end_date).toLocaleDateString()}
                                </div>
                              )}
                            </div>

                            {entry.refund_id && (
                              <div style={{
                                marginTop: '0.75rem',
                                padding: '0.5rem',
                                background: '#fee2e2',
                                borderRadius: '6px',
                                fontSize: '0.75rem',
                                color: '#991b1b'
                              }}>
                                Refund ID: {entry.refund_id}
                              </div>
                            )}
                          </div>

                          <button
                            onClick={() => handleRemoveFromBlacklist(entry.blacklist_id)}
                            style={{
                              padding: '0.5rem 1rem',
                              background: '#10b981',
                              border: 'none',
                              borderRadius: '8px',
                              color: 'white',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              cursor: 'pointer',
                              marginLeft: '1rem'
                            }}
                          >
                            ✓ Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div style={{
                    marginTop: '2rem',
                    padding: '1.5rem',
                    background: '#eff6ff',
                    borderRadius: '12px',
                    border: '2px solid #3b82f6'
                  }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '0.75rem', color: '#1e40af' }}>
                      ℹ️ Blacklist Information:
                    </h4>
                    <ul style={{ color: '#1e40af', lineHeight: '1.8', paddingLeft: '1.5rem' }}>
                      <li>Users are automatically blacklisted when they request a refund</li>
                      <li>Blacklisted users retain access until their subscription period ends</li>
                      <li>After subscription ends, they cannot sign up with the same email or details</li>
                      <li>They must request a new invite with different information</li>
                      <li>Removing from blacklist allows them to re-register with the same details</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Trade Detail Modal */}
      {showTradeDetailModal && selectedTrade && (
        <TradeDetailModal
          trade={selectedTrade}
          onClose={() => {
            setShowTradeDetailModal(false);
            setSelectedTrade(null);
          }}
        />
      )}
    </div>
  );
};

// Helper Components
const PriceInput = ({ label, value, onChange }) => (
  <div style={{ marginBottom: '1.5rem' }}>
    <label style={{
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: '600',
      marginBottom: '0.5rem',
      color: '#334155'
    }}>
      {label}
    </label>
    <div style={{ position: 'relative' }}>
      <span style={{
        position: 'absolute',
        left: '1rem',
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '1.25rem',
        color: '#64748b',
        fontWeight: '600'
      }}>$</span>
      <input
        type="number"
        value={value}
        onChange={onChange}
        style={{
          width: '100%',
          padding: '0.75rem 1rem 0.75rem 2.5rem',
          fontSize: '1rem',
          border: '2px solid #e2e8f0',
          borderRadius: '8px',
          outline: 'none'
        }}
      />
    </div>
  </div>
);

const StatCard = ({ label, value, icon, color }) => (
  <div style={{
    padding: '1.5rem',
    background: `${color}15`,
    borderRadius: '12px',
    border: `2px solid ${color}30`
  }}>
    <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{icon}</div>
    <div style={{ fontSize: '2rem', fontWeight: '700', color: color, marginBottom: '0.25rem' }}>
      {value}
    </div>
    <div style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: '600' }}>
      {label}
    </div>
  </div>
);

export default AdminDashboard;
